import {JsonProperty} from "../common/json/json-property";
import {$KTM, $KTS, KeyMetadatas, KeyTextStrings} from "../common/keytext";
import {StringUtil} from "../common/string_util";
import {
  BaseLabTestListResponse,
  BaseMedicineListResponse,
  CITIES,
  CITIES_KTS,
  CityId,
  MESSAGING_TEMPLATE_KTM,
  MessagingTemplateId,
  SearchListResponse
} from "../libwyze-common/api/types";
import {BaseResponse, Environment} from "../common/types";
import {ICache} from "../common/cache";
import {JSON_OBJECT, Type} from "../common/json/helpers";
import {enumValue, FormGen, FormGenMetadata} from "../shared/formgen";
import {
  $PRTXT,
  $PRTXT0,
  Account,
  AccountProfile,
  Action,
  BaseListItem,
  BaseListItemsLoader,
  ColorValue,
  CurrencyText,
  DefinedPresets,
  DisplayName,
  ENV,
  Filter,
  KeyTextString,
  ListItemChange,
  ObjectConfig,
  OnListItemsListener,
  Preset,
  PresetsKey,
  TypedObject
} from "@/shared/types";
import {renderLocalitySelect} from "./components/LocalitySelect";
import {passkey} from "../shared/passkey";
import {FormGenMultiStepConfigStep} from "../shared/FormGenMultiStepFragment";
import {BaseApp, ContextTrait} from "../shared/BaseApp";
import {ProvisioningContext, Rel, Rule} from "../shared/database";
import {md5, md5_code, md5_uuid} from "../common/md5";
import {LocalCache} from "../shared/LocalCache";
import {FormGenContainer, FormGenContainerMode} from "../shared/FormGenContainer";
import {JsonObject} from "../shared/json/json-object";
import {
  black,
  colorDarkBlue,
  colorDarkGreen,
  colorDarkPink,
  colorLightPastelBlue,
  colorLightPastelGreen,
  colorPastelBlue,
  colorPastelGreen,
  colorPastelRed,
  colorPastelYellow,
  colorRed,
  colorYellow,
  mediumGray,
  white
} from "../shared/colors";
import {renderProviderPageVerificationIdentity} from "./components/ProviderPageVerificationIdentity";
import {renderProviderPageVerificationRegistration} from "./components/ProviderPageVerificationRegistration";
import {renderImageGalleryUpload} from "./components/GalleryUpload";
import {renderPracticePageVerificationIdentity} from "./components/PracticePageVerificationIdentity";
import {SimpleImageEditor} from "./components/SimpleImageEditor";
import dayjs from "dayjs";
import {DateUtil, DAY_IN_MILLIS, FORMAT_SHOW_YEAR} from "../shared/date_util";
import {GalleryHelper} from "./components/GalleryHelper";
import {SvgIcon} from "@mui/material";
import {
  AddOutlined,
  BadgeOutlined,
  BloodtypeOutlined,
  BusinessOutlined,
  EmergencyOutlined,
  HistoryEduOutlined,
  HomeOutlined,
  HomeWorkOutlined,
  LocalHospitalOutlined,
  MedicationOutlined,
  NewspaperOutlined,
  PersonOutlined,
  PersonPinOutlined,
  PhoneOutlined,
  PhotoOutlined,
  SchoolOutlined,
  SpaOutlined,
  VideoCallOutlined
} from "@mui/icons-material";
import {
  connect,
  LocalParticipant,
  LocalTrack,
  LocalTrackPublication,
  Logger,
  RemoteParticipant,
  RemoteTrack,
  RemoteTrackPublication,
  Room
} from "twilio-video";
import {getMemberAuth} from "@/shared/auth";

import {appType, appTypeObject, AppTypeObject} from "../libwyze-common/types";
import {EditPatientProfileHelper} from "./components/EditPatientProfileHelper";
import {
  renderEditLabTestTemplateButton,
  renderLabTestTemplateParameters,
  renderLabTestTemplatePreview
} from "./components/EditLabTestTemplateHelper";
import {ArrayUtil} from "@/shared/array_util";
import {Members} from "../shared/entities";
import {EditExpenseTypeHelper} from "./components/EditExpenseTypeHelper";
import {EditInventorySkuHelper} from "./components/EditInventorySkuHelper";
import {generateSvgAvatarDataUri} from "../shared/avatar_gen";

const MINUTE_IN_MILLIS: number = 1000 * 60;
const HOUR_IN_MILLIS: number = 60 * MINUTE_IN_MILLIS;

export enum LinkPath {
  // Practice (Provider or Staff)
  PRACTICE_CONSULTS = "/mycare/practice/consults",
  PRACTICE_JOURNALS = "/mycare/practice/journals",
  PRACTICE_LABS = "/mycare/practice/labs",
  PRACTICE_PATIENTS = "/mycare/practice/patients",
  PRACTICE_PATIENTS_PROFILE_TEMPLATE = "/mycare/practice/patients/profile?id=%{id}",
  PRACTICE_DENTAL_PRESCRIPTIONS = "/mycare/practice/prescriptions",
  PRACTICE_MESSAGES = "/mycare/practice/messages",
  PRACTICE_CALENDAR = "/mycare/practice/calendar",
  PRACTICE_CALENDAR_REQUESTED_INBOUND = "/mycare/practice/calendar/requested/inbound",
  PRACTICE_CALENDAR_REQUESTED_REJECTED = "/mycare/practice/calendar/requested/rejected",
  PRACTICE_SETTINGS = "/mycare/practice/settings",
  PRACTICE_SETTINGS_PROFILE = "/mycare/practice/settings/profile",
  PRACTICE_SETTINGS_ACCOUNT = "/mycare/practice/settings/account",
  PRACTICE_SETTINGS_PRACTICE = "/mycare/practice/settings/practice",
  PRACTICE_BILLING = "/mycare/practice/billing",
  PRACTICE_NOTIFICATIONS = "/mycare/practice/notifications",
  PRACTICE_ASKWYZE = "/mycare/practice/askwyze",
  PRACTICE_CHECKINS = "/mycare/practice/checkins",
  PRACTICE_CHATS = "/mycare/practice/chats",
  PRACTICE_EXPENSES = "/mycare/practice/expenses",
  PRACTICE_VENDORS = "/mycare/practice/vendors",
  PRACTICE_SUPPLIERS = "/mycare/practice/suppliers",
  PRACTICE_INVENTORY = "/mycare/practice/inventory",

  // Patient
  PATIENT_MESSAGES = "/mycare/patient/messages",
  PATIENT_APPOINTMENTS = "/mycare/patient/appointments",
  PATIENT_DOCUMENTS = "/mycare/patient/documents",
  PATIENT_SETTINGS = "/mycare/patient/settings",
  PATIENT_NOTIFICATIONS = "/mycare/patient/notifications",
  PATIENT_SETTINGS_PROFILE = "/mycare/patient/settings/profile",
  PATIENT_SETTINGS_LINKED = "/mycare/patient/settings/linked",
  PATIENT_SETTINGS_BILLING = "/mycare/patient/settings/billing",
}

export enum LabTestTemplateType {
  SINGLE = "single",
  MULTI = "multi",
  NESTED = "nested",
  FREEFORM = "freeform",
}

export interface AccountProfileContextTrait extends ContextTrait {

  getAccountProfile<T extends AccountProfile>(): T;

  getProviderPageId(): string | undefined;

  getPracticePageId(): string | undefined;

  getProviderClaimedPracticePageIds(): string[]; // Defined only for PROVIDER accounts

  getPatientLinkedPracticePageIds(): string[]; // Defined only for PATIENT accounts
}

export async function LocalityNameById(cityId: string, localityId: string): Promise<string | null> {
  if (!cityId || !localityId) {
    return null;
  }
  const localityList = await DentwyzePresetListsCache.getInstance().lists.getOrLoadList(PresetListKey.LOCALITIES, [[":cityId", cityId]]);
  if (!localityList) {
    return null;
  }
  return localityList.findText(localityId);
}

export enum DentwyzeErrorCodes {
  ERROR_INVALID_RESPONSE_TYPE = 1001,
}

export function getServiceHostForEnvironment(env: Environment): string {
  switch (env) {
    case Environment.DEBUG:
    case Environment.EMULATOR_DEBUG:
      return "http://localhost:8080";
    case Environment.EMULATOR_LOCAL:
      return `http://127.0.0.1:5001/praxis-6b6a8/us-central1/${appType}`;
    case Environment.EMULATOR_TEST:
    case Environment.TEST:
      return "https://dentwyze-ncselpqhza-uc.a.run.app";
    default:
    case Environment.STAGING: // Assumes db is staging (e.g. praxis-6b6a8) but services are prod. Useful for DB dev.
    case Environment.PROD:
      return `https://fynedental-apps-${appType}-service.web.app`;
  }
}

export function getVantageSitesServiceHostForEnvironment(env: Environment): string {
  switch (env) {
    case Environment.DEBUG:
    case Environment.EMULATOR_DEBUG:
      return "http://localhost:8081";
    case Environment.EMULATOR_LOCAL:
      return `http://127.0.0.1:5001/praxis-6b6a8/us-central1/vantagesites`;
    case Environment.EMULATOR_TEST:
    case Environment.TEST:
      return "https://vantagesites-ncselpqhza-uc.a.run.app";
    default:
    case Environment.PROD:
      return `https://fynedental-apps-vantage-sites-service.web.app`;
  }
}

export function getServiceUrlForEnvironment(env: Environment, path: string): string {
  return `${getServiceHostForEnvironment(env)}/${path}`;
}

export function getServiceUrl(path: string): string {
  return `${getServiceHostForEnvironment(ENV)}/${path}`;
}

export function getVantageSitesServiceUrlForEnvironment(env: Environment, path: string): string {
  return `${getVantageSitesServiceHostForEnvironment(env)}/${path}`;
}

export function getPageBySlugUrl(slug: string): string {
  return getServiceUrl(`pages/slug/${slug}`);
}

export function getPageByIdUrl(pageId: string): string {
  return getServiceUrl(`pages/id/${pageId}`);
}

export function getReviewsByPracticePageIdUrl(practicePageId: string): string {
  return getServiceUrl(`reviews/practice/${practicePageId}`);
}

export function getReviewsByProviderPageIdUrl(providerPageId: string): string {
  return getServiceUrl(`reviews/provider/${providerPageId}`);
}

export function getReviewsByPatientIdUrl(patientId: string): string {
  return getServiceUrl(`reviews/patient/${patientId}`);
}

export function getPageSearchLocationUrl(cityId: string, localityId?: string, iv?: PageVisibility[]): string {
  return getServiceUrl(`search/pages?${new URLSearchParams([
    ["category", "location"],
    ["cityId", cityId],
    iv ? ["iv", iv.join(",")] : null,
    localityId ? ["localityId", localityId] : null]
    .filter(Boolean)).toString()}`
  );
}

export function getMedicineListUrl(q: string): string {
  return getServiceUrl(`medicines/list?q=${q}&format=mini`);
}

export function getMedicineFormatListUrl(format: "package" | "popular"): string {
  return getServiceUrl(`medicines/list?format=${format}`);
}

export function getMedicineBySlugUrl(slug: string): string {
  return getServiceUrl(`medicines/slug/${slug}`);
}

export function getLabTestListUrl(q: string): string {
  return getServiceUrl(`labtests/list?q=${q}&format=mini`);
}

export function getLabTestFormatListUrl(format: "package" | "popular"): string {
  return getServiceUrl(`labtests/list?format=${format}`);
}

export function getLabTestBySlugUrl(slug: string): string {
  return getServiceUrl(`labtests/slug/${slug}`);
}

export function getLabTestByIdUrl(labTestId: string): string {
  return getServiceUrl(`labtests/id/${labTestId}`);
}

export function getLabTestTemplateBySlugUrl(slug: string): string {
  return getServiceUrl(`labtests/templates/slug/${slug}`);
}

export function getLabTestTemplateByIdUrl(labTestId: string): string {
  return getServiceUrl(`labtests/templates/id/${labTestId}`);
}

export function getLabReportFileByIdUrl(practiceId: string, labOrderId: string): string {
  return getServiceUrl(`webreports/${practiceId}/file/id/${labOrderId}`);
}

export function getDentalPrescriptionFileByIdUrl(practiceId: string, prescriptionId: string): string {
  return getServiceUrl(`webscripts/${practiceId}/file/id/${prescriptionId}`);
}

export function getPostSummariesUrl(subPath: string): string {
  return getServiceUrl(`posts/${subPath}/summaries`);
}

export function getPostBySlugUrl(subPath: string, slug: string): string {
  return getServiceUrl(`posts/${subPath}/slug/${slug}`);
}

export function getDevicesLoginUrl(deviceUnitId): string {
  return getServiceUrlForEnvironment(ENV, `devices/login/${deviceUnitId}`);
}

export function getDevicesSetupUrl(): string {
  return getServiceUrlForEnvironment(ENV, "devices/setup");
}

export function getDevicesTeardownUrl(): string {
  return getServiceUrlForEnvironment(ENV, "devices/teardown");
}

// Vantage site builds only work on the emulator (with 'serve-emulator' command), but other API call can work with local run.
//
// Set the following to true when testing builds, which will convert DEBUG env to EMULATOR for vantage sites calls, so
// that dentwyze-service can continue to use local runs but build requests will be routed to local emulator.
//
// Set to false when testing api-endpoints, which can be use 'runlocal' command.
const VANTAGE_SITE_BUILD_EMULATOR: boolean = false;

export function getVantageSiteByIdUrl(practiceId: string, siteId: string): string {
  return getVantageSitesServiceUrlForEnvironment(VANTAGE_SITE_BUILD_EMULATOR && ENV === Environment.DEBUG ? Environment.EMULATOR_LOCAL : ENV, `vantage/sites/id/${practiceId}/${siteId}`);
}

export function getVantageSiteBuildStatusUrl(practiceId: string, siteId: string): string {
  return getVantageSitesServiceUrlForEnvironment(VANTAGE_SITE_BUILD_EMULATOR && ENV === Environment.DEBUG ? Environment.EMULATOR_LOCAL : ENV, `vantage/sites/build/${practiceId}/${siteId}`);
}

export function getVantageSiteBuildUrl(): string {
  return getVantageSitesServiceUrlForEnvironment(VANTAGE_SITE_BUILD_EMULATOR && ENV === Environment.DEBUG ? Environment.EMULATOR_LOCAL : ENV, `vantage/sites/build`);
}

export function getVantageSiteCustomDomainUrl(): string {
  return getVantageSitesServiceUrlForEnvironment(VANTAGE_SITE_BUILD_EMULATOR && ENV === Environment.DEBUG ? Environment.EMULATOR_LOCAL : ENV, `vantage/sites/customdomain`);
}

export function getMailSendUrl(): string {
  return getServiceUrlForEnvironment(ENV, "mail/send");
}

export function getMessagingSendUrl(): string {
  return getServiceUrlForEnvironment(ENV, "messaging/send");
}

export function getAccountWelcomeUrl(): string {
  return getServiceUrlForEnvironment(ENV, "account/welcome");
}

export function getAccountResetPwdUrl(): string {
  return getServiceUrlForEnvironment(ENV, "account/resetpwd");
}

export function getAccountUpdatePwdUrl(): string {
  return getServiceUrlForEnvironment(ENV, "account/updatepwd");
}

export function getNotificationEventUrl(): string {
  return getServiceUrlForEnvironment(ENV, "notifications/event");
}

export function getAskWyzeChatUrl(): string {
  return getServiceUrlForEnvironment(ENV, "askwyze/chat");
}

export function getAskWyzeSttUrl(): string {
  return getServiceUrlForEnvironment(ENV, "askwyze/stt");
}

export function getAskWyzeTtsUrl(): string {
  return getServiceUrlForEnvironment(ENV, "askwyze/tts");
}

export enum PostType {
  NEWS = "news",
  BLOG = "blog",
  LEARN = "learn",
}

export type PostTypeMetadata = {
  text: string,
  iconType: typeof SvgIcon,
  subPath: string,
  categories: KeyTextStrings,
}

export const POST_TYPE_KTM = new KeyMetadatas<PostTypeMetadata>([
  $KTM(PostType.NEWS, {
    text: "News",
    iconType: NewspaperOutlined,
    subPath: "news",
    categories: KeyTextStrings.fromList([
      "Announcement",
      "Product",
    ]),
  }),
  $KTM(PostType.BLOG, {
    text: "Blog",
    iconType: HistoryEduOutlined,
    subPath: "blog",
    categories: KeyTextStrings.fromList([
      "Article",
      "Culture",
      "Engineering",
    ]),
  }),
  $KTM(PostType.LEARN, {
    text: "Learn",
    iconType: SchoolOutlined,
    subPath: "learn",
    categories: KeyTextStrings.fromList([
      "Dental Care",
      "Conditions",
      "Procedures",
      "Health & Diet",
      "Parents & Children",
    ]),
  }),
]);

export enum PostStatus {
  DRAFT = "draft",
  PUBLISHED = "published",
  ARCHIVED = "archived",
}

export const POST_STATUS_KTS = new KeyTextStrings([
  $KTS(PostStatus.DRAFT, "Draft"),
  $KTS(PostStatus.PUBLISHED, "Published"),
  $KTS(PostStatus.ARCHIVED, "Archived"),
]);

@JsonObject()
export class Post extends BaseListItem {

  @JsonProperty()
  readonly type: PostType;

  @FormGen({
    name: "Status",
    type: "enum",
    enumVariant: "button",
    enumValues: POST_STATUS_KTS.values,
    enumDefaultKey: PostStatus.DRAFT,
  })
  @JsonProperty()
  status: PostStatus = PostStatus.DRAFT;

  @FormGen({name: "Published At", type: "datenum"})
  @JsonProperty()
  publishedAt: number;

  @FormGen({
    name: "Category",
    type: "enum",
    _asyncInit: async function (metadata, target) {
      const type = (target as Post)?.type;
      metadata.enumValues = POST_TYPE_KTM.findMetadata(type)?.categories.values || [];
    },
  })
  @JsonProperty()
  category: string;

  @FormGen({name: "Title", type: "string", required: true})
  @JsonProperty()
  title: string;

  @FormGen({name: "Subtitle", type: "string"})
  @JsonProperty()
  subtitle: string;

  @FormGen({
    name: "Cover image",
    type: "file",
    fileMimeTypes: ["image/png", "image/jpg"],
    fileStoredDataSource: "posts/cover_images"
  })
  @JsonProperty()
  coverImageUrl: string;

  @FormGen({name: "Body", type: "text"})
  @JsonProperty()
  body: string;

  @FormGen({name: "Attachments", type: "array_file", fileMimeTypes: ["image/png", "image/jpg"]})
  @JsonProperty()
  attachments: string[];

  static createNewNews(): Post {
    return new Post(md5_uuid(), PostType.NEWS);
  }

  static createNewBlog(): Post {
    return new Post(md5_uuid(), PostType.BLOG);
  }

  static createNewLearn(): Post {
    return new Post(md5_uuid(), PostType.LEARN);
  }

  constructor(id: string, type: PostType) {
    super(id);
    this.type = type;
  }

  categoryText(): string {
    return POST_TYPE_KTM.findMetadata(this.type)?.categories.findText(this.category);
  }

  toSlug(): string {
    return StringUtil.toSlug([this.title, this.id.substring(0, 4)].join(" "));
  }

  static readonly SLUG_REL_NAME = "slugs";

  slugRel(): Rel {
    const id1 = this.toSlug();
    if (!id1) {
      return null;
    }
    return new Rel(Post.SLUG_REL_NAME, id1, this.id, false, true);
  }
}

export class Posts extends BaseListItemsLoader<Post> {

  private static readonly instances = new Map<PostType, Posts>();

  static getInstance(postType: PostType): Posts {
    let instance = this.instances.get(postType);
    if (!instance) {
      instance = new Posts(postType);
      this.instances.set(postType, instance);
    }
    return instance;
  }

  private constructor(private readonly postType: PostType) {
    super({shared: true});
  }

  protected basePath(): string {
    return `posts/${POST_TYPE_KTM.findMetadata(this.postType)?.subPath || "uncategorized"}`;
  }

  async addPost(post: Post): Promise<void> {
    await this.addListItem(post, post.slugRel());
  }

  protected deserializeItem(value: any): Post {
    return JSON_OBJECT.deserializeObject(value, Post);
  }

  protected serializeItem(item: Post): any {
    return JSON_OBJECT.serializeObject(item);
  }

  protected sortOrder(item1: Post, item2: Post): number {
    return item2.created - item1.created;
  }
}

export enum PresetListKey {
  QUALIFICATION_COLLEGES = "qualification-colleges",
  REGISTRATION_COUNCILS = "registration-councils",
  LOCALITIES = "localities/:cityId",
  TRANSACTION_METHOD_TYPES = "transaction-method-types",
  SYMPTOM_TYPES = "symptom-types",
  FINDING_TYPES = "finding-types",
  DIAGNOSIS_TYPES = "diagnosis-types",
  POPULAR_MEDICINE_TYPES = "popular-medicine-types",
  POPULAR_LABTEST_TYPES = "popular-labtest-types",
  DENTAL_MEDICATION_DRUGS = "dental-medication-drugs",
  DENTAL_MEDICATION_FREQUENCIES = "dental-medication-frequencies",
  DENTAL_MEDICATION_DURATIONS = "dental-medication-durations",
  DENTAL_MEDICATION_MEALSPECS = "dental-medication-mealspecs",
  DENTAL_PROCEDURE_TYPES = "dental-procedure-types",
  PAYMENT_MODES = "payment-modes",
  TAXES = "taxes",
  EXPENSE_TYPES = "expense-types",
  INVENTORY_SKUS = "inventory-skus",
  SMS_TEMPLATES = "sms-templates",
  EMAIL_TEMPLATES = "email-templates",
}

type PresetListMetadata = {
  type: "remote" | "defined",

  // "defined"
  definedPresets?: DefinedPresets<any>,

  // "remote"
  remoteUrl?: string,

  responseList?: (json: any) => string[],
}

const PRESET_LIST_KTM = new KeyMetadatas<PresetListMetadata>([
  $KTM(PresetListKey.QUALIFICATION_COLLEGES, {
    type: "remote",
    remoteUrl: getServiceUrlForEnvironment(ENV, "search/colleges")
  }),
  $KTM(PresetListKey.REGISTRATION_COUNCILS, {
    type: "remote",
    remoteUrl: getServiceUrlForEnvironment(ENV, "search/registration_councils")
  }),
  $KTM(PresetListKey.LOCALITIES, {
    type: "remote",
    remoteUrl: getServiceUrlForEnvironment(ENV, "search/localities/:cityId")
  }),
  $KTM(PresetListKey.POPULAR_MEDICINE_TYPES, {
    type: "remote",
    remoteUrl: getServiceUrlForEnvironment(ENV, "medicines/list?format=popular"),
    responseList: json => ((json as BaseMedicineListResponse).medicines as MedicineType[]).map(med => med.name),
  }),
  $KTM(PresetListKey.POPULAR_LABTEST_TYPES, {
    type: "remote",
    remoteUrl: getServiceUrlForEnvironment(ENV, "labtests/list?format=popular"),
    responseList: json => ((json as BaseLabTestListResponse).labTests as LabTestType[]).map(test => test.name),
  }),
]);

export class DentwyzePresetLists {

  private static instance: DentwyzePresetLists;

  static getInstance(cache?: ICache): DentwyzePresetLists {
    if (!this.instance) {
      this.instance = new DentwyzePresetLists(cache);
    }
    return this.instance;
  }

  protected constructor(private readonly cache?: ICache) {
  }

  private readonly map = new Map<string, KeyTextStrings>();

  getList(key: PresetListKey, replaceStrings?: string[][]): KeyTextStrings | undefined {
    const cacheKey = this.getCacheKey(key, replaceStrings);
    return this.map.get(cacheKey);
  }

  async getOrLoadList(key: PresetListKey, replaceStrings?: string[][]): Promise<KeyTextStrings> {
    const cacheKey = this.getCacheKey(key, replaceStrings);
    let list = this.map.get(cacheKey);
    if (!list) {
      const str = this.cache?.get(cacheKey);
      if (str) {
        try {
          list = KeyTextStrings.fromList(JSON.parse(str));
          this.map.set(cacheKey, list);
        } catch (e) {
          //Continue.
        }
      }
      if (!list) {
        const metadata = PRESET_LIST_KTM.findMetadata(key);
        switch (metadata.type) {
          case "remote": {
            let fetchUrl = metadata.remoteUrl;
            if (replaceStrings) {
              for (const replaceString of replaceStrings) {
                fetchUrl = fetchUrl.replaceAll(replaceString[0], replaceString[1]);
              }
            }
            const json = (await fetch(fetchUrl).then(res => res.json())) as BaseResponse;
            if (json.status.code === 200) {
              const responseList = metadata.responseList?.(json) || (json as SearchListResponse).list;
              list = KeyTextStrings.fromList(responseList);
              this.cache?.set(cacheKey, JSON.stringify(responseList));
              this.map.set(cacheKey, list);
            }
            break;
          }
          case "defined": {
            const presets = await metadata.definedPresets.getOrLoadListItems();
            list = new KeyTextStrings(presets.map(preset => $KTS(preset.toKey(), preset.toString())));
            this.map.set(cacheKey, list);
            break;
          }
        }
      }
    }
    return list;
  }

  private getCacheKey(key: PresetListKey, replaceStrings: string[][]): string {
    let cacheKey = key.toString();
    if (replaceStrings) {
      for (const replaceString of replaceStrings) {
        cacheKey = cacheKey.replaceAll(replaceString[0], replaceString[1]);
      }
    }
    return cacheKey;
  }
}

export class DentwyzePresetListsCache {

  private static instance: DentwyzePresetListsCache;

  static getInstance(): DentwyzePresetListsCache {
    if (!this.instance) {
      this.instance = new DentwyzePresetListsCache();
    }
    return this.instance;
  }

  readonly lists: DentwyzePresetLists = DentwyzePresetLists.getInstance(LocalCache.getInstance("preset_lists", 7 * 24 * 3600));

  private constructor() {
  }
}

function toYearstampWithOffset(offset: number = 0): string {
  const year = new Date().getFullYear();
  const yearOffset = year + (Math.floor(offset) || 0);
  return yearOffset.toString();
}

export type UnsubscribeParams = {
  email: string,
}

export enum NamePrefix {
  DR = "Dr.",
  MR = "Mr.",
  MS = "Ms.",
  MRS = "Mrs.",
}

export enum Gender {
  MALE = "male",
  FEMALE = "female",
  OTHER = "other",
}

export const GENDER_KTS = new KeyTextStrings([
  $KTS(Gender.MALE, "Male"),
  $KTS(Gender.FEMALE, "Female"),
  $KTS(Gender.OTHER, "Other"),
]);

@JsonObject()
export class RatingStats {

  @JsonProperty()
  readonly starsCount: number[] = [0, 0, 0, 0, 1];

  average(): number | null {
    let sum = 0, total = 0;
    for (let i = 0; i < this.starsCount.length; i++) {
      const count = this.starsCount[i];
      if (count > 0) {
        sum += count * (i + 1);
        total++;
      }
    }
    return total > 0 ? Math.floor((sum / total) + 0.5) : null;
  }
}

export const REGISTRATION_YEARS_KTS = new KeyTextStrings(new Array(50).fill(null).map((_, index) => {
  const ys = toYearstampWithOffset(-index);
  return $KTS(ys, ys);
}));

export enum QualificationDegree {
  // Undergraduate
  BDS = "BDS",
  DMD = "DMD",
  DDS = "DDS",

  MBBS = "MBBS",
  MBBS_SURGERY = "MBBS-Surgery",
  BAMS = "BAMS",
  BUMS = "BUMS",
  BHMS = "BHMS",
  BYNS = "BYNS",
  BVS = "BVS",
  BSC = "BSC",

  // Postgraduate
  MDS = "MDS",
  MDS_ORTHODONTICS_DENTOFACIAL_ORTHOPEDICS = "Orthodontics & Dentofacial Orthopedics",
  MDS_PEDODONTICS_PREVENTIVE_DENTISTRY = "Pedodontics & Preventive Dentistry",
  MDS_PERIODONTICS = "Periodontics",
  MDS_CONSERVATIVE_DENTISTRY_ENDODONTICS = "Conservative Dentistry & Endodontics",
  MDS_ORAL_MAXILLOFACIAL_PATHOLOGY_ORAL_MICROBIOLOGY = "Oral & Maxillofacial Pathology and Oral Microbiology",
  MDS_ORAL_MAXILLOFACIAL_SURGERY = "Oral & Maxillofacial Surgery",
  MDS_ORAL_MEDICINE_RADIOLOGY = "Oral Medicine & Radiology",
  MDS_PROSTHODONTICS_CROWN_BRIDGE = "Prosthodontics & Crown and Bridge",
  MDS_PUBLIC_HEALTH_DENTISTRY = "Public Health Dentistry",

  PGD = "PGD",
  PGD_ANESTHESIA = "PGD_Anesthesia",
  PGD_CLINICAL_PATHOLOGY = "PGD_Clinical_Pathology",
  PGD_DVL = "PGD_DVL",
  PGD_ENT = "PGD_ENT",
  PGD_HEALTH_EDUCATION = "PGD_Health_Education",
  PGD_IMMUNOHAEMATOLOGY = "PGD_Immunohaematology",
  PGD_OBSTETRICS_GYNAECOLOGY = "PGD_Obstetrics_Gynaecology",
  PGD_OCCUPATIONAL_HEALTH = "PGD_Occupational_Health",
  PGD_OPHTHALMOLOGY = "PGD_Ophthalmology",
  PGD_ORTHOPAEDICS = "PGD_Orthopaedics",
  PGD_PAEDIATRICS = "PGD_Paediatrics",
  PGD_PSYCHIATRY = "PGD_Psychiatry",
  PGD_PHYSICAL_MEDICINE_REHABILITATION = "PGD_Physical_Medicine_Rehabilitation",
  PGD_PULMONARY_MEDICINE = "PGD_Pulmonary_Medicine",
  PGD_RADIO_DIAGNOSIS = "PGD_Radio_Diagnosis",
  PGD_RADIATION_MEDICINE = "PGD_Radiation_Medicine",
  PGD_SPORTS_MEDICINE = "PGD_Sports_Medicine",
  PGD_TROPICAL_MEDICINE = "PGD_Tropical_Medicine",
  PGD_ALLERGY_CLINICAL_IMMUNOLOGY = "PGD_Allergy_Clinical_Immunology",

  MD = "MD",
  MD_AEROSPACE_MEDICINE = "MD_Aerospace_Medicine",
  MD_ANATOMY = "MD_Anatomy",
  MD_ANESTHESIA = "MD_Anesthesia",
  MD_BIOCHEMISTRY = "MD_Biochemistry",
  MD_COMMUNITY_MEDICINE = "MD_Community_Medicine",
  MD_DERMATOLOGY_VENEREOLOGY_LEPROSY = "MD_Dermatology_Venereology_Leprosy",
  MD_FAMILY_MEDICINE = "MD_Family_Medicine",
  MD_FORENSIC_MEDICINE = "MD_Forensic_Medicine",
  MD_GENERAL_MEDICINE = "MD_General_Medicine",
  MD_GERIATRICS = "MD_Geriatrics",
  MD_HEALTH_ADMINISTRATION = "MD_Health_Administration",
  MD_HOSPITAL_ADMINISTRATION = "MD_Hospital_Administration",
  MD_IMMUNOHAEMATOLOGY = "MD_Immunohaematology",
  MD_MICROBIOLOGY = "MD_Microbiology",
  MD_NUCLEAR_MEDICINE = "MD_Nuclear_medicine",
  MD_PAEDIATRICS = "MD_Paediatrics",
  MD_PATHOLOGY = "MD_Pathology",
  MD_PHARMACOLOGY = "MD_Pharmacology",
  MD_PSYCHIATRY = "MD_Psychiatry",
  MD_PHYSICAL_MEDICINE_REHABILITATION = "MD_Physical_Medicine_Rehabilitation",
  MD_PHYSIOLOGY = "MD_Physiology",
  MD_PULMONARY_MEDICINE = "MD_Pulmonary_medicine",
  MD_RADIODIAGNOSIS = "MD_Radiodiagnosis",
  MD_RADIOTHERAPY = "MD_Radiotherapy",
  MD_SPORTS_MEDICINE = "MD_Sports_Medicine",
  MD_TROPICAL_MEDICINE = "MD_Tropical_Medicine",
  MD_PALLIATIVE_MEDICINE = "MD_Palliative_Medicine",
  MD_MARINE_MEDICINE = "MD_Marine_Medicine",
  MD_LABORATORY_MEDICINE = "MD_Laboratory_Medicine",
  MD_EMERGENCY_MEDICINE = "MD_Emergency_Medicine",

  MS = "MS",
  MS_ENT = "MS_ENT",
  MS_GENERAL_SURGERY = "MS_General_Surgery",
  MS_OBSTETRICS_GYNAECOLOGY = "MS_Obstetrics_Gynaecology",
  MS_OPHTHALMOLOGY = "MS_Ophthalmology",
  MS_ORTHOPAEDICS = "MS_Orthopaedics",
  MS_TRAUMATOLOGY_SURGERY = "MS_Traumatology_Surgery",

  DM = "DM",
  DM_CLINICAL_IMMUNOLOGY_RHEUMATOLOGY = "DM_Clinical_Immunology_Rheumatology",
  DM_CRITICAL_CARE_MEDICINE = "DM_Critical_Care_Medicine",
  DM_HEPATOLOGY = "DM_Hepatology",
  DM_NEPHROLOGY = "DM_Nephrology",
  DM_NEUROLOGY = "DM_Neurology",
  DM_ONCOLOGY = "DM_Neurology",
  DM_NEUROANESTHESIA = "DM_Neuroanesthesia",
  DM_NEURORADIOLOGY = "DM_Neuroradiology",
  DM_ORGAN_TRANSPLANT_ANAESTHESIA_CRITICAL_CARE = "DM_Organ_Transplant_Anaesthesia_Critical_Care",
  DM_PEDIATRIC_ONCOLOGY = "DM_Pediatric_Oncology",
  DM_PEDIATRIC_HEPATOLOGY = "DM_Pediatric_Hepatology",
  DM_PEDIATRIC_NEUROLOGY = "DM_Pediatric_Neurology",
  DM_PEDIATRIC_NEPHROLOGY = "DM_Pediatric_Nephrology",
  DM_VIROLOGY = "DM_Virology",
  DM_INFECTIOUS_DISEASES = "DM_Infectious_Diseases",
  DM_ENDOCRINOLOGY = "DM_Endocrinology",
  DM_MEDICAL_GENETICS = "DM_Medical_Genetics",
  DM_GERIATRIC_MENTAL_HEALTH = "DM_Geriatric_Mental_Health",
  DM_NEONATOLOGY = "DM_Neonatology",
  DM_CLINICAL_PHARMACOLOGY = "DM_Clinical_Pharmacology",

  MCH = "MCH",
  MCH_GYNAECOLOGICAL_ONCOLOGY = "MCH_Gynaecological_Oncology",
  MCH_HAND_SURGERY = "MCH_Hand_Surgery",
  MCH_HEPATO_PANCREATO_BILIARY_SURGERY = "MCH_Hepato_Pancreato_Biliary_Surgery",
  MCH_REPRODUCTIVE_MEDICINE_SURGERY = "MCH_Reproductive_Medicine_Surgery",
  MCH_VASCULAR_SURGERY = "MCH_Vascular_Surgery",
  MCH_SURGICAL_GASTROENTEROLOGY = "MCH_Surgical_Gastroenterology",
  MCH_PEDIATRIC_SURGERY = "MCH_Pediatric_Surgery",
  MCH_PLASTIC_RECONSTRUCTIVE_SURGERY = "MCH_Plastic_Reconstructive_Surgery",
  MCH_ENDOCRINE_SURGERY = "MCH_Endocrine_Surgery",
}

export type QualificationDegreeMetadata = {
  text: string,
  shortText: string,
  isPostGraduate?: boolean,
}

export const QUALIFICATION_DEGREES_KTM: AppTypeObject<KeyMetadatas<QualificationDegreeMetadata>> = {
  dentwyze: new KeyMetadatas<QualificationDegreeMetadata>([
    $KTM(QualificationDegree.BDS, {text: "Bachelor of Dental Surgery", shortText: "BDS"}),
    $KTM(QualificationDegree.DMD, {text: "Doctor of Dental Medicine", shortText: "DMD"}),
    $KTM(QualificationDegree.DDS, {text: "Doctor of Dental Surgery", shortText: "DDS"}),

    $KTM(QualificationDegree.MDS, {text: "MDS (Master of Dental Surgery)", shortText: "MDS", isPostGraduate: true}),
    $KTM(QualificationDegree.MDS_ORTHODONTICS_DENTOFACIAL_ORTHOPEDICS, {
      text: "MDS in Orthodontics & Dentofacial Orthopedics",
      shortText: "MDS",
      isPostGraduate: true
    }),
    $KTM(QualificationDegree.MDS_PEDODONTICS_PREVENTIVE_DENTISTRY, {
      text: "MDS in Pedodontics & Preventive Dentistry",
      shortText: "MDS",
      isPostGraduate: true
    }),
    $KTM(QualificationDegree.MDS_PERIODONTICS, {text: "MDS in Periodontics", shortText: "MDS", isPostGraduate: true}),
    $KTM(QualificationDegree.MDS_CONSERVATIVE_DENTISTRY_ENDODONTICS, {
      text: "MDS in Conservative Dentistry & Endodontics",
      shortText: "MDS",
      isPostGraduate: true
    }),
    $KTM(QualificationDegree.MDS_ORAL_MAXILLOFACIAL_PATHOLOGY_ORAL_MICROBIOLOGY, {
      text: "MDS in Oral & Maxillofacial Pathology and Oral Microbiology",
      shortText: "MDS",
      isPostGraduate: true
    }),
    $KTM(QualificationDegree.MDS_ORAL_MAXILLOFACIAL_SURGERY, {
      text: "MDS in Oral & Maxillofacial Surgery",
      shortText: "MDS",
      isPostGraduate: true
    }),
    $KTM(QualificationDegree.MDS_ORAL_MEDICINE_RADIOLOGY, {
      text: "MDS in Oral Medicine & Radiology",
      shortText: "MDS",
      isPostGraduate: true
    }),
    $KTM(QualificationDegree.MDS_PROSTHODONTICS_CROWN_BRIDGE, {
      text: "MDS in Prosthodontics & Crown and Bridge",
      shortText: "MDS",
      isPostGraduate: true
    }),
    $KTM(QualificationDegree.MDS_PUBLIC_HEALTH_DENTISTRY, {
      text: "MDS in Public Health Dentistry",
      shortText: "MDS",
      isPostGraduate: true
    }),
  ]),

  docwyze: new KeyMetadatas<QualificationDegreeMetadata>([
    $KTM(QualificationDegree.MBBS, {text: "Bachelor of Medicine", shortText: "MBBS", isPostGraduate: true}),
    $KTM(QualificationDegree.MBBS_SURGERY, {text: "Bachelor of Surgery", shortText: "MBBS", isPostGraduate: true}),
    $KTM(QualificationDegree.BAMS, {
      text: "Bachelor of Ayurvedic Medicine and Surgery",
      shortText: "BAMS",
      isPostGraduate: true
    }),
    $KTM(QualificationDegree.BUMS, {
      text: "Bachelor of Unani Medicine and Surgery",
      shortText: "BUMS",
      isPostGraduate: true
    }),
    $KTM(QualificationDegree.BHMS, {
      text: "Bachelor of Homeopathy Medicine and Surgery",
      shortText: "BHMS",
      isPostGraduate: true
    }),
    $KTM(QualificationDegree.BYNS, {
      text: "Bachelor of Yoga and Naturopathy Sciences",
      shortText: "BYNS",
      isPostGraduate: true
    }),
    $KTM(QualificationDegree.BVS, {
      text: "Bachelor of Veterinary Sciences and Animal Husbandry",
      shortText: "BVSc & AH",
      isPostGraduate: true
    }),
    $KTM(QualificationDegree.BSC, {text: "Bachelor of Science", shortText: "BSc", isPostGraduate: true}),

    $KTM(QualificationDegree.PGD, {text: "PGD (Post Graduate Diploma)", shortText: "PGD", isPostGraduate: true}),
    $KTM(QualificationDegree.PGD_ANESTHESIA, {text: "Diploma in Anesthesia", shortText: "PGD", isPostGraduate: true}),
    $KTM(QualificationDegree.PGD_CLINICAL_PATHOLOGY, {
      text: "Diploma in Clinical Pathology",
      shortText: "PGD",
      isPostGraduate: true
    }),
    $KTM(QualificationDegree.PGD_DVL, {text: "Diploma in DVL", shortText: "PGD", isPostGraduate: true}),
    $KTM(QualificationDegree.PGD_ENT, {text: "Diploma in ENT", shortText: "PGD", isPostGraduate: true}),
    $KTM(QualificationDegree.PGD_HEALTH_EDUCATION, {
      text: "Diploma in Health Education",
      shortText: "PGD",
      isPostGraduate: true
    }),
    $KTM(QualificationDegree.PGD_IMMUNOHAEMATOLOGY, {
      text: "Diploma in Immunohaematology",
      shortText: "PGD",
      isPostGraduate: true
    }),
    $KTM(QualificationDegree.PGD_OBSTETRICS_GYNAECOLOGY, {
      text: "Diploma in Obstetrics & Gynaecology",
      shortText: "PGD",
      isPostGraduate: true
    }),
    $KTM(QualificationDegree.PGD_OCCUPATIONAL_HEALTH, {
      text: "Diploma in Occupational Health",
      shortText: "PGD",
      isPostGraduate: true
    }),
    $KTM(QualificationDegree.PGD_OPHTHALMOLOGY, {
      text: "Diploma in Ophthalmology",
      shortText: "PGD",
      isPostGraduate: true
    }),
    $KTM(QualificationDegree.PGD_ORTHOPAEDICS, {
      text: "Diploma in Orthopaedics",
      shortText: "PGD",
      isPostGraduate: true
    }),
    $KTM(QualificationDegree.PGD_PAEDIATRICS, {
      text: "Diploma in Paediatrics",
      shortText: "PGD",
      isPostGraduate: true
    }),
    $KTM(QualificationDegree.PGD_PSYCHIATRY, {text: "Diploma in Psychiatry", shortText: "PGD", isPostGraduate: true}),
    $KTM(QualificationDegree.PGD_PHYSICAL_MEDICINE_REHABILITATION, {
      text: "Diploma in Physical Medicine & Rehabilitation",
      shortText: "PGD",
      isPostGraduate: true
    }),
    $KTM(QualificationDegree.PGD_PULMONARY_MEDICINE, {
      text: "Diploma in Pulmonary medicine",
      shortText: "PGD",
      isPostGraduate: true
    }),
    $KTM(QualificationDegree.PGD_RADIO_DIAGNOSIS, {
      text: "Diploma in Radio-diagnosis",
      shortText: "PGD",
      isPostGraduate: true
    }),
    $KTM(QualificationDegree.PGD_RADIATION_MEDICINE, {
      text: "Diploma in Radiation Medicine",
      shortText: "PGD",
      isPostGraduate: true
    }),
    $KTM(QualificationDegree.PGD_SPORTS_MEDICINE, {
      text: "Diploma in Sports Medicine",
      shortText: "PGD",
      isPostGraduate: true
    }),
    $KTM(QualificationDegree.PGD_TROPICAL_MEDICINE, {
      text: "Diploma in Tropical medicine",
      shortText: "PGD",
      isPostGraduate: true
    }),
    $KTM(QualificationDegree.PGD_ALLERGY_CLINICAL_IMMUNOLOGY, {
      text: "Diploma in Allergy & Clinical Immunology",
      shortText: "PGD",
      isPostGraduate: true
    }),

    // MD
    $KTM(QualificationDegree.MD, {text: "MD (Doctor of Medicine)", shortText: "MD", isPostGraduate: true}),
    $KTM(QualificationDegree.MD_AEROSPACE_MEDICINE, {
      text: "MD in Aerospace Medicine",
      shortText: "MD",
      isPostGraduate: true
    }),
    $KTM(QualificationDegree.MD_ANATOMY, {text: "MD in Anatomy", shortText: "MD", isPostGraduate: true}),
    $KTM(QualificationDegree.MD_ANESTHESIA, {text: "MD in Anesthesia", shortText: "MD", isPostGraduate: true}),
    $KTM(QualificationDegree.MD_BIOCHEMISTRY, {text: "MD in Biochemistry", shortText: "MD", isPostGraduate: true}),
    $KTM(QualificationDegree.MD_COMMUNITY_MEDICINE, {
      text: "MD in Community medicine",
      shortText: "MD",
      isPostGraduate: true
    }),
    $KTM(QualificationDegree.MD_DERMATOLOGY_VENEREOLOGY_LEPROSY, {
      text: "MD in Dermatology, Venereology & Leprosy",
      shortText: "MD",
      isPostGraduate: true
    }),
    $KTM(QualificationDegree.MD_FAMILY_MEDICINE, {
      text: "MD in Family Medicine",
      shortText: "MD",
      isPostGraduate: true
    }),
    $KTM(QualificationDegree.MD_FORENSIC_MEDICINE, {
      text: "MD in Forensic Medicine",
      shortText: "MD",
      isPostGraduate: true
    }),
    $KTM(QualificationDegree.MD_GENERAL_MEDICINE, {
      text: "MD in General Medicine",
      shortText: "MD",
      isPostGraduate: true
    }),
    $KTM(QualificationDegree.MD_GERIATRICS, {text: "MD in Geriatrics", shortText: "MD", isPostGraduate: true}),
    $KTM(QualificationDegree.MD_HEALTH_ADMINISTRATION, {
      text: "MD in Health Administration",
      shortText: "MD",
      isPostGraduate: true
    }),
    $KTM(QualificationDegree.MD_HOSPITAL_ADMINISTRATION, {
      text: "MD in Hospital Administration",
      shortText: "MD",
      isPostGraduate: true
    }),
    $KTM(QualificationDegree.MD_IMMUNOHAEMATOLOGY, {
      text: "MD in Immunohaematology",
      shortText: "MD",
      isPostGraduate: true
    }),
    $KTM(QualificationDegree.MD_MICROBIOLOGY, {text: "MD in Microbiology", shortText: "MD", isPostGraduate: true}),
    $KTM(QualificationDegree.MD_NUCLEAR_MEDICINE, {
      text: "MD in Nuclear medicine",
      shortText: "MD",
      isPostGraduate: true
    }),
    $KTM(QualificationDegree.MD_PAEDIATRICS, {text: "MD in Paediatrics", shortText: "MD", isPostGraduate: true}),
    $KTM(QualificationDegree.MD_PATHOLOGY, {text: "MD in Pathology", shortText: "MD", isPostGraduate: true}),
    $KTM(QualificationDegree.MD_PHARMACOLOGY, {text: "MD in Pharmacology", shortText: "MD", isPostGraduate: true}),
    $KTM(QualificationDegree.MD_PSYCHIATRY, {text: "MD in Psychiatry", shortText: "MD", isPostGraduate: true}),
    $KTM(QualificationDegree.MD_PHYSICAL_MEDICINE_REHABILITATION, {
      text: "MD in Physical Medicine & Rehabilitation",
      shortText: "MD",
      isPostGraduate: true
    }),
    $KTM(QualificationDegree.MD_PHYSIOLOGY, {text: "MD in Physiology", shortText: "MD", isPostGraduate: true}),
    $KTM(QualificationDegree.MD_PULMONARY_MEDICINE, {
      text: "MD in Pulmonary medicine",
      shortText: "MD",
      isPostGraduate: true
    }),
    $KTM(QualificationDegree.MD_RADIODIAGNOSIS, {text: "MD in Radiodiagnosis", shortText: "MD", isPostGraduate: true}),
    $KTM(QualificationDegree.MD_RADIOTHERAPY, {text: "MD in Radiotherapy", shortText: "MD", isPostGraduate: true}),
    $KTM(QualificationDegree.MD_SPORTS_MEDICINE, {
      text: "MD in Sports Medicine",
      shortText: "MD",
      isPostGraduate: true
    }),
    $KTM(QualificationDegree.MD_TROPICAL_MEDICINE, {
      text: "MD in Tropical Medicine",
      shortText: "MD",
      isPostGraduate: true
    }),
    $KTM(QualificationDegree.MD_PALLIATIVE_MEDICINE, {
      text: "MD in Palliative Medicine",
      shortText: "MD",
      isPostGraduate: true
    }),
    $KTM(QualificationDegree.MD_MARINE_MEDICINE, {
      text: "MD in Marine Medicine",
      shortText: "MD",
      isPostGraduate: true
    }),
    $KTM(QualificationDegree.MD_LABORATORY_MEDICINE, {
      text: "MD in Laboratory Medicine",
      shortText: "MD",
      isPostGraduate: true
    }),
    $KTM(QualificationDegree.MD_EMERGENCY_MEDICINE, {
      text: "MD in Emergency Medicine",
      shortText: "MD",
      isPostGraduate: true
    }),


    // MS
    $KTM(QualificationDegree.MS, {text: "MS (Master of Surgery)", shortText: "MS", isPostGraduate: true}),
    $KTM(QualificationDegree.MS_ENT, {text: "MS in ENT", shortText: "MS", isPostGraduate: true}),
    $KTM(QualificationDegree.MS_GENERAL_SURGERY, {
      text: "MS in General Surgery",
      shortText: "MS",
      isPostGraduate: true
    }),
    $KTM(QualificationDegree.MS_OBSTETRICS_GYNAECOLOGY, {
      text: "MS in Obstetrics & Gynaecology",
      shortText: "MS",
      isPostGraduate: true
    }),
    $KTM(QualificationDegree.MS_OPHTHALMOLOGY, {text: "MS in Ophthalmology", shortText: "MS", isPostGraduate: true}),
    $KTM(QualificationDegree.MS_ORTHOPAEDICS, {text: "MS in Orthopaedics", shortText: "MS", isPostGraduate: true}),
    $KTM(QualificationDegree.MS_TRAUMATOLOGY_SURGERY, {
      text: "MS in Traumatology & Surgery",
      shortText: "MS",
      isPostGraduate: true
    }),


    // DM
    $KTM(QualificationDegree.DM, {text: "DM (Doctorate of Medicine)", shortText: "DM", isPostGraduate: true}),
    $KTM(QualificationDegree.DM_CLINICAL_IMMUNOLOGY_RHEUMATOLOGY, {
      text: "DM in Clinical Immunology and Rheumatology",
      shortText: "DM",
      isPostGraduate: true
    }),
    $KTM(QualificationDegree.DM_CRITICAL_CARE_MEDICINE, {
      text: "DM in Critical Care Medicine",
      shortText: "DM",
      isPostGraduate: true
    }),
    $KTM(QualificationDegree.DM_HEPATOLOGY, {text: "DM in Hepatology", shortText: "DM", isPostGraduate: true}),
    $KTM(QualificationDegree.DM_NEPHROLOGY, {text: "DM in Nephrology", shortText: "DM", isPostGraduate: true}),
    $KTM(QualificationDegree.DM_NEUROLOGY, {text: "DM in Neurology", shortText: "DM", isPostGraduate: true}),
    $KTM(QualificationDegree.DM_ONCOLOGY, {text: "DM in Oncology", shortText: "DM", isPostGraduate: true}),
    $KTM(QualificationDegree.DM_NEUROANESTHESIA, {
      text: "DM in Neuroanesthesia",
      shortText: "DM",
      isPostGraduate: true
    }),
    $KTM(QualificationDegree.DM_NEURORADIOLOGY, {text: "DM in Neuroradiology", shortText: "DM", isPostGraduate: true}),
    $KTM(QualificationDegree.DM_ORGAN_TRANSPLANT_ANAESTHESIA_CRITICAL_CARE, {
      text: "DM in Organ Transplant Anaesthesia and Critical Care",
      shortText: "DM",
      isPostGraduate: true
    }),
    $KTM(QualificationDegree.DM_PEDIATRIC_ONCOLOGY, {
      text: "DM in Pediatric Oncology",
      shortText: "DM",
      isPostGraduate: true
    }),
    $KTM(QualificationDegree.DM_PEDIATRIC_HEPATOLOGY, {
      text: "DM in Pediatric Hepatology",
      shortText: "DM",
      isPostGraduate: true
    }),
    $KTM(QualificationDegree.DM_PEDIATRIC_NEPHROLOGY, {
      text: "DM in Pediatric Nephrology",
      shortText: "DM",
      isPostGraduate: true
    }),
    $KTM(QualificationDegree.DM_PEDIATRIC_NEUROLOGY, {
      text: "DM in Pediatric Neurology",
      shortText: "DM",
      isPostGraduate: true
    }),
    $KTM(QualificationDegree.DM_VIROLOGY, {text: "DM in Virology", shortText: "DM", isPostGraduate: true}),
    $KTM(QualificationDegree.DM_INFECTIOUS_DISEASES, {
      text: "DM in Infectious Diseases",
      shortText: "DM",
      isPostGraduate: true
    }),
    $KTM(QualificationDegree.DM_ENDOCRINOLOGY, {
      text: "DM in Endocrinology",
      shortText: "DM",
      isPostGraduate: true
    }),
    $KTM(QualificationDegree.DM_MEDICAL_GENETICS, {
      text: "DM in Medical Genetics",
      shortText: "DM",
      isPostGraduate: true
    }),
    $KTM(QualificationDegree.DM_GERIATRIC_MENTAL_HEALTH, {
      text: "DM in Geriatric Mental Health",
      shortText: "DM",
      isPostGraduate: true
    }),
    $KTM(QualificationDegree.DM_NEONATOLOGY, {text: "DM in Neonatology", shortText: "DM", isPostGraduate: true}),
    $KTM(QualificationDegree.DM_CLINICAL_PHARMACOLOGY, {
      text: "DM in Clinical Pharmacology",
      shortText: "DM",
      isPostGraduate: true
    }),

    // MCH
    $KTM(QualificationDegree.MCH, {text: "MCh (Master of Chirurgiae)", shortText: "MCh", isPostGraduate: true}),
    $KTM(QualificationDegree.MCH_GYNAECOLOGICAL_ONCOLOGY, {
      text: "MCh in Gynaecological Oncology",
      shortText: "MCh",
      isPostGraduate: true
    }),
    $KTM(QualificationDegree.MCH_HAND_SURGERY, {text: "MCh in Hand Surgery", shortText: "MCh", isPostGraduate: true}),
    $KTM(QualificationDegree.MCH_HEPATO_PANCREATO_BILIARY_SURGERY, {
      text: "MCh in Hepato Pancreato Biliary Surgery",
      shortText: "MCh",
      isPostGraduate: true
    }),
    $KTM(QualificationDegree.MCH_REPRODUCTIVE_MEDICINE_SURGERY, {
      text: "MCh in Reproductive Medicine & Surgery",
      shortText: "MCh",
      isPostGraduate: true
    }),
    $KTM(QualificationDegree.MCH_VASCULAR_SURGERY, {
      text: "MCh in Vascular Surgery",
      shortText: "MCh",
      isPostGraduate: true
    }),
    $KTM(QualificationDegree.MCH_SURGICAL_GASTROENTEROLOGY, {
      text: "MCh in Surgical Gastroenterology",
      shortText: "MCh",
      isPostGraduate: true
    }),
    $KTM(QualificationDegree.MCH_PEDIATRIC_SURGERY, {
      text: "MCh in Pediatric Surgery",
      shortText: "MCh",
      isPostGraduate: true
    }),
    $KTM(QualificationDegree.MCH_PLASTIC_RECONSTRUCTIVE_SURGERY, {
      text: "MCh in Plastic & Reconstructive Surgery",
      shortText: "MCh",
      isPostGraduate: true
    }),
    $KTM(QualificationDegree.MCH_ENDOCRINE_SURGERY, {
      text: "MCh in Endocrine Surgery",
      shortText: "MCh",
      isPostGraduate: true
    }),
  ])
};

export const QUALIFICATION_COMPLETION_YEARS_KTS = new KeyTextStrings(new Array(50).fill(null).map((_, index) => {
  const ys = toYearstampWithOffset(-index);
  return $KTS(ys, ys);
}));

export const SPECIALTIES: AppTypeObject<string[]> = {
  docwyze: [
    "Pediatrics",
    "Internal Medicine",
    "General Physician",
    "Cardiology",
    "Urology",
    "Diabetology",
    "Pulmonology",
    "Endocrinology",
    "Dermatology",
    "Gastroenterology",
    "Hepatology",
    "Ophthalmology",
    "Gynecology",
    "E.N.T.",
    "Orthopedics",
    "Neurology",
    "Nephrology",
    "Oncology",
    "Hematology",
    "Physical Health",
    "Therapy",
    "Psychiatry",
    "Primary Care",
  ],
  dentwyze: [
    "Dentist",
    "Oral Surgeon",
    "Orthodontist",
    "Pediatric Dentist",
    "Prosthodontist",
    "Endodontist",
    "Periodontist",
  ]
};


export const SERVICES_KTS: AppTypeObject<KeyTextStrings> = {
  dentwyze: new KeyTextStrings([
    "3 Shape",
    "3D CBCT",
    "Acrylic Partial Dentures",
    "Advanced Implant Surgery",
    "Air Polishing",
    "Alveolectomy",
    "Alveoloplasty",
    "Amalgam Removal",
    "Apicoectomy or Endo Surgery",
    "Artificial Teeth",
    "Biopsy",
    "Bleeding Gums Treatment",
    "BPS Dentures Fixing",
    "CAD/CAM Dentistry",
    "Cast Partial Denture",
    "Ceramic Crowns / Bridges",
    "Ceramic Veneers / Crowns ",
    "Cerec CADCAM",
    "Channel Treatment",
    "Clear Aligner Therapy",
    "Cleft Lip and Palate",
    "Cleft Lip Repair",
    "Complete / Partial Dentures",
    "Composite Bondings",
    "Composite Restorations",
    "Conservative Dentistry",
    "Cosmetic Filling",
    "Cosmetic Makeovers",
    "Cosmetic Veneers/Bonding",
    "Cosmetic / Aesthetic Dentistry",
    "Crown & Bridge",
    "Cryosurgery",
    "Damon Sel Ligating Braces",
    "Damon System",
    "Dental Braces Fixing",
    "Dental Checkup",
    "Dental Contact lens",
    "Dental Crown",
    "Dental Examinations",
    "Dental Fillings",
    "Dental Implant Fixing",
    "Dental Implant Rehabilitation",
    "Dental Implant",
    "Dental Restoration",
    "Dental Screening and Prophylaxis",
    "Dental Sealant",
    "Dental X Ray / RVG",
    "Endo Surgery Or Apicoectomy",
    "Endodontic Treatment",
    "Endodontics",
    "Endosurgery",
    "Facet Dental",
    "Facial Aesthetic Surgery",
    "Facial Fracture Treatment",
    "Facial Trauma",
    "Family Dentistry",
    "Fillings",
    "Fixed Partial Denture (FPD)",
    "Fixed Prosthodontics",
    "Flap Surgery",
    "Flexible Partial / Complete Denture",
    "Flexite",
    "Forensic Odontology",
    "Full Denture (Acrylic)",
    "Gap Closing",
    "General  Dentistry",
    "General Anaesthesia",
    "Geriatric Dentistry",
    "Gingivoplasty",
    "Gum Disease Treatment/ Surgery",
    "Immediate Dentures",
    "Impaction / Impacted Tooth Extraction",
    "Implant Dentistry",
    "Implant Prosthesis",
    "Implant Rehabilitation",
    "Implant Retained Dentures",
    "Implantology",
    "In-house ceramist",
    "Inlays and Onlays",
    "Intra Oral Scanner",
    "Invisalign / Clear Aligners",
    "Itero",
    "Jaw Orthopedics",
    "Kids Dental Treatment",
    "Laminates",
    "Laser Dentistry",
    "Laser Depigmentation",
    "Laser Gingivoplasty",
    "Laser Gum Surgery",
    "Laser Lip Surgery",
    "Laser Surgery",
    "Mandibular Fracture Treatment",
    "Maxilla Fracture Treatment",
    "Maxillo Facial Prosthodontics",
    "Maxillo Facial Prosthetics",
    "Maxillo Facial Trauma",
    "Metal Braces Fixing",
    "Metal / Ceramic Braces",
    "Minimally Invasive Dentistry",
    "Minor Surgery",
    "Mouth Guard",
    "Myofunctional Orthodontics",
    "Nitrous Oxide Sedation/ Conscious Sedation",
    "Occlusual Therapy",
    "OPG",
    "Oral & Maxillofacial Surgery",
    "Oral and Maxillofacial Pathology",
    "Oral and Maxillofacial Radiology",
    "Oral and Maxillofacial Surgery",
    "Oral Cancer Screening",
    "Oral Cancer Treatment",
    "Oral Cancer",
    "Oral Lesions Screening",
    "Oral Rehabilitation",
    "Oral Surgery Procedures",
    "Orthagnathic Surgery",
    "Orthodontic Treatment",
    "Orthognathic Surgery",
    "Orthomolecular medicine",
    "Osseointegrated implants",
    "Paedodontics",
    "Palate Surgery",
    "Partial Denture (Metal Based)",
    "Partial Denture of Mold",
    "Pediatric Dentistry Treatment",
    "Pediatric Dentistry",
    "Periodontal Flap Surgery",
    "Periodontal Surgery",
    "Periodontal Treatment",
    "Periodontitis / Gingivitis Treatment",
    "Pit and Fissure Sealents",
    "Porcelain inlays and onlays",
    "Post and Core",
    "Post Surgical Orthodontics",
    "Pre Surgical Orthodontics",
    "Preadjusted Edgewise Technique",
    "Preparation for Dentures",
    "Presurgical Orthodontics",
    "Prosthetic Care and Rehabilitation",
    "Prosthetics",
    "Prosthodontic Treatment",
    "Pulpectomy",
    "Pulpotomy",
    "RCT - Root Canal Treatment",
    "Re Root Canal Treatment",
    "Reconstruction & Rehabilitation",
    "Retainers",
    "Retention implant dentures",
    "Rhinoplasty",
    "Root Canal Treatment",
    "Scaling / Polishing",
    "Scar Revision Surgery",
    "Scrape",
    "Sedation Dentistry",
    "Smile Design",
    "Smile designing",
    "Snap-On Smile",
    "Space Maintainers",
    "Sterilization",
    "Straightening Teeth (Conventional Braces)",
    "Straightening Teeth (Invisalign) ",
    "Surgical Orthodontics",
    "Surgical Tooth Extraction",
    "Teeth - Jewellery",
    "Teeth Reshaping",
    "Teeth Whitening",
    "Temporomandibular Joint Disorder",
    "Thineers",
    "TMJ Pain Management",
    "TMJ Therapy",
    "Tooth Air Polishing",
    "Tooth Coloured Fillings",
    "Tooth Extraction",
    "Tooth Jewellery",
    "Tooth Recontouring",
    "Tooth Reshaping / Recountouring",
    "Tumors and Cysts",
    "Veneers / Laminates",
    "Veneers",
    "Wedding Smile Planner",
    "Wedding Smile Planning/ Designing",
    "Wisdom Tooth Extraction",
    "Zirconia Crowns",
    "Zirconium",
  ].map(name => $KTS(StringUtil.toSlug(name), name))),

  docwyze: new KeyTextStrings([
    "Consultation",
    "Counseling",
    "Pathology Testing",
    "Imaging",
    "X-Ray",
    "Ultrasound",
  ].map(name => $KTS(StringUtil.toSlug(name), name))),
};

export enum AppointmentDuration {
  MIN_15 = "min_15",
  MIN_30 = "min_30",
  MIN_45 = "min_45",
  HR_1 = "hr_1",
  HR_1_5 = "hr_1_5",
  HR_2 = "hr_2",
}

export type AppointmentDurationMetadata = {
  text: string,
  duration: number; // In millseconds
}

export const APPOINTMENT_DURATION_KTM = new KeyMetadatas<AppointmentDurationMetadata>([
  $KTM(AppointmentDuration.MIN_15, {text: "15 Minutes", duration: 15 * MINUTE_IN_MILLIS}),
  $KTM(AppointmentDuration.MIN_30, {text: "30 Minutes", duration: 30 * MINUTE_IN_MILLIS}),
  $KTM(AppointmentDuration.MIN_45, {text: "45 Minutes", duration: 45 * MINUTE_IN_MILLIS}),
  $KTM(AppointmentDuration.HR_1, {text: "1 Hour", duration: 60 * MINUTE_IN_MILLIS}),
  $KTM(AppointmentDuration.HR_1_5, {text: "1.5 Hours", duration: 90 * MINUTE_IN_MILLIS}),
  $KTM(AppointmentDuration.HR_2, {text: "2 Hours", duration: 120 * MINUTE_IN_MILLIS}),
]);

export enum ScheduleDay {
  MON = "mon",
  TUE = "tue",
  WED = "wed",
  THU = "thu",
  FRI = "fri",
  SAT = "sat",
  SUN = "sun",
}

export type ScheduleDayMetadata = {
  text: string,
  dayOfWeek: number,
}

export const SCHEDULE_DAY_KTM = new KeyMetadatas<ScheduleDayMetadata>([
  $KTM(ScheduleDay.MON, {text: "Mon", dayOfWeek: 1}),
  $KTM(ScheduleDay.TUE, {text: "Tue", dayOfWeek: 2}),
  $KTM(ScheduleDay.WED, {text: "Wed", dayOfWeek: 3}),
  $KTM(ScheduleDay.THU, {text: "Thu", dayOfWeek: 4}),
  $KTM(ScheduleDay.FRI, {text: "Fri", dayOfWeek: 5}),
  $KTM(ScheduleDay.SAT, {text: "Sat", dayOfWeek: 6}),
  $KTM(ScheduleDay.SUN, {text: "Sun", dayOfWeek: 0}),
]);

export enum ScheduleTime {
  AM_0000 = "am_0000",
  AM_0030 = "am_0030",
  AM_0100 = "am_0100",
  AM_0130 = "am_0130",
  AM_0200 = "am_0200",
  AM_0230 = "am_0230",
  AM_0300 = "am_0300",
  AM_0330 = "am_0330",
  AM_0400 = "am_0400",
  AM_0430 = "am_0430",
  AM_0500 = "am_0500",
  AM_0530 = "am_0530",
  AM_0600 = "am_0600",
  AM_0630 = "am_0630",
  AM_0700 = "am_0700",
  AM_0730 = "am_0730",
  AM_0800 = "am_0800",
  AM_0830 = "am_0830",
  AM_0900 = "am_0900",
  AM_0930 = "am_0930",
  AM_1000 = "am_1000",
  AM_1030 = "am_1030",
  AM_1100 = "am_1100",
  AM_1130 = "am_1130",
  PM_1200 = "pm_1200",
  PM_1230 = "pm_1230",
  PM_0100 = "pm_0100",
  PM_0130 = "pm_0130",
  PM_0200 = "pm_0200",
  PM_0230 = "pm_0230",
  PM_0300 = "pm_0300",
  PM_0330 = "pm_0330",
  PM_0400 = "pm_0400",
  PM_0430 = "pm_0430",
  PM_0500 = "pm_0500",
  PM_0530 = "pm_0530",
  PM_0600 = "pm_0600",
  PM_0630 = "pm_0630",
  PM_0700 = "pm_0700",
  PM_0730 = "pm_0730",
  PM_0800 = "pm_0800",
  PM_0830 = "pm_0830",
  PM_0900 = "pm_0900",
  PM_0930 = "pm_0930",
  PM_1000 = "pm_1000",
  PM_1030 = "pm_1030",
  PM_1100 = "pm_1100",
  PM_1130 = "pm_1130",
}

export type ScheduleTimeMetadata = {
  text: string,
  timeOffset: number, // In milliseconds, from midnight of that day.
}

export const SCHEDULE_TIME_KTM = new KeyMetadatas<ScheduleTimeMetadata>([
  $KTM(ScheduleTime.AM_0000, {text: "0:00 AM", timeOffset: 0 * HOUR_IN_MILLIS}),
  $KTM(ScheduleTime.AM_0030, {text: "0:30 AM", timeOffset: 0.5 * HOUR_IN_MILLIS}),
  $KTM(ScheduleTime.AM_0100, {text: "1:00 AM", timeOffset: 1 * HOUR_IN_MILLIS}),
  $KTM(ScheduleTime.AM_0130, {text: "1:30 AM", timeOffset: 1.5 * HOUR_IN_MILLIS}),
  $KTM(ScheduleTime.AM_0200, {text: "2:00 AM", timeOffset: 2 * HOUR_IN_MILLIS}),
  $KTM(ScheduleTime.AM_0230, {text: "2:30 AM", timeOffset: 2.5 * HOUR_IN_MILLIS}),
  $KTM(ScheduleTime.AM_0300, {text: "3:00 AM", timeOffset: 3 * HOUR_IN_MILLIS}),
  $KTM(ScheduleTime.AM_0330, {text: "3:30 AM", timeOffset: 3.5 * HOUR_IN_MILLIS}),
  $KTM(ScheduleTime.AM_0400, {text: "4:00 AM", timeOffset: 4 * HOUR_IN_MILLIS}),
  $KTM(ScheduleTime.AM_0430, {text: "4:30 AM", timeOffset: 4.5 * HOUR_IN_MILLIS}),
  $KTM(ScheduleTime.AM_0500, {text: "5:00 AM", timeOffset: 5 * HOUR_IN_MILLIS}),
  $KTM(ScheduleTime.AM_0530, {text: "5:30 AM", timeOffset: 5.5 * HOUR_IN_MILLIS}),
  $KTM(ScheduleTime.AM_0600, {text: "6:00 AM", timeOffset: 6 * HOUR_IN_MILLIS}),
  $KTM(ScheduleTime.AM_0630, {text: "6:30 AM", timeOffset: 6.5 * HOUR_IN_MILLIS}),
  $KTM(ScheduleTime.AM_0700, {text: "7:00 AM", timeOffset: 7 * HOUR_IN_MILLIS}),
  $KTM(ScheduleTime.AM_0730, {text: "7:30 AM", timeOffset: 7.5 * HOUR_IN_MILLIS}),
  $KTM(ScheduleTime.AM_0800, {text: "8:00 AM", timeOffset: 8 * HOUR_IN_MILLIS}),
  $KTM(ScheduleTime.AM_0830, {text: "8:30 AM", timeOffset: 8.5 * HOUR_IN_MILLIS}),
  $KTM(ScheduleTime.AM_0900, {text: "9:00 AM", timeOffset: 9 * HOUR_IN_MILLIS}),
  $KTM(ScheduleTime.AM_0930, {text: "9:30 AM", timeOffset: 9.5 * HOUR_IN_MILLIS}),
  $KTM(ScheduleTime.AM_1000, {text: "10:00 AM", timeOffset: 10 * HOUR_IN_MILLIS}),
  $KTM(ScheduleTime.AM_1030, {text: "10:30 AM", timeOffset: 10.5 * HOUR_IN_MILLIS}),
  $KTM(ScheduleTime.AM_1100, {text: "11:00 AM", timeOffset: 11 * HOUR_IN_MILLIS}),
  $KTM(ScheduleTime.AM_1130, {text: "11:30 AM", timeOffset: 11.5 * HOUR_IN_MILLIS}),
  $KTM(ScheduleTime.PM_1200, {text: "12:00 PM", timeOffset: 12 * HOUR_IN_MILLIS}),
  $KTM(ScheduleTime.PM_1230, {text: "12:30 PM", timeOffset: 12.5 * HOUR_IN_MILLIS}),
  $KTM(ScheduleTime.PM_0100, {text: "1:00 PM", timeOffset: 13 * HOUR_IN_MILLIS}),
  $KTM(ScheduleTime.PM_0130, {text: "1:30 PM", timeOffset: 13.5 * HOUR_IN_MILLIS}),
  $KTM(ScheduleTime.PM_0200, {text: "2:00 PM", timeOffset: 14 * HOUR_IN_MILLIS}),
  $KTM(ScheduleTime.PM_0230, {text: "2:30 PM", timeOffset: 14.5 * HOUR_IN_MILLIS}),
  $KTM(ScheduleTime.PM_0300, {text: "3:00 PM", timeOffset: 15 * HOUR_IN_MILLIS}),
  $KTM(ScheduleTime.PM_0330, {text: "3:30 PM", timeOffset: 15.5 * HOUR_IN_MILLIS}),
  $KTM(ScheduleTime.PM_0400, {text: "4:00 PM", timeOffset: 16 * HOUR_IN_MILLIS}),
  $KTM(ScheduleTime.PM_0430, {text: "4:30 PM", timeOffset: 16.5 * HOUR_IN_MILLIS}),
  $KTM(ScheduleTime.PM_0500, {text: "5:00 PM", timeOffset: 17 * HOUR_IN_MILLIS}),
  $KTM(ScheduleTime.PM_0530, {text: "5:30 PM", timeOffset: 17.5 * HOUR_IN_MILLIS}),
  $KTM(ScheduleTime.PM_0600, {text: "6:00 PM", timeOffset: 18 * HOUR_IN_MILLIS}),
  $KTM(ScheduleTime.PM_0630, {text: "6:30 PM", timeOffset: 18.5 * HOUR_IN_MILLIS}),
  $KTM(ScheduleTime.PM_0700, {text: "7:00 PM", timeOffset: 19 * HOUR_IN_MILLIS}),
  $KTM(ScheduleTime.PM_0730, {text: "7:30 PM", timeOffset: 19.5 * HOUR_IN_MILLIS}),
  $KTM(ScheduleTime.PM_0800, {text: "8:00 PM", timeOffset: 20 * HOUR_IN_MILLIS}),
  $KTM(ScheduleTime.PM_0830, {text: "8:30 PM", timeOffset: 20.5 * HOUR_IN_MILLIS}),
  $KTM(ScheduleTime.PM_0900, {text: "9:00 PM", timeOffset: 21 * HOUR_IN_MILLIS}),
  $KTM(ScheduleTime.PM_0930, {text: "9:30 PM", timeOffset: 21.5 * HOUR_IN_MILLIS}),
  $KTM(ScheduleTime.PM_1000, {text: "10:00 PM", timeOffset: 22 * HOUR_IN_MILLIS}),
  $KTM(ScheduleTime.PM_1030, {text: "10:30 PM", timeOffset: 22.5 * HOUR_IN_MILLIS}),
  $KTM(ScheduleTime.PM_1100, {text: "11:00 PM", timeOffset: 23 * HOUR_IN_MILLIS}),
  $KTM(ScheduleTime.PM_1130, {text: "11:30 PM", timeOffset: 23.5 * HOUR_IN_MILLIS}),
]);

@JsonObject()
export class ScheduleEntrySession {

  @FormGen({
    name: "Start time",
    type: "enum",
    enumValues: SCHEDULE_TIME_KTM.kts().values,
  })
  @JsonProperty()
  startTime: string = "";

  @FormGen({
    name: "End time",
    type: "enum",
    enumValues: SCHEDULE_TIME_KTM.kts().values,
  })
  @JsonProperty()
  endTime: string = "";
}

@JsonObject()
export class ScheduleEntry {

  @FormGen({
    name: "Days",
    type: "enum",
    enumMulti: true,
    enumMultiSortByValues: true,
    enumValues: SCHEDULE_DAY_KTM.kts().values,
    enumVariant: "button",
    required: true,
  })
  @JsonProperty()
  days: string[] = [];

  @FormGen({
    name: "Session 1",
    type: "object",
    required: true,
  })
  @JsonProperty()
  readonly session1: ScheduleEntrySession = new ScheduleEntrySession();

  @FormGen({
    name: "Session 2",
    type: "object",
  })
  @JsonProperty()
  readonly session2: ScheduleEntrySession = new ScheduleEntrySession();
}

@JsonObject()
export class Schedule {

  @FormGen({
    name: null,
    type: "array_object",
    arrayObjectCreator: () => new ScheduleEntry(),
    // required: true,
  })
  @JsonProperty({dataStructure: "array", type: ScheduleEntry})
  readonly entries: Array<ScheduleEntry> = new Array<ScheduleEntry>();
}

@JsonObject()
export class Calendar {

  @FormGen({
    name: "Default duration",
    type: "enum",
    description: "Choose the typical duration for an appointment at your practice. Your calendar's appointment slots will also use the same duration for online and myCare app bookings.",
    enumValues: APPOINTMENT_DURATION_KTM.kts().values,
    enumDefaultKey: AppointmentDuration.MIN_30,
  })
  @JsonProperty()
  defaultDuration: string = AppointmentDuration.MIN_30;

  @FormGen({
    name: "Practice schedule",
    type: "object",
    description: "Pick the days and the operating hours of your practice. These will also be used in your online profile and appointment booking pages.",
  })
  @JsonProperty()
  readonly schedule: Schedule = new Schedule();

  isOpen(): boolean {
    const now = Date.now();
    const today = dayjs(now)
      .set('h', 0)
      .set('m', 0)
      .set('s', 0)
      .set('ms', 0);
    const timeOffset = now - today.toDate().getTime();
    const dayOfWeek = today.day();
    if (this.schedule.entries?.length > 0) {
      for (const entry of this.schedule.entries) {
        const metadata = entry.days?.map(day => SCHEDULE_DAY_KTM.findMetadata(day))?.find(metadata => metadata.dayOfWeek === dayOfWeek);
        if (!metadata) {
          break;
        }
        for (const session of [entry.session1, entry.session2]) {
          const startTimeMetadata = session.startTime && SCHEDULE_TIME_KTM.findMetadata(session.startTime);
          const endTimeMetadata = session.endTime && SCHEDULE_TIME_KTM.findMetadata(session.endTime);
          if (!startTimeMetadata || !endTimeMetadata) {
            continue;
          }
          if (timeOffset >= startTimeMetadata.timeOffset && timeOffset < endTimeMetadata.timeOffset) {
            return true;
          }
        }
      }
    }
    return false;
  }
}

@JsonObject()
export class Services {

  @FormGen({
    name: "Consultation Fee",
    type: "string",
    inputMode: "numeric",
    required: true,
  })
  @JsonProperty()
  consultationFee: string;

  @FormGen({
    name: null,
    type: "enum",
    enumValues: appTypeObject(SERVICES_KTS).values,
    enumMulti: true,
    enumVariant: "button",
    required: true,
  })
  @JsonProperty()
  readonly services: string[] = [];
}

@JsonObject()
export class CaseFileEntry {

  @FormGen({
    name: "Photo",
    type: "profile_photo",
    imageEditor: new SimpleImageEditor(),
    imageOptions: {keepAspectRatio: 1},
    profilePhotoStoredDataSource: "case-files",
    description: "Patient identifiable information must be anonymized.",
    required: true,
  })
  @JsonProperty()
  photo: string;

  @FormGen({
    name: "Title",
    type: "string",
  })
  @JsonProperty()
  title: string;

  @FormGen({
    name: "Text",
    type: "string",
  })
  @JsonProperty()
  text: string;
}

@JsonObject()
export class CaseFile {

  @FormGen({
    name: "Before",
    type: "object",
  })
  @JsonProperty()
  readonly before: CaseFileEntry = new CaseFileEntry();

  @FormGen({
    name: "After",
    type: "object",
  })
  @JsonProperty()
  readonly after: CaseFileEntry = new CaseFileEntry();
}

@JsonObject()
export class CaseFiles {

  @FormGen({
    name: null,
    type: "array_object",
    arrayObjectCreator: () => new CaseFile(),
    // required: true,
  })
  @JsonProperty({dataStructure: "array", type: CaseFile})
  readonly entries: Array<CaseFile> = new Array<CaseFile>();
}

////////////// Pages and Profiles /////////////////

@JsonObject()
export class PatientAccountProfileBasicInfo {

  @FormGen({
    name: "Profile Photo",
    type: "profile_photo",
    description: "This photo is visible to anyone on your online profile.",
  })
  @JsonProperty()
  profilePhoto: string;

  @FormGen({
    name: "First Name",
    type: "string",
    noneditable: true,
    required: true,
  })
  @JsonProperty()
  firstname: string;

  @FormGen({
    name: "Last Name",
    type: "string",
    noneditable: true,
    required: true,
  })
  @JsonProperty()
  lastname: string;

  @FormGen({
    name: "Email",
    type: "string",
    inputMode: "email",
    noneditable: true,
    required: true,
  })
  @JsonProperty()
  email: string;

  @FormGen({
    name: "Gender",
    type: "enum",
    required: true,
    enumValues: GENDER_KTS.values,
    enumVariant: "button",
  })
  @JsonProperty()
  gender: string;

  @FormGen({
    name: "Phone Number",
    type: "phone_number",
    required: true,
  })
  @JsonProperty()
  phoneNumber: string;
}

@JsonObject()
export class PatientAccountProfileAddress {

  @FormGen({
    name: "House No., or Flat, with Street name",
    type: "string",
    required: true,
  })
  @JsonProperty()
  houseNumber: string;

  @FormGen({
    name: "Locality, Colony or Area",
    type: "string",
    required: true,
  })
  @JsonProperty()
  locality: string;

  @FormGen({
    name: "City",
    type: "string",
    required: true,
  })
  @JsonProperty()
  city: string;

  @FormGen({
    name: "State",
    type: "string",
    required: true,
  })
  @JsonProperty()
  state: string;

  @FormGen({
    name: "Country",
    type: "country_select",
    required: true,
  })
  @JsonProperty()
  country: string;

  @FormGen({
    name: "Pin or Zip Code",
    type: "string",
    required: true,
  })
  @JsonProperty()
  zipCode: string;
}

@JsonObject()
export class PatientAccountProfile extends AccountProfile {

  @FormGen({
    name: "Basic Info",
    type: "object",
  })
  @JsonProperty()
  readonly basicInfo: PatientAccountProfileBasicInfo = new PatientAccountProfileBasicInfo();

  @FormGen({
    name: "Address",
    type: "object",
  })
  @JsonProperty()
  readonly address: PatientAccountProfileAddress = new PatientAccountProfileAddress();

  constructor(accountId: string, firstname: string, lastname: string, profilePhoto: string, email: string) {
    super(accountId, AccountTypeName.PATIENT);
    this.basicInfo.firstname = firstname;
    this.basicInfo.lastname = lastname;
    this.basicInfo.profilePhoto = profilePhoto;
    this.basicInfo.email = email;
  }

  protected getType(): Type<any> {
    return PatientAccountProfile;
  }
}

@JsonObject()
export class ProviderProfileBasicInfo {

  @FormGen({
    name: "Profile Photo",
    type: "profile_photo",
    description: "This photo is publicly visible to anyone on your online profile",
    profilePhotoStoredDataSource: "profile-photos",
    imageEditor: new SimpleImageEditor(),
    imageOptions: {
      keepAspectRatio: 1,
    },
  })
  @JsonProperty()
  profilePhoto: string;

  @FormGen({
    name: "Prefix",
    type: "enum",
    enumValues: Object.values(NamePrefix).map(value => $KTS(value, value)),
  })
  @JsonProperty()
  prefix: NamePrefix;

  @FormGen({
    name: "First Name",
    type: "string",
    noneditable: true,
    required: true,
  })
  @JsonProperty()
  firstname: string;

  @FormGen({
    name: "Last Name",
    type: "string",
    noneditable: true,
    required: true,
  })
  @JsonProperty()
  lastname: string;

  @FormGen({
    name: "Email",
    type: "string",
    inputMode: "email",
    noneditable: true,
    required: true,
  })
  @JsonProperty()
  email: string;

  @FormGen({
    name: "Gender",
    type: "enum",
    required: true,
    enumValues: GENDER_KTS.values,
    enumVariant: "button",
  })
  @JsonProperty()
  gender: string;

  @FormGen({
    name: "Phone Number",
    type: "phone_number",
    required: true,
  })
  @JsonProperty()
  phoneNumber: string;

  @FormGen({
    name: "About",
    type: "text",
    size: "small",
    required: true,
    textAutoEnhancePrompt: "Re-write the following so that all the important points are kept intact but the result is very different from the original. Keep the language professional and neutral:",
  })
  @JsonProperty()
  about: string;

  @FormGen({
    name: "City",
    type: "enum",
    enumValues: CITIES_KTS.values,
  })
  @JsonProperty()
  cityId: string;

  displayName(): string {
    return DisplayName(this.firstname, this.lastname, this.prefix);
  }
}

@JsonObject()
export class ProviderProfileRegistration {

  @FormGen({
    name: "Registration Number",
    type: "string",
    required: true,
  })
  @JsonProperty()
  number: string;

  @FormGen({
    name: "Registration Council",
    type: "enum",
    _asyncInit: async (metadata: FormGenMetadata) => {
      metadata.enumValues = (await DentwyzePresetListsCache.getInstance().lists.getOrLoadList(PresetListKey.REGISTRATION_COUNCILS)).values;
    },
    required: true,
  })
  @JsonProperty()
  council: string;

  @FormGen({
    name: "Registration Year",
    type: "enum",
    enumValues: REGISTRATION_YEARS_KTS.values,
    required: true,
  })
  @JsonProperty()
  year: string;
}

@JsonObject()
export class ProviderProfileQualification {

  @FormGen({
    id: "degree",
    name: "Degree",
    type: "enum",
    enumValues: appTypeObject(QUALIFICATION_DEGREES_KTM).kts().values,
    required: true,
  })
  @JsonProperty()
  degree: string;

  @FormGen({
    name: "College or Institution",
    description: "College or institution from where this degree was earned.",
    type: "enum",
    _asyncInit: async (metadata: FormGenMetadata) => {
      metadata.enumValues = (await DentwyzePresetListsCache.getInstance().lists.getOrLoadList(PresetListKey.QUALIFICATION_COLLEGES)).values;
    },
    required: true,
  })
  @JsonProperty()
  college: string;

  @FormGen({
    name: "Completion Year",
    type: "enum",
    enumValues: QUALIFICATION_COMPLETION_YEARS_KTS.values,
    required: true,
  })
  @JsonProperty()
  completionYear: string;
}

@JsonObject()
export class ProviderProfileQualifications {

  @FormGen({
    name: "Degree(s)",
    description: "Please enter all your degrees, such as Bachelors, Post Graduate etc.",
    type: "array_object",
    arrayObjectCreator: () => new ProviderProfileQualification(),
    required: true,
  })
  @JsonProperty({dataStructure: "array", type: ProviderProfileQualification})
  readonly degrees: Array<ProviderProfileQualification> = new Array<ProviderProfileQualification>();

  @FormGen({
    name: "Years of Experience",
    type: "string",
    inputMode: "numeric",
    required: true,
  })
  @JsonProperty()
  yearsExperience: string;

  degreesText(): string {
    return this.degrees?.map(item => {
      const metadata = appTypeObject(QUALIFICATION_DEGREES_KTM).findMetadata(item.degree);
      if (!metadata) {
        return null;
      }
      return `${metadata.shortText}${metadata.isPostGraduate ? " (" + metadata.text + ")" : ""}`;
    }).filter(Boolean).join(", ") || "-";
  }
}

export enum ProviderCalendarType {
  INHERIT = "inherit",
  CUSTOM = "custom"
}

export const PROVIDER_CALENDAR_TYPE_KTS = new KeyTextStrings([
  $KTS(ProviderCalendarType.INHERIT, "Schedule is the same as practice"),
  $KTS(ProviderCalendarType.CUSTOM, "Schedule is different from practice"),
]);

@JsonObject()
export class ProviderProfileCalendar {

  @FormGen({
    id: "provider_profile_calendar",
    name: null,
    type: "enum",
    enumValues: PROVIDER_CALENDAR_TYPE_KTS.values,
    enumDefaultKey: ProviderCalendarType.INHERIT,
    enumVariant: "radio",
    description: "If schedule is different from practice, enter a custom schedule below.",
  })
  @JsonProperty()
  type: ProviderCalendarType = ProviderCalendarType.INHERIT;

  @FormGen({
    name: null,
    type: "object",
  })
  @JsonProperty()
  readonly calendar: Calendar = new Calendar();
}


export enum ProviderServicesType {
  INHERIT = "inherit",
  CUSTOM = "custom"
}

export const PROVIDER_SERVICES_TYPE_KTS = new KeyTextStrings([
  $KTS(ProviderServicesType.INHERIT, "Services are the same as Practice"),
  $KTS(ProviderServicesType.CUSTOM, "Services are different from Practice"),
]);

@JsonObject()
export class ProviderProfileServices {

  @FormGen({
    name: null,
    type: "enum",
    enumValues: PROVIDER_SERVICES_TYPE_KTS.values,
    enumDefaultKey: ProviderServicesType.INHERIT,
    enumVariant: "radio",
    description: "If services are different from practice, enter custom services below.",
  })
  @JsonProperty()
  type: ProviderServicesType = ProviderServicesType.INHERIT;

  @FormGen({
    name: null,
    type: "object",
  })
  @JsonProperty()
  readonly services: Services = new Services();
}

@JsonObject()
export class ProviderProfileCaseFiles {

  @FormGen({
    name: null,
    type: "object",
  })
  @JsonProperty()
  readonly caseFiles: CaseFiles = new CaseFiles();
}

export enum PageTypeName {
  PROVIDER = "pg_provider",
  PRACTICE = "pg_practice",
}

export type PageTypeNameMetadata = {
  text: string,
  iconName: string,
}

export const PAGE_TYPE_NAMES_KTM = new KeyMetadatas<PageTypeNameMetadata>([
  $KTM(PageTypeName.PRACTICE, {text: "Practice", iconName: "person"}),
  $KTM(PageTypeName.PROVIDER, {text: "Provider", iconName: "local_hospital"}),
]);

export enum PageVisibility {
  PROD = "prod",
  DRAFT = "draft",
  TEST = "test",
  DEMO = "demo",
}

export type PageVisibilityMetadata = {
  text: string,
  color: ColorValue,
}

export const PAGE_VISIBILITY_KTM = new KeyMetadatas<PageVisibilityMetadata>([
  $KTM(PageVisibility.PROD, {text: "Prod", color: {background: colorPastelGreen, color: "black"}}),
  $KTM(PageVisibility.DRAFT, {text: "Draft", color: {background: colorPastelYellow, color: "black"}}),
  $KTM(PageVisibility.TEST, {text: "Test", color: {background: colorPastelRed, color: "black"}}),
  $KTM(PageVisibility.DEMO, {text: "Demo", color: {background: colorPastelBlue, color: "black"}}),
]);

export enum PageStatus {
  ACTIVE = "active",
  SUSPENDED = "suspended",
  DEACTIVATED = "deactivated",
  DELETED = "deleted",
}

export type PageStatusMetadata = {
  text: string,
  colorValue: ColorValue,
}

export const PAGE_STATUS_KTM = new KeyMetadatas<PageStatusMetadata>([
  $KTM(PageStatus.ACTIVE, {text: "Active", colorValue: new ColorValue(colorDarkGreen, colorPastelGreen)}),
  $KTM(PageStatus.SUSPENDED, {text: "Suspended", colorValue: new ColorValue(black, colorYellow)}),
  $KTM(PageStatus.DEACTIVATED, {text: "Deactivated", colorValue: new ColorValue(colorRed, colorPastelRed)}),
  $KTM(PageStatus.DELETED, {text: "Deleted", colorValue: new ColorValue(white, colorRed)}),
]);

export enum PageVerificationStatus {
  UNVERIFIED = "unverified",
  PENDING = "pending",
  VERIFIED = "verified",
}

export type PageVerificationStatusMetadata = {
  text: string,
  iconName: string,
  colorValue: ColorValue,
}

export const PAGE_VERIFICATION_STATUS_KTM = new KeyMetadatas<PageVerificationStatusMetadata>([
  $KTM(PageVerificationStatus.UNVERIFIED, {
    text: "Unverified",
    iconName: "new_releases",
    colorValue: new ColorValue(colorRed, colorPastelRed)
  }),
  $KTM(PageVerificationStatus.PENDING, {
    text: "Pending",
    iconName: "clock",
    colorValue: new ColorValue(black, colorPastelYellow)
  }),
  $KTM(PageVerificationStatus.VERIFIED, {
    text: "Verified",
    iconName: "verified",
    colorValue: new ColorValue(colorDarkGreen, colorPastelGreen)
  }),
]);

@JsonObject()
abstract class BasePageData {

  private _page: Page; // Not serialized.

  getPage(): Page {
    return this._page;
  }

  setPage(page: Page): void {
    this._page = page;
  }
}

export enum PageProfileImportSourceType {
  GALILEO = md5_code("galileo"),
}

@JsonObject()
export class PageProfileImportSource {

  @JsonProperty()
  readonly type: PageProfileImportSourceType;

  @JsonProperty()
  readonly id: string;

  @JsonProperty()
  readonly doctorId: string;

  @JsonProperty()
  readonly practiceId: string;

  constructor(type: PageProfileImportSourceType, id: string, doctorId?: string, practiceId?: string) {
    this.type = type;
    this.id = id;
    this.doctorId = doctorId;
    this.practiceId = practiceId;
  }

  pageId(pageType: PageTypeName): string {
    let id: string;
    switch (pageType) {
      case PageTypeName.PROVIDER:
        id = this.doctorId;
        break;
      case PageTypeName.PRACTICE:
        id = this.practiceId;
        break;
    }
    if (!id) {
      id = this.id || "unknown";
    }
    return md5(`${this.type}-${id}-${pageType}`);
  }
}

export type PageProfileRoleMetadata = {
  text: string,
  iconType: typeof SvgIcon,
  description: string,
}

export enum PracticePageProfileRole {
  PRIVATE = "private",
  GROUP = "group",
  MULTI_SPECIALTY = "multi_specialty",
  SERVICE_ORGANIZATION = "service_organization",
  HOSPITAL = "hospital",
  PATHOLOGY_LAB = "pathology_lab",
  IMAGING_CENTER = "imaging_center",
  TEACHING_HOSPITAL = "teaching_hospital",
  EDU_INSTITUTE = "edu_institute",
}

export const PRACTICE_PAGE_PROFILE_ROLES_KTM = new KeyMetadatas<PageProfileRoleMetadata>([
  $KTM(PracticePageProfileRole.PRIVATE, {
    text: "Private Practice",
    description: "This is your private practice, which is supported by additional staff.",
    iconType: HomeOutlined,
  }),
  $KTM(PracticePageProfileRole.GROUP, {
    text: "Group Practice",
    description: "This is a group practice, where one or more providers may practice.",
    iconType: HomeWorkOutlined,
  }),
  $KTM(PracticePageProfileRole.MULTI_SPECIALTY, {
    text: "Multi-specialty Center",
    description: "This is a multi-specialty center, where one or more providers may practice multiple specialties.",
    iconType: BusinessOutlined,
  }),
  $KTM(PracticePageProfileRole.SERVICE_ORGANIZATION, {
    text: "Service Organization",
    description: "This is a service organization, supporting one or more practices.",
    iconType: MedicationOutlined,
  }),
  $KTM(PracticePageProfileRole.HOSPITAL, {
    text: "Hospital",
    description: "This is a full-service hospital.",
    iconType: LocalHospitalOutlined,
  }),
  $KTM(PracticePageProfileRole.PATHOLOGY_LAB, {
    text: "Pathology Lab",
    description: "This is a pathology laboratory.",
    iconType: BloodtypeOutlined,
  }),
  $KTM(PracticePageProfileRole.IMAGING_CENTER, {
    text: "Imaging Lab",
    description: "This is an imaging laboratory, with services such as Ultrasound, MRI etc.",
    iconType: PhotoOutlined,
  }),
  $KTM(PracticePageProfileRole.TEACHING_HOSPITAL, {
    text: "Teaching Hospital",
    description: "This is a teaching hospital, which trains medical staff in addition to providing hospital services.",
    iconType: EmergencyOutlined,
  }),
  $KTM(PracticePageProfileRole.EDU_INSTITUTE, {
    text: "Educational Institute",
    description: "This is an educational institure, which trains medical staff but does not provide medical services.",
    iconType: SchoolOutlined,
  }),
]);

export const PRACTICE_PAGE_PROFILE_SUPPORTED_ROLES: AppTypeObject<PracticePageProfileRole[]> = {
  dentwyze: [
    PracticePageProfileRole.PRIVATE,
    PracticePageProfileRole.GROUP,
    PracticePageProfileRole.SERVICE_ORGANIZATION,
    PracticePageProfileRole.TEACHING_HOSPITAL,
    PracticePageProfileRole.EDU_INSTITUTE,
  ],
  docwyze: [
    PracticePageProfileRole.PRIVATE,
    PracticePageProfileRole.GROUP,
    PracticePageProfileRole.MULTI_SPECIALTY,
    PracticePageProfileRole.SERVICE_ORGANIZATION,
    PracticePageProfileRole.HOSPITAL,
    PracticePageProfileRole.PATHOLOGY_LAB,
    PracticePageProfileRole.IMAGING_CENTER,
    PracticePageProfileRole.TEACHING_HOSPITAL,
    PracticePageProfileRole.EDU_INSTITUTE,
  ],
}

export enum ProviderPageProfileRole {
  CONSULTANT = "consultant",
  THERAPIST = "therapist",
  OTHER = "other",
}

export const PROVIDER_PAGE_PROFILE_ROLES_KTM = new KeyMetadatas<PageProfileRoleMetadata>([
  $KTM(ProviderPageProfileRole.CONSULTANT, {
    text: "Consultant",
    //  You must be registered with the appropriate licensing board and be legally allowed to diagnose and advise on treatments. For example, you practice in a field of medicine.
    description: "You work directly with patients, diagnose and prescribe medications.",
    iconType: PersonPinOutlined
  }),
  $KTM(ProviderPageProfileRole.THERAPIST, {
    text: "Therapist",
    //  You have an appropriate background or occupational experience in providing therapy and/or counseling. For example, you are a physiotherapist, mental health counselor or yoga instructor.
    description: "You work directly with patients, but do NOT diagnose or prescribe medications (e.g Physiotherapist, Counselor etc.)",
    iconType: SpaOutlined
  }),
  $KTM(ProviderPageProfileRole.OTHER, {
    text: "Other",
    //  For example, you are a dentist, surgeon or a professional with another certified medical license.
    description: "You may or or may not work directly with patients (e.g Dentist, Surgeon etc.)",
    iconType: PersonOutlined
  }),
]);

export const PROVIDER_PAGE_PROFILE_SUPPORTED_ROLES: AppTypeObject<ProviderPageProfileRole[]> = {
  dentwyze: [
    ProviderPageProfileRole.OTHER,
  ],
  docwyze: [
    ProviderPageProfileRole.CONSULTANT,
    ProviderPageProfileRole.THERAPIST,
    ProviderPageProfileRole.OTHER,
  ],
}

@JsonObject()
export abstract class PageProfile<R = PracticePageProfileRole | ProviderPageProfileRole> extends BasePageData {

  @JsonProperty()
  roles: R[];

  @JsonProperty()
  importSource: PageProfileImportSource;

  constructor(roles: R[]) {
    super();
    this.roles = roles;
  }
}

@JsonObject()
export abstract class PageVerification extends BasePageData {

}

@JsonObject()
export class Page extends BaseListItem {

  @JsonProperty()
  readonly type: PageTypeName;

  @JsonProperty()
  status: string = PageStatus.ACTIVE;

  @JsonProperty()
  visibility: string = PageVisibility.DRAFT;

  @JsonProperty()
  verificationStatus: PageVerificationStatus = PageVerificationStatus.UNVERIFIED;

  @JsonProperty()
  readonly claimCode: string;

  practicePageProfile?: PracticePageProfile; // Set only when type === PageTypeName.PRACTICE

  providerPageProfile?: ProviderPageProfile; // Set only when type === PageTypeName.PROVIDER

  @JsonProperty()
  _profile: any;

  practicePageVerification?: PracticePageVerification; // Set only when type === PageTypeName.PRACTICE

  providerPageVerification?: ProviderPageVerification; // Set only when type === PageTypeName.PROVIDER

  @JsonProperty()
  _verification: any;

  pageUrl(): string {
    return `https://dentwyze.in/pages/${this.toSlug()}`;
  }

  getFiles(): string[] {
    return [
      ...super.getFiles(),
      ...BaseListItem.getFilesOf(this.providerPageProfile),
      ...BaseListItem.getFilesOf(this.practicePageProfile),
      ...BaseListItem.getFilesOf(this.providerPageVerification),
      ...BaseListItem.getFilesOf(this.practicePageVerification),
    ];
  }

  async onBeforeItemSerialized(): Promise<void> {
    await super.onBeforeItemSerialized();
    switch (this.type) {
      case PageTypeName.PRACTICE: {
        // const profilePhoto = this.practicePageProfile.basicInfo.profilePhoto;
        // if (profilePhoto?.startsWith("blob:")) {
        //   const blob = await fetch(profilePhoto).then(res => res.blob());
        //   this.practicePageProfile.basicInfo.profilePhoto = await FileUtil.uploadBlob(blob, "practice-photos", null)
        // }
        await FormGenContainer.maybeUploadItemBlobs(this.practicePageProfile);
        this._profile = JSON_OBJECT.serializeObject(this.practicePageProfile);
        this._verification = this.practicePageVerification && JSON_OBJECT.serializeObject(this.practicePageVerification);
        break;
      }
      case PageTypeName.PROVIDER: {
        // const profilePhoto = this.providerPageProfile.basicInfo.profilePhoto;
        // if (profilePhoto?.startsWith("blob:")) {
        //   const blob = await fetch(profilePhoto).then(res => res.blob());
        //   this.providerPageProfile.basicInfo.profilePhoto = await FileUtil.uploadBlob(blob, "profile-photos", null)
        // }
        await FormGenContainer.maybeUploadItemBlobs(this.providerPageProfile);
        this._profile = JSON_OBJECT.serializeObject(this.providerPageProfile);
        this._verification = this.providerPageVerification && JSON_OBJECT.serializeObject(this.providerPageVerification);
        break;
      }
    }
  }

  // We should not assume Firebase is present in this method, as this object could be created by a non-app layout site page (using API calls).
  async onAfterItemDeserialized(): Promise<void> {
    await super.onAfterItemDeserialized();
    switch (this.type) {
      case PageTypeName.PRACTICE: {
        const practice = JSON_OBJECT.deserializeObject(this._profile, PracticePageProfile);
        practice.setPage(this);

        // const linkedProviderPages: Page[] = [];
        // const pageLinks = await PageLinks.getInstance().loadPageLinksByPracticePageId(this.id);
        // for (const pageLink of pageLinks) {
        //   if (pageLink.providerPageId !== this.id) {
        //     continue;
        //   }
        //   linkedProviderPages.push(await Pages.getInstance().getOrLoadItem(pageLink.providerPageId));
        // }
        // this.setLinkedProviderPages(linkedProviderPages);

        const cityId = practice.basicInfo.cityId;
        const localityId = practice.basicInfo.localityId;
        practice.basicInfo.locality = await LocalityNameById(cityId, localityId);
        practice.basicInfo.city = CITIES.find(loc => CityId(loc) === cityId)?.name;
        this.practicePageProfile = practice;

        if (this._verification) {
          this.practicePageVerification = JSON_OBJECT.deserializeObject(this._verification, PracticePageVerification);
        }
        break;
      }
      case PageTypeName.PROVIDER: {
        const profile = JSON_OBJECT.deserializeObject(this._profile, ProviderPageProfile);
        profile.setPage(this);

        // const linkedPracticePages: Page[] = [];
        // const pageLinks = await PageLinks.getInstance().loadPageLinksByProviderPageId(this.id);
        // for (const pageLink of pageLinks) {
        //   if (pageLink.providerPageId !== this.id) {
        //     continue;
        //   }
        //   linkedPracticePages.push(await Pages.getInstance().getOrLoadItem(pageLink.practicePageId));
        // }
        // this.setLinkedPracticePages(linkedPracticePages);

        this.providerPageProfile = profile;

        if (this._verification) {
          const verification = JSON_OBJECT.deserializeObject(this._verification, ProviderPageVerification);
          verification.setPage(this);
          this.providerPageVerification = verification;
        }
        break;
      }
    }
  }

  static createNewProviderPage(account: Account, roles: ProviderPageProfileRole[]): Page {
    const user = account.member.user;
    const page = this.createPage(PageTypeName.PROVIDER);
    const profile = new ProviderPageProfile(roles, user.firstname, user.lastname, user.profilePhoto, user.email);
    profile.setPage(page);
    page.providerPageProfile = profile;
    return page;
  }

  static createNewPracticePage(roles: PracticePageProfileRole[]): Page {
    const page = this.createPage(PageTypeName.PRACTICE);
    const profile = new PracticePageProfile(roles);
    profile.setPage(page);
    page.practicePageProfile = profile;
    return page;
  }

  static fromProvider(providerPageProfile: ProviderPageProfile): Page {
    const page = this.createPage(PageTypeName.PROVIDER, providerPageProfile.importSource);
    providerPageProfile.setPage(page);
    page.providerPageProfile = providerPageProfile;
    return page;
  }

  static fromPractice(practicePageProfile: PracticePageProfile): Page {
    const page = this.createPage(PageTypeName.PRACTICE, practicePageProfile.importSource);
    practicePageProfile.setPage(page);
    page.practicePageProfile = practicePageProfile;
    return page;
  }

  private static createPage(type: PageTypeName, importSource?: PageProfileImportSource): Page {
    const claimCode = passkey({
      length: 9,
      type: "alphanumeric_upper"
    });
    return new Page(importSource ? importSource.pageId(type) : md5_uuid(), claimCode, type);
  }

  static async fromJson(json: any): Promise<Page> {
    const page = JSON_OBJECT.deserializeObject(json, Page);
    await page?.onAfterItemDeserialized();
    return page;
  }

  constructor(id: string, claimCode: string, type: PageTypeName) {
    super(id);
    this.claimCode = claimCode;
    this.type = type;
  }

  async fetchLinkedPages(linkedPages: any[]): Promise<void> {
    switch (this.type) {
      case PageTypeName.PRACTICE:
        const linkedProviderPages = linkedPages ? JSON_OBJECT.deserializeObjectArray(linkedPages, Page) : [];
        for (const linkedPage of linkedProviderPages) {
          await linkedPage.onAfterItemDeserialized();
        }
        this.setLinkedProviderPages(linkedProviderPages);
        break;
      case PageTypeName.PROVIDER:
        const linkedPracticePages = linkedPages ? JSON_OBJECT.deserializeObjectArray(linkedPages, Page) : [];
        for (const linkedPage of linkedPracticePages) {
          await linkedPage.onAfterItemDeserialized();
        }
        this.setLinkedPracticePages(linkedPracticePages);
        break;
    }
  }

  private linkedPracticePages?: Page[];  // May be set only when type === PageTypeName.PROVIDER

  getLinkedPracticePages(): Page[] {
    return this.linkedPracticePages;
  }

  setLinkedPracticePages(linkedPracticePages: Page[]): void {
    if (this.type !== PageTypeName.PROVIDER) {
      throw new Error("Only a provider can be linked with practice pages.");
    }
    this.linkedPracticePages = linkedPracticePages;
  }


  private linkedProviderPages?: Page[];  // May be set only when type === PageTypeName.PRACTICE

  getLinkedProviderPages(): Page[] {
    return this.linkedProviderPages;
  }

  setLinkedProviderPages(linkedProviderPages: Page[]): void {
    if (this.type !== PageTypeName.PRACTICE) {
      throw new Error("Only a practice can be linked with provider pages.");
    }
    this.linkedProviderPages = linkedProviderPages;
  }

  toSlug(default_?: string): string {
    let slug: string;
    switch (this.type) {
      case PageTypeName.PRACTICE: {
        const name = this.practicePageProfile?.basicInfo.name;
        if (name) {
          slug = StringUtil.toSlug([name, this.id.substring(0, 4)].join(" "));
        }
        break;
      }
      case PageTypeName.PROVIDER: {
        const basicInfo = this.providerPageProfile?.basicInfo;
        const name = DisplayName(basicInfo?.firstname, basicInfo?.lastname);
        if (name) {
          slug = StringUtil.toSlug([basicInfo?.prefix, name, this.id.substring(0, 4)].filter(Boolean).join(" "));
        }
        break;
      }
    }
    return slug || default_;
  }

  static readonly CLAIM_CODE_REL_NAME = "ccs";

  claimCodeRel(): Rel {
    return new Rel(Page.CLAIM_CODE_REL_NAME, this.claimCode, this.id, false, true);
  }

  static readonly SLUG_REL_NAME = "slugs";

  slugRel(): Rel {
    const id1 = this.toSlug(null);
    if (!id1) {
      return null;
    }
    return new Rel(Page.SLUG_REL_NAME, id1, this.id, false, true);
  }

  static readonly LOCATION_REL_NAME = "locations";

  locationRel(): Rel {
    let id1: string;
    switch (this.type) {
      case PageTypeName.PRACTICE: {
        const basicInfo = this.practicePageProfile?.basicInfo;
        if (basicInfo?.cityId) {
          id1 = `${basicInfo.cityId}`;
        }
        break;
      }
      case PageTypeName.PROVIDER: {
        const basicInfo = this.providerPageProfile?.basicInfo;
        if (basicInfo?.cityId) {
          id1 = `${basicInfo.cityId}`;
        }
      }
    }
    if (!id1) {
      id1 = "unknown";
    }
    return new Rel(Page.LOCATION_REL_NAME, id1, this.id, true, true);
  }
}

export class Pages extends BaseListItemsLoader<Page> {

  private static instance: Pages;

  static getInstance(): Pages {
    if (!this.instance) {
      this.instance = new Pages();
    }
    return this.instance;
  }

  constructor() {
    super({
      shared: true,
      overrideProvisioningContext: BaseApp.CONTEXT.getAppConfig()?.loginConfig?.provisioning?.membersUseSharedProvisioningContext ? ProvisioningContext.MAIN : undefined,
    });
  }

  protected getRules(): Rule[] {
    return [
      ...super.getRules(),
      {
        indexOn: ["type"],
      },
    ];
  }

  async loadPageByClaimCode(claimCode: string): Promise<Page | null> {
    const id = await this.getProvisioningContext().dbRef_getVal(this.listPath() + Rel.oneToOnePathFor(Page.CLAIM_CODE_REL_NAME, claimCode));
    if (id) {
      return super.loadListItem(id);
    }
    return null;
  }

  async addPage(page: Page): Promise<void> {
    await this.addListItem(page, page.claimCodeRel(), page.slugRel(), page.locationRel());
  }

  async deletePage(page: Page): Promise<void> {
    await this.deleteListItem(page, page.claimCodeRel(), page.slugRel(), page.locationRel());
  }

  protected basePathTemplate(): string | null | undefined {
    return "pages";
  }

  protected basePath(): string {
    return this.basePathTemplate();
  }

  protected deserializeItem(value: any): Page {
    return JSON_OBJECT.deserializeObject(value, Page);
  }

  protected serializeItem(item: Page): any {
    return JSON_OBJECT.serializeObject(item);
  }

  protected sortOrder(item1: Page, item2: Page): number {
    return item1.created - item2.created;
  }
}

@JsonObject()
export class ProviderPageProfile extends PageProfile<ProviderPageProfileRole> {

  static multiStepConfigSteps(quickSetup?: boolean): FormGenMultiStepConfigStep[] {
    return [
      {text: "Profile"},
      {text: "Registration", skip: quickSetup},
      {text: "Qualifications"},
      {text: "Calendar", skip: quickSetup},
      {text: "Services", skip: quickSetup},
      {text: "Case Files", skip: quickSetup},
    ];
  }

  @FormGen({
    name: "Basic Info",
    type: "object",
    multiStep: true,
    multiStepIndex: 0,
  })
  @JsonProperty()
  readonly basicInfo: ProviderProfileBasicInfo = new ProviderProfileBasicInfo();

  @FormGen({
    name: "Medical Registration",
    type: "object",
    multiStep: true,
    multiStepIndex: 1,
  })
  @JsonProperty()
  readonly registration: ProviderProfileRegistration = new ProviderProfileRegistration();

  @FormGen({
    name: "Education Qualifications",
    type: "object",
    multiStep: true,
    multiStepIndex: 2,
  })
  @JsonProperty()
  readonly qualifications: ProviderProfileQualifications = new ProviderProfileQualifications();

  @FormGen({
    name: "Calendar",
    type: "object",
    multiStep: true,
    multiStepIndex: 3,
  })
  @JsonProperty()
  readonly calendar: ProviderProfileCalendar = new ProviderProfileCalendar();

  @FormGen({
    name: "Services",
    type: "object",
    multiStep: true,
    multiStepIndex: 4,
  })
  @JsonProperty()
  readonly services: ProviderProfileServices = new ProviderProfileServices();

  @FormGen({
    name: "Case Files",
    type: "object",
    multiStep: true,
    multiStepIndex: 5,
  })
  @JsonProperty()
  readonly caseFiles: ProviderProfileCaseFiles = new ProviderProfileCaseFiles();

  @JsonProperty()
  readonly ratingStats: RatingStats = new RatingStats();

  constructor(roles: ProviderPageProfileRole[], firstname: string, lastname: string, profilePhoto: string, email: string) {
    super(roles);
    this.basicInfo.firstname = firstname;
    this.basicInfo.lastname = lastname;
    this.basicInfo.profilePhoto = profilePhoto;
    this.basicInfo.email = email;
  }
}

// export function providerPageProfileAutocompleteLoaderCreator(practiceId: string) {
//   return async (): Promise<KeyTextString[]> => {
//     const pageLinks = await PageLinks.getInstance().loadPageLinksByPracticePageId(practiceId);
//     const providerPages: Page[] = [];
//     for (const pageLink of pageLinks) {
//       const page = await Pages.getInstance().getOrLoadItem(pageLink.providerPageId);
//       if (!page) {
//         continue;
//       }
//       providerPages.push(page);
//     }
//     return providerPages.map(page => $KTS(page.id, page.providerPageProfile.basicInfo.displayName()));
//   };
// }

export function ProviderFilter<T extends { providerId: string }>(providerId: string): Filter<T> {
  return item => !providerId || (item.providerId === providerId);
}

@JsonObject()
export class ProviderAccountProfile extends AccountProfile {

  constructor(accountId: string) {
    super(accountId, AccountTypeName.PROVIDER);
  }

  protected getType(): Type<any> {
    return ProviderAccountProfile;
  }
}

@JsonObject()
export class PracticePageProfileBasicInfo {

  @FormGen({
    name: "Profile Photo",
    type: "profile_photo",
    profilePhotoStoredDataSource: "practice-photos",
    imageEditor: new SimpleImageEditor(),
    profilePhotoDefaultImageUrl: "/images/default_practice.png",
    description: "This photo is publicly visible to anyone on your online profile",
    profilePhotoObjectFit: "contain",
  })
  @JsonProperty()
  profilePhoto: string;

  @FormGen({
    name: "Name",
    type: "string",
    required: true,
  })
  @JsonProperty()
  name: string;

  @FormGen({
    name: "Logo",
    type: "profile_photo",
    profilePhotoStoredDataSource: "practice-photos",
    imageEditor: new SimpleImageEditor(),
    profilePhotoDefaultImageUrl: "/images/default_logo.png",
    description: "This is the logo of your practice. Image should be square with a minimum size of 96x96 pixels.",
    profilePhotoObjectFit: "contain",
  })
  @JsonProperty()
  logo: string = "/images/default_logo.png";

  @FormGen({
    name: "Phone Number",
    type: "phone_number",
  })
  @JsonProperty()
  phoneNumber: string;

  @FormGen({
    name: "Email",
    type: "string",
    inputMode: "email",
  })
  @JsonProperty()
  email: string;

  @FormGen({
    name: "About",
    type: "text",
    size: "small",
    required: true,
  })
  @JsonProperty()
  about: string;

  city: string;

  @FormGen({
    name: "City",
    type: "enum",
    enumValues: CITIES_KTS.values,
    required: true,
  })
  @JsonProperty()
  cityId: string;

  locality: string;

  @FormGen({
    name: "Locality",
    type: "custom",
    customRenderer: (target: any, value: any, setValue: (value: any) => void, args?: {
      mode: FormGenContainerMode
    }) => renderLocalitySelect(target["cityId"], value, setValue, args?.mode),
    required: true,
  })
  @JsonProperty()
  localityId: string;
}

@JsonObject()
export class PracticePageProfileAddress {

  @FormGen({
    name: "House No., or Flat, with Street name",
    type: "string",
    required: true,
  })
  @JsonProperty()
  houseNumber: string;

  @FormGen({
    name: "Locality, Colony or Area",
    type: "string",
    required: true,
  })
  @JsonProperty()
  locality: string; // This is a string, not an id-based enum

  @FormGen({
    name: "City",
    type: "string",
    required: true,
  })
  @JsonProperty()
  city: string; // This is a string, not an id-based enum

  @FormGen({
    name: "State",
    type: "string",
    required: true,
  })
  @JsonProperty()
  state: string;

  @FormGen({
    name: "Country",
    type: "country_select",
    required: true,
  })
  @JsonProperty()
  country: string;

  @FormGen({
    name: "Pin or Zip Code",
    type: "string",
    required: true,
  })
  @JsonProperty()
  zipCode: string;

  @FormGen({
    name: "Location",
    type: "latlng",
  })
  @JsonProperty()
  latlng: number[];

  getAddressParts(): string[] {
    return [this.houseNumber, this.locality, this.city, this.state, this.zipCode].filter(Boolean);
  }

  getAddress2Line(): string[] {
    return [[this.houseNumber, this.locality].filter(Boolean).join(", "), [this.city, this.state, this.zipCode].filter(Boolean).join(", ")];
  }

  getAddress(join?: string): string {
    return this.getAddressParts().join(join || ", ");
  }
}

@JsonObject()
export class PracticePageProfile extends PageProfile<PracticePageProfileRole> {

  static multiStepConfigSteps(quickSetup?: boolean): FormGenMultiStepConfigStep[] {
    return [
      {text: "Basic Info"},
      {text: "Address"},
      {text: "Calendar", skip: quickSetup},
      {text: "Services", skip: quickSetup},
      {text: "Photos", skip: quickSetup},
    ];
  }

  @FormGen({
    name: "Basic Info",
    type: "object",
    multiStep: true,
    multiStepIndex: 0,
  })
  @JsonProperty()
  readonly basicInfo: PracticePageProfileBasicInfo = new PracticePageProfileBasicInfo();

  @FormGen({
    name: "Address",
    type: "object",
    multiStep: true,
    multiStepIndex: 1,
  })
  @JsonProperty()
  readonly address: PracticePageProfileAddress = new PracticePageProfileAddress();

  @FormGen({
    name: "Calendar",
    type: "object",
    multiStep: true,
    multiStepIndex: 2,
  })
  @JsonProperty()
  readonly calendar: Calendar = new Calendar();

  @FormGen({
    name: "Services",
    type: "object",
    multiStep: true,
    multiStepIndex: 3,
  })
  @JsonProperty()
  readonly services: Services = new Services();

  @FormGen({
    name: null,
    type: "custom",
    customRenderer: (target: any, value: any, setValue: (value: any) => void, args?: {
      mode: FormGenContainerMode
    }) => renderImageGalleryUpload(
      (target as PracticePageProfile).getPage().id,
      "practice-photos",
      value,
      setValue,
      GalleryHelper.showGalleryDialog,
      args?.mode),
    multiStep: true,
    multiStepIndex: 4,
  })
  @JsonProperty()
  readonly photos: string[];

  @JsonProperty()
  readonly ratingStats: RatingStats = new RatingStats();

  constructor(roles: PracticePageProfileRole[]) {
    super(roles);
  }

  protected getType(): Type<any> {
    return PracticePageProfile;
  }
}

@JsonObject()
export class ProviderPageVerification extends PageVerification {

  static multiStepConfigSteps(): FormGenMultiStepConfigStep[] {
    return [
      {text: "Identity"},
      {text: "Registration"}
    ];
  }

  @FormGen({
    name: "Identity Proof",
    type: "custom",
    customRenderer: (target: any, value: any, setValue: (value: any) => void, args?: {
      mode: FormGenContainerMode
    }) => renderProviderPageVerificationIdentity((target as ProviderPageVerification).getPage(), value, setValue),
    multiStep: true,
    multiStepIndex: 0,
    required: true,
  })
  @JsonProperty()
  identity: string;

  @FormGen({
    name: "Registration Proof",
    type: "custom",
    customRenderer: (target: any, value: any, setValue: (value: any) => void, args?: {
      mode: FormGenContainerMode
    }) => renderProviderPageVerificationRegistration((target as ProviderPageVerification).getPage(), value, setValue),
    multiStep: true,
    multiStepIndex: 1,
  })
  @JsonProperty()
  registration: string;
}

@JsonObject()
export class PracticePageVerification extends PageVerification {

  static multiStepConfigSteps(): FormGenMultiStepConfigStep[] {
    return [
      {text: "Identity"},
    ];
  }

  @FormGen({
    name: "Identity Proof",
    type: "custom",
    customRenderer: (target: any, value: any, setValue: (value: any) => void, args?: {
      mode: FormGenContainerMode
    }) => renderPracticePageVerificationIdentity((target as PracticePageVerification).getPage(), value, setValue),
    multiStep: true,
    multiStepIndex: 0,
    required: true,
  })
  @JsonProperty()
  identity: string;
}

@JsonObject()
export class StaffAccountProfile extends AccountProfile {

  @JsonProperty()
  readonly practiceId: string;

  @JsonProperty()
  readonly staffId: string;

  constructor(accountId: string, practiceId: string, staffId: string) {
    super(accountId, AccountTypeName.STAFF);
    this.practiceId = practiceId;
    this.staffId = staffId;
  }

  protected getType(): Type<any> {
    return StaffAccountProfile;
  }
}

@JsonObject()
export class ServiceAccountProfile extends AccountProfile {

  @JsonProperty()
  readonly practiceId: string;

  @JsonProperty()
  readonly serviceId: string;

  constructor(accountId: string, practiceId: string, serviceId: string) {
    super(accountId, AccountTypeName.SERVICE);
    this.practiceId = practiceId;
    this.serviceId = serviceId;
  }

  protected getType(): Type<any> {
    return ServiceAccountProfile;
  }
}

export enum AccountTypeName {
  PATIENT = "patient",
  PROVIDER = "provider",
  STAFF = "staff",
  SERVICE = "service",
}

export type AccountTypeNameMetadata = {
  text: string,
  iconType: typeof SvgIcon,
  description: string,
}

export const ACCOUNT_TYPE_NAMES_KTM = new KeyMetadatas<AccountTypeNameMetadata>([
  $KTM(AccountTypeName.PATIENT, {
    text: "Patient",
    iconType: PersonOutlined,
    description: "I'm a patient."
  }),
  $KTM(AccountTypeName.PROVIDER, {
    text: "Provider",
    iconType: BadgeOutlined,
    description: "I'm a dentist / doctor."
  }),
  // $KTM(AccountTypeName.STAFF, {
  //   text: "Staff",
  //   iconType: LocalHospitalOutlined,
  //   description: "I'm the owner or co-owner of a practice, a clinic or a hospital."
  // }),
]);


@JsonObject()
export class PageLink extends BaseListItem {

  @JsonProperty()
  readonly practicePageId: string;

  @JsonProperty()
  readonly providerPageId: string;

  static createNew(practicePageId: string, providerPageId: string): PageLink {
    return new PageLink(md5(practicePageId, providerPageId), practicePageId, providerPageId);
  }

  constructor(id: string, practicePageId: string, providerPageId: string) {
    super(id);
    this.practicePageId = practicePageId;
    this.providerPageId = providerPageId;
  }

  static readonly PRACTICE_PAGE_ID_REL_NAME = "practicePageIds";

  practicePageIdsRel(): Rel {
    return new Rel(PageLink.PRACTICE_PAGE_ID_REL_NAME, this.practicePageId, this.id, true, true);
  }

  static readonly PROVIDER_PAGE_ID_REL_NAME = "providerPageIds";

  providerPageIdsRel(): Rel {
    return new Rel(PageLink.PROVIDER_PAGE_ID_REL_NAME, this.providerPageId, this.id, true, true);
  }
}

export class PageLinks extends BaseListItemsLoader<PageLink> {

  private static instance: PageLinks;

  static getInstance(): PageLinks {
    if (!this.instance) {
      this.instance = new PageLinks();
    }
    return this.instance;
  }

  constructor() {
    super({
      shared: true,
      overrideProvisioningContext: BaseApp.CONTEXT.getAppConfig()?.loginConfig?.provisioning?.membersUseSharedProvisioningContext ? ProvisioningContext.MAIN : undefined,
    });
  }

  async loadListItems(): Promise<void> {
    throw new Error("Operation not allowed.");
  }

  async loadPageLinksByPracticePageId(practicePageId: string): Promise<PageLink[]> {
    const links: PageLink[] = [];
    const val = await this.getProvisioningContext().dbRef_getVal(this.listPath() + Rel.oneToOnePathFor(PageLink.PRACTICE_PAGE_ID_REL_NAME, practicePageId));
    if (val) {
      for (const key of Object.keys(val)) {
        links.push(await this.loadListItem(key));
      }
    }
    return links;
  }

  async loadPageLinksByProviderPageId(providerPageId: string): Promise<PageLink[]> {
    const links: PageLink[] = [];
    const val = await this.getProvisioningContext().dbRef_getVal(this.listPath() + Rel.oneToOnePathFor(PageLink.PROVIDER_PAGE_ID_REL_NAME, providerPageId));
    if (val) {
      for (const key of Object.keys(val)) {
        links.push(await this.loadListItem(key));
      }
    }
    return links;
  }

  async addPageLink(pageLink: PageLink): Promise<void> {
    return super.addListItem(pageLink, pageLink.practicePageIdsRel(), pageLink.providerPageIdsRel());
  }

  async deletePageLink(pageLink: PageLink): Promise<void> {
    return super.deleteListItem(pageLink, pageLink.practicePageIdsRel(), pageLink.providerPageIdsRel());
  }

  protected basePath(): string {
    return "pageLinks";
  }

  protected deserializeItem(value: any): PageLink {
    return JSON_OBJECT.deserializeObject(value, PageLink);
  }

  protected serializeItem(item: PageLink): any {
    return JSON_OBJECT.serializeObject(item);
  }

  protected sortOrder(item1: PageLink, item2: PageLink): number {
    return item2.created - item1.created;
  }
}

@JsonObject()
export class PageClaim extends BaseListItem {

  @JsonProperty()
  readonly pageId: string;

  @JsonProperty()
  readonly pageType: PageTypeName;

  @JsonProperty()
  readonly accountId: string;

  @JsonProperty()
  readonly role: "editor" | "viewer";

  static createNewEditor(pageId: string, pageType: PageTypeName, accountId: string): PageClaim {
    return new PageClaim(md5(pageId, pageType, accountId), pageId, pageType, accountId, "editor");
  }

  static createNewViewer(pageId: string, pageType: PageTypeName, accountId: string): PageClaim {
    return new PageClaim(md5(pageId, pageType, accountId), pageId, pageType, accountId, "viewer");
  }

  constructor(id: string, pageId: string, pageType: PageTypeName, accountId: string, role: "editor" | "viewer") {
    super(id);
    this.pageId = pageId;
    this.pageType = pageType;
    this.accountId = accountId;
    this.role = role;
  }

  static readonly ACCOUNT_ID_REL_NAME = "accountIds";

  accountIdsRel(): Rel {
    return new Rel(PageClaim.ACCOUNT_ID_REL_NAME, this.accountId, this.id, true, true);
  }
}

export class PageClaims extends BaseListItemsLoader<PageClaim> {

  private static instance: PageClaims;

  static getInstance(): PageClaims {
    if (!this.instance) {
      this.instance = new PageClaims();
    }
    return this.instance;
  }

  constructor() {
    super({
      shared: true,
      overrideProvisioningContext: BaseApp.CONTEXT.getAppConfig()?.loginConfig?.provisioning?.membersUseSharedProvisioningContext ? ProvisioningContext.MAIN : undefined,
    });
  }

  async loadListItems(): Promise<void> {
    throw new Error("Operation not allowed.");
  }

  private readonly pageClaimsByAccountId = new Map<string, PageClaim[]>();

  getPageClaimsByAccountId(accountId: string): PageClaim[] {
    return this.pageClaimsByAccountId.get(accountId) || [];
  }

  async loadPageClaimsByAccountId(accountId: string): Promise<PageClaim[]> {
    const claims: PageClaim[] = [];
    const val = await this.getProvisioningContext().dbRef_getVal(this.listPath() + Rel.oneToOnePathFor(PageClaim.ACCOUNT_ID_REL_NAME, accountId));
    if (val) {
      for (const key of Object.keys(val)) {
        const item = await this.loadListItem(key);
        if (!item) {
          continue;
        }
        claims.push(item);
      }
    }
    this.pageClaimsByAccountId.set(accountId, claims);
    return claims;
  }

  async addPageClaim(pageClaim: PageClaim): Promise<void> {
    return super.addListItem(pageClaim, pageClaim.accountIdsRel());
  }

  async deletePageClaim(pageClaim: PageClaim): Promise<void> {
    return super.deleteListItem(pageClaim, pageClaim.accountIdsRel());
  }

  protected basePath(): string {
    return "pageClaims";
  }

  protected deserializeItem(value: any): PageClaim {
    return JSON_OBJECT.deserializeObject(value, PageClaim);
  }

  protected serializeItem(item: PageClaim): any {
    return JSON_OBJECT.serializeObject(item);
  }

  protected sortOrder(item1: PageClaim, item2: PageClaim): number {
    return item2.created - item1.created;
  }
}

@JsonObject()
export class PatientPracticeLink extends BaseListItem {

  @JsonProperty()
  readonly patientMemberId: string;

  @JsonProperty()
  readonly patientAccountId: string;

  @JsonProperty()
  readonly practicePageId: string;

  @JsonProperty()
  readonly practicePatientId: string;

  static createNew(patientMemberId: string, patientAccountId: string, practicePageId: string, practicePatientId: string): PatientPracticeLink {
    // We use the practicePageId as 'id' since it uniquely and idempotently identifies a link for a patient-practice.
    return new PatientPracticeLink(practicePageId, patientMemberId, patientAccountId, practicePageId, practicePatientId);
  }

  constructor(id: string, patientMemberId: string, patientAccountId: string, practicePageId: string, practicePatientId: string) {
    super(id);
    this.patientMemberId = patientMemberId;
    this.patientAccountId = patientAccountId;
    this.practicePageId = practicePageId;
    this.practicePatientId = practicePatientId;
  }

  static readonly PRACTICE_PATIENT_LINK_REL_NAME = "practicePatientLinks";

  practicePatientLinksRel(): Rel {
    return new Rel(PatientPracticeLink.PRACTICE_PATIENT_LINK_REL_NAME, `${this.practicePageId}/${this.practicePatientId}`, `${this.patientMemberId}/${this.patientAccountId}/${this.id}`, false, true, 2);
  }
}

export class PatientPracticeLinks extends BaseListItemsLoader<PatientPracticeLink> {

  private static instance: PatientPracticeLinks;

  static createDefaultInstance(memberId: string, patientAccountProfileId: string): void {
    this.instance = new PatientPracticeLinks(memberId, patientAccountProfileId);
  }

  static getDefaultInstance(): PatientPracticeLinks {
    return this.instance;
  }

  constructor(readonly memberId: string, readonly patientAccountProfileId: string) {
    super({
      shared: true,
      overrideProvisioningContext: BaseApp.CONTEXT.getAppConfig()?.loginConfig?.provisioning?.membersUseSharedProvisioningContext ? ProvisioningContext.MAIN : undefined,
    });
  }

  async addListItem(item: PatientPracticeLink, ...rels): Promise<void> {
    throw new Error("Use addPatientPracticeLink instead");
  }

  async deleteListItem(item: PatientPracticeLink, ...rels): Promise<void> {
    throw new Error("Use deletePatientPracticeLink instead");
  }

  static async loadPatientPracticeLinkForPractice(practicePageId: string, practicePatientId: string): Promise<PatientPracticeLink | null> {
    // Not a valid instance. Only used to execute db query.
    const inst_ = new PatientPracticeLinks(null, null);
    const fullyQualifiedId = await inst_.getProvisioningContext().dbRef_getVal(inst_.listPath() + Rel.oneToOnePathFor(PatientPracticeLink.PRACTICE_PATIENT_LINK_REL_NAME, `${practicePageId}/${practicePatientId}`));
    if (typeof fullyQualifiedId === "string") {
      const parts = fullyQualifiedId.split("/");
      if (parts?.length !== 3) {
        return null;
      }
      const memberId = parts[0];
      const patientAccountProfileId = parts[1];
      const id = parts[2];
      return new PatientPracticeLinks(memberId, patientAccountProfileId).loadListItem(id);
    }
    return null;
  }

  async addPatientPracticeLink(patientPracticeLink: PatientPracticeLink): Promise<void> {
    if (patientPracticeLink.patientAccountId !== this.patientAccountProfileId) {
      throw new Error("Invalid instance of PatientPracticeLinks");
    }
    return super.addListItem(patientPracticeLink, patientPracticeLink.practicePatientLinksRel());
  }

  async deletePatientPracticeLink(patientPracticeLink: PatientPracticeLink): Promise<void> {
    if (patientPracticeLink.patientAccountId !== this.patientAccountProfileId) {
      throw new Error("Invalid instance of PatientPracticeLinks");
    }
    return super.deleteListItem(patientPracticeLink, patientPracticeLink.practicePatientLinksRel());
  }

  protected basePath(): string {
    // This could be called with an invalid constructed list in loadPatientPracticeLinkForPractice, so adapt based on null vars.
    return ["patientPracticeLinks", this.memberId, this.patientAccountProfileId].filter(Boolean).join("/");
  }

  protected deserializeItem(value: any): PatientPracticeLink {
    return JSON_OBJECT.deserializeObject(value, PatientPracticeLink);
  }

  protected serializeItem(item: PatientPracticeLink): any {
    return JSON_OBJECT.serializeObject(item);
  }

  protected sortOrder(item1: PatientPracticeLink, item2: PatientPracticeLink): number {
    return item2.created - item1.created;
  }
}

@JsonObject()
export class Review extends BaseListItem {

  @JsonProperty()
  readonly patientName: string;

  @JsonProperty()
  readonly practicePageId: string;

  @JsonProperty()
  readonly providerPageId: string;

  @JsonProperty()
  readonly patientId: string;

  @JsonProperty()
  rating: number;

  @JsonProperty()
  text: string;

  static createReview(patientName: string, practicePageId: string, providerPageId: string, patientId: string): Review {
    return new Review(md5(practicePageId, providerPageId, patientId), patientName, practicePageId, providerPageId, patientId);
  }

  constructor(id: string, patientName: string, practicePageId: string, providerPageId: string, patientId: string) {
    super(id);
    this.patientName = patientName;
    this.practicePageId = practicePageId;
    this.providerPageId = providerPageId;
    this.patientId = patientId;
  }

  static readonly PRACTICE_PAGE_ID_REL_NAME = "practicePageIds";

  practicePageIdsRel(): Rel {
    return new Rel(Review.PRACTICE_PAGE_ID_REL_NAME, this.practicePageId, this.id, true, true);
  }

  static readonly PROVIDER_PAGE_ID_REL_NAME = "providerPageIds";

  providerPageIdsRel(): Rel {
    return new Rel(Review.PROVIDER_PAGE_ID_REL_NAME, this.providerPageId, this.id, true, true);
  }

  static readonly PATIENT_ID_REL_NAME = "patientIds";

  patientIdsRel(): Rel {
    return new Rel(Review.PATIENT_ID_REL_NAME, this.patientId, this.id, true, true);
  }
}

export class Reviews extends BaseListItemsLoader<Review> {

  private static instance: Reviews;

  static getInstance(): Reviews {
    if (!this.instance) {
      this.instance = new Reviews();
    }
    return this.instance;
  }

  constructor() {
    super({
      shared: true,
      overrideProvisioningContext: BaseApp.CONTEXT.getAppConfig()?.loginConfig?.provisioning?.membersUseSharedProvisioningContext ? ProvisioningContext.MAIN : undefined,
    });
  }

  async addReview(review: Review): Promise<void> {
    await this.addListItem(review, review.practicePageIdsRel(), review.providerPageIdsRel(), review.patientIdsRel());
  }

  async deleteReview(review: Review): Promise<void> {
    await this.deleteListItem(review, review.practicePageIdsRel(), review.providerPageIdsRel(), review.patientIdsRel());
  }

  protected basePath(): string {
    return "reviews";
  }

  protected deserializeItem(value: any): Review {
    return JSON_OBJECT.deserializeObject(value, Review);
  }

  protected serializeItem(item: Review): any {
    return JSON_OBJECT.serializeObject(item);
  }

  protected sortOrder(item1: Review, item2: Review): number {
    return item2.created - item1.created;
  }
}


////////// Practice Scoped Objects /////////////////

export class PracticeData {

  private static type: PracticeProfileType;
  private static patientInstance: PracticeDataPatient;
  private static providerInstance: PracticeDataProvider;

  // This must be called whenever current Practice selection changes.
  static async createAndInitPatient(practiceId: string): Promise<void> {
    this.type = PracticeProfileType.PATIENT;
    this.patientInstance = new PracticeDataPatient(
      practiceId,
      new Appointments(practiceId),
      new Calls(practiceId),
      new GroupProfiles(practiceId),
      new Messages(practiceId),
      new Notifications(practiceId),
      new PatientProfiles(practiceId),
      new ProviderProfiles(practiceId),
      new StaffProfiles(practiceId),
      new Statements(practiceId),
    );
    await this.patientInstance.init();
  }

  // This must be called whenever current Practice selection changes.
  static async createAndInitProvider(practiceId: string): Promise<void> {
    this.type = PracticeProfileType.PROVIDER;
    this.providerInstance = new PracticeDataProvider(
      practiceId,
      new Appointments(practiceId),
      new Calls(practiceId),
      new GroupProfiles(practiceId),
      new Messages(practiceId),
      new Notifications(practiceId),
      new PatientProfiles(practiceId),
      new ProviderProfiles(practiceId),
      new StaffProfiles(practiceId),
      new Statements(practiceId),

      new PracticeDataProviderPresets(
        new Taxes(practiceId),
        new PaymentModes(practiceId),
        new ExpenseTypes(practiceId),
        new DentalProcedures(practiceId),
        new InventorySkus(practiceId),
      ),

      new PracticeDataProviderTemplates(
        new SmsTemplates(practiceId),
        new EmailTemplates(practiceId),
      ),

      new PracticeDataProviderLogs(
        new ActivityLogs(practiceId),
        new SmsLogs(practiceId),
        new EmailLogs(practiceId),
        new WhatsAppLogs(practiceId),
        new VoiceLogs(practiceId),
      ),

      new AppointmentRequests(practiceId),
      new Consults(practiceId),
      new ContactRequests(practiceId),
      new LabTests(practiceId),
      new Documents(practiceId),
      new Transactions(practiceId),
      new LabOrders(practiceId),
      new DentalPrescriptions(practiceId),
      new DentalTxPlans(practiceId),
      new DentalCharts(practiceId),
      new DentalNotes(practiceId),
      new Expenses(practiceId),
      new Vendors(practiceId),
      new Suppliers(practiceId),
      new InventoryItems(practiceId),
    );

    await this.providerInstance.init();
  }

  static shared(): PracticeDataShared {
    switch (this.type) {
      case PracticeProfileType.PATIENT:
        return this.patientInstance;
      case PracticeProfileType.PROVIDER:
        return this.providerInstance;
      case PracticeProfileType.GROUP:
        throw new Error("Illegal operation.");
    }
    return null;
  }

  static patient(): PracticeDataPatient {
    return this.patientInstance;
  }

  static provider(): PracticeDataProvider {
    return this.providerInstance;
  }

  private constructor() {
  }
}

export abstract class PracticeDataShared {

  constructor(
    readonly profileType: PracticeProfileType,
    readonly practiceId: string,
    readonly appointments: Appointments,
    readonly calls: Calls,
    readonly groupProfiles: GroupProfiles,
    readonly messages: Messages,
    readonly notifications: Notifications,
    readonly patientProfiles: PatientProfiles,
    readonly providerProfiles: ProviderProfiles,
    readonly staffProfiles: StaffProfiles,
    readonly statements: Statements,
  ) {
  }

  async init(): Promise<void> {
    await this.groupProfiles.init(this.profileType);
  }
}

// These are data types that are scoped to practice + (provider, staff or patient) profile.
export abstract class PracticeProfileDataShared {

  constructor(
    readonly profileId: string,
  ) {
  }

  async init(): Promise<void> {
  }
}

export class PracticeDataPatient extends PracticeDataShared {

  constructor(
    practiceId: string,
    appointments: Appointments,
    calls: Calls,
    groupProfiles: GroupProfiles,
    messages: Messages,
    notifications: Notifications,
    patientProfiles: PatientProfiles,
    providerProfiles: ProviderProfiles,
    staffProfiles: StaffProfiles,
    statements: Statements,
  ) {
    super(PracticeProfileType.PATIENT, practiceId, appointments, calls, groupProfiles, messages, notifications, patientProfiles, providerProfiles, staffProfiles, statements);
  }

  private myProfile_: PatientProfile;

  myProfile(): PatientProfile {
    return this.myProfile_;
  }

  private profileData_: PracticeProfileDataPatient;

  profileData(): PracticeProfileDataPatient {
    return this.profileData_;
  }

  async init(): Promise<void> {
    await super.init();
    const accountProfile = BaseApp.CONTEXT.getTrait<AccountProfileContextTrait>().getAccountProfile();
    if (accountProfile.type !== AccountTypeName.PATIENT) {
      throw new Error("Invalid account profile type: " + accountProfile.type);
    }
    const links = PatientPracticeLinks.getDefaultInstance().getListItems();
    const profiles = PracticeData.patient().patientProfiles;
    const patientId = links.find(link => link.practicePageId === this.practiceId)?.practicePatientId;
    if (patientId) {
      this.myProfile_ = await profiles.getOrLoadItem(patientId);
      this.notifications.init(patientId);
      this.profileData_ = new PracticeProfileDataPatient(patientId);
    }
  }
}

export class PracticeProfileDataPatient extends PracticeProfileDataShared {

  constructor(profileId: string) {
    super(profileId);
  }
}

export class PracticeDataProviderPresets {
  constructor(
    readonly taxes: Taxes,
    readonly paymentModes: PaymentModes,
    readonly expenseTypes: ExpenseTypes,
    readonly dentalProcedures: DentalProcedures,
    readonly inventorySkus: InventorySkus,
  ) {
  }

  async init(): Promise<void> {
    await this.taxes.loadListItems();
    await this.paymentModes.loadListItems();
    await this.expenseTypes.loadListItems();
    await this.dentalProcedures.loadListItems();
  }
}

export class PracticeDataProviderTemplates {
  constructor(
    readonly smsTemplates: SmsTemplates,
    readonly emailTemplates: EmailTemplates,
  ) {
  }
}

export class PracticeDataProviderLogs {
  constructor(
    readonly activityLogs: ActivityLogs,
    readonly smsLogs: SmsLogs,
    readonly emailLogs: EmailLogs,
    readonly whatsAppLogs: WhatsAppLogs,
    readonly voiceLogs: VoiceLogs,
  ) {
  }
}

export class PracticeProfileDataProvider extends PracticeProfileDataShared {

  constructor(
    profileId: string,
    readonly scheduledTasks: ScheduledTasks,
  ) {
    super(profileId);
  }
}

export class PracticeDataProvider extends PracticeDataShared {

  constructor(
    practiceId: string,
    appointments: Appointments,
    calls: Calls,
    groupProfiles: GroupProfiles,
    messages: Messages,
    notifications: Notifications,
    patientProfiles: PatientProfiles,
    providerProfiles: ProviderProfiles,
    staffProfiles: StaffProfiles,
    statements: Statements,
    readonly presets: PracticeDataProviderPresets,
    readonly templates: PracticeDataProviderTemplates,
    readonly logs: PracticeDataProviderLogs,
    readonly appointmentRequests: AppointmentRequests,
    readonly consults: Consults,
    readonly contactRequests: ContactRequests,
    readonly labTests: LabTests,
    readonly documents: Documents,
    readonly transactions: Transactions,
    readonly labOrders: LabOrders,
    readonly dentalPrescriptions: DentalPrescriptions,
    readonly dentalTxPlans: DentalTxPlans,
    readonly dentalCharts: DentalCharts,
    readonly dentalNotes: DentalNotes,
    readonly expenses: Expenses,
    readonly vendors: Vendors,
    readonly suppliers: Suppliers,
    readonly inventoryItems: InventoryItems,
  ) {
    super(PracticeProfileType.PROVIDER, practiceId, appointments, calls, groupProfiles, messages, notifications, patientProfiles, providerProfiles, staffProfiles, statements);
  }

  private myProfile_: ProviderProfile | StaffProfile;

  myProfile(): ProviderProfile | StaffProfile {
    return this.myProfile_;
  }

  private profileData_: PracticeProfileDataProvider;

  profileData(): PracticeProfileDataProvider {
    return this.profileData_;
  }

  async init(): Promise<void> {
    await super.init();
    await this.presets.init();
    const accountProfile = BaseApp.CONTEXT.getTrait<AccountProfileContextTrait>().getAccountProfile();
    if (accountProfile.type !== AccountTypeName.PROVIDER && accountProfile.type !== AccountTypeName.STAFF) {
      throw new Error("Invalid account profile type: " + accountProfile.type);
    }
    const providerProfiles = PracticeData.provider().providerProfiles;
    await providerProfiles.loadListItems();
    const staffProfiles = PracticeData.provider().staffProfiles;
    await staffProfiles.loadListItems();
    let profile: ProviderProfile | StaffProfile;
    switch (accountProfile.type) {
      case AccountTypeName.PROVIDER: {
        const providerPageId = BaseApp.CONTEXT.getTrait<AccountProfileContextTrait>().getProviderPageId();
        profile = providerProfiles.getListItem(providerPageId); // Provider profile and page id are the same.
        const providerPage = await Pages.getInstance().getOrLoadItem(providerPageId);
        const memberId = getMemberAuth().getMemberId();
        if (!profile?.matchesPage(providerPage)) {
          profile = ProviderProfile.createNew(profile, providerPage, memberId);
          await providerProfiles.addListItem(profile);
        }
        if (!profile.memberId) {
          profile.memberId = memberId;
          await providerProfiles.addListItem(profile);
        }
        this.myProfile_ = profile;
      }
        break;
      case AccountTypeName.STAFF: {
        const staffId = (accountProfile as StaffAccountProfile).staffId;
        profile = staffProfiles.getListItem(staffId);
        this.myProfile_ = profile;
      }
        break;
    }
    this.notifications.init(profile.id);
    this.profileData_ = new PracticeProfileDataProvider(
      profile.id,
      new ScheduledTasks(this.practiceId, profile.type(), profile.id),
    );
  }
}

export abstract class BasePracticeTypedObject extends TypedObject {

  constructor(readonly practiceId: string) {
    super(null, null);
  }

  protected objectConfig(): ObjectConfig | null {
    return {
      shared: true,
    };
  }

  protected basePath(): string | null {
    return `practiceData/${this.practiceId}/${this.id}`;
  }
}

@JsonObject()
export abstract class BasePracticeListItem extends BaseListItem {

  constructor(id: string) {
    super(id);
  }
}

export abstract class BasePracticeListItemsLoader<T extends BasePracticeListItem> extends BaseListItemsLoader<T> {

  constructor(readonly practiceId: string) {
    super({shared: true});
  }

  protected basePath(): string {
    return `practiceData/${this.practiceId}/${this.practiceDataBasePath()}`;
  }

  protected abstract practiceDataBasePath(): string;
}

export abstract class BasePracticeProfileListItem extends BaseListItem {

  constructor(id: string) {
    super(id);
  }
}

export abstract class BasePracticeProfileListItemsLoader<T extends BasePracticeProfileListItem> extends BaseListItemsLoader<T> {

  constructor(readonly practiceId: string, readonly profileType: PracticeProfileType, readonly profileId: string) {
    super({shared: true});
  }

  protected basePath(): string {
    return `practiceData/${this.practiceId}/profileData/${this.profileType}s/${this.profileId}/${this.practiceProfileDataBasePath()}`;
  }

  protected abstract practiceProfileDataBasePath(): string;
}


abstract class BasePracticeProfileTypedObject extends BasePracticeTypedObject {

  constructor(practiceId: string, readonly profileId: string) {
    super(practiceId);
  }

  protected basePath(): string | null {
    return `${super.basePath()}/${this.profileId}`;
  }
}

abstract class BaseRemotePrefs extends BasePracticeProfileTypedObject {

  readonly id: string = "remotePrefs";

  protected constructor(practiceId: string, profileId: string) {
    super(practiceId, profileId);
  }
}

@JsonObject()
export class ProviderRemotePrefsData {

  @JsonProperty()
  lastNotificationsReadAt: number;
}

// Final, do not change, or subclass.
@JsonObject()
export class ProviderRemotePrefs extends BaseRemotePrefs {

  static getInstance(): ProviderRemotePrefs {
    return globalThis.ProviderRemotePrefs;
  }

  static async loadInstance() {
    globalThis.ProviderRemotePrefs = await this.load();
  }

  private static async load(): Promise<ProviderRemotePrefs> {
    const request = new ProviderRemotePrefs(PracticeData.provider().practiceId, PracticeData.provider().myProfile().id);
    await request.load();
    return request;
  }

  constructor(practiceId: string, profileId: string) {
    super(practiceId, profileId);
  }

  @JsonProperty()
  readonly data: ProviderRemotePrefsData = new ProviderRemotePrefsData();

  protected getType(): Type<ProviderRemotePrefs> {
    return ProviderRemotePrefs;
  }
}

@JsonObject()
export class Notification extends BasePracticeListItem {

  @JsonProperty()
  readonly imageUrl: string;

  @JsonProperty()
  readonly title: string;

  @JsonProperty()
  readonly text: string;

  @JsonProperty()
  read?: boolean;

  static createNew(profilePhoto: string, title: string, text: string): Notification {
    return new Notification(md5_uuid(), profilePhoto, title, text);
  }

  constructor(id: string, imageUrl: string, title: string, text: string) {
    super(id);
    this.imageUrl = imageUrl;
    this.title = title;
    this.text = text;
  }

  isUnread(): boolean {
    return !this.read;
  }
}

export class Notifications extends BasePracticeListItemsLoader<Notification> {

  private patientId: string;

  constructor(practiceId: string) {
    super(practiceId);
  }

  init(patientId: string): void {
    this.patientId = patientId;
  }

  checkInit(): void {
    if (!this.patientId) {
      throw new Error("Please call init() first to initialize.");
    }
  }

  notifyAll(): void {
    this.observers.forEach(observer => observer.onItemChanged(null, ListItemChange.CHANGED));
  }

  protected practiceDataBasePath(): string {
    this.checkInit();
    return `notifications/${this.patientId}`;
  }

  protected deserializeItem(value: any): Notification {
    return JSON_OBJECT.deserializeObject(value, Notification);
  }

  protected serializeItem(item: Notification): any {
    return JSON_OBJECT.serializeObject(item);
  }

  protected sortOrder(item1: Notification, item2: Notification): number {
    return item2.created - item1.created;
  }
}

export interface NotificationsContextTrait extends ContextTrait {

  getNotificationsSource(): NotificationsSource;
}

export interface NotificationsSource {

  registerObserver(observer: OnListItemsListener<Notification>): void;

  unregisterObserver(observer: OnListItemsListener<Notification>): void;

  getNotificationsCount(): number;

  showPopup(anchorEl: HTMLElement): void;
}

export enum PracticeProfileType {
  PATIENT = "patient",
  PROVIDER = "provider",
  STAFF = "staff",
  GROUP = "group", // Used as a container profile for group of provider and staff profiles.
  BOT = "bot",
}

@JsonObject()
export class PracticeProfileBasicInfo {

  @FormGen({
    name: "Profile Photo",
    type: "profile_photo",
  })
  @JsonProperty()
  profilePhoto: string;

  @FormGen({
    name: "Prefix",
    type: "enum",
    enumValues: Object.values(NamePrefix).map(value => $KTS(value, value)),
  })
  @JsonProperty()
  prefix: NamePrefix;

  @FormGen({
    name: "First Name",
    type: "string",
    noneditable: true,
    required: true,
  })
  @JsonProperty()
  firstname: string;

  @FormGen({
    name: "Last Name",
    type: "string",
    noneditable: true,
    required: true,
  })
  @JsonProperty()
  lastname: string;

  @FormGen({
    name: "Email",
    type: "string",
    inputMode: "email",
    noneditable: true,
    required: true,
  })
  @JsonProperty()
  email: string;

  @FormGen({
    name: "Gender",
    type: "enum",
    required: true,
    enumValues: GENDER_KTS.values,
    enumVariant: "button",
  })
  @JsonProperty()
  gender: string;

  @FormGen({
    name: "Phone Number",
    type: "phone_number",
    required: true,
  })
  @JsonProperty()
  phoneNumber: string;

  @FormGen({
    name: "Date of Birth",
    type: "datenum",
    required: true,
  })
  @JsonProperty()
  dateOfBirth: number;

  displayName(): string {
    return DisplayName(this.firstname, this.lastname, this.prefix);
  }

  private _age: string;

  ageNum(): number {
    return this.dateOfBirth > 0 ? (dayjs().diff(dayjs(new Date(this.dateOfBirth)), "year")) : -1;
  }

  age(): string {
    if (!this._age) {
      const str = this.dateOfBirth > 0 ? (dayjs().diff(dayjs(new Date(this.dateOfBirth)), "year")).toString() : "-";
      this._age = str ? `${str}y` : '-';
    }
    return this._age;
  }

  dateOfBirthText(): string {
    return this.dateOfBirth ? DateUtil.formatDateTime(this.dateOfBirth, FORMAT_SHOW_YEAR) : "-";
  }

  genderText(): string {
    return GENDER_KTS.findText(this.gender) || "-";
  }
}

@JsonObject()
export class PracticeProfileAddress {

  @FormGen({
    name: "House No., or Flat, with Street name",
    type: "string",
  })
  @JsonProperty()
  houseNumber: string;

  @FormGen({
    name: "Locality, Colony or Area",
    type: "string",
  })
  @JsonProperty()
  locality: string;

  @FormGen({
    name: "City",
    type: "string",
  })
  @JsonProperty()
  city: string;

  @FormGen({
    name: "State",
    type: "string",
  })
  @JsonProperty()
  state: string;

  @FormGen({
    name: "Country",
    type: "country_select",
  })
  @JsonProperty()
  country: string;

  @FormGen({
    name: "Pin or Zip Code",
    type: "string",
  })
  @JsonProperty()
  zipCode: string;
}

export enum PracticeProfileStatus {
  ACTIVE = "active",
  INACTIVE = "inactive",
}

@JsonObject()
export abstract class BasePracticeProfile extends BasePracticeListItem {

  @FormGen({
    name: "Basic Info",
    type: "object",
  })
  @JsonProperty()
  readonly basicInfo: PracticeProfileBasicInfo = new PracticeProfileBasicInfo();

  @FormGen({
    name: "Address",
    type: "object",
    separator: "above",
  })
  @JsonProperty()
  readonly address: PracticeProfileAddress = new PracticeProfileAddress();

  @JsonProperty()
  readonly status: PracticeProfileStatus = PracticeProfileStatus.ACTIVE;

  async onAfterItemDeserialized(): Promise<void> {
    await super.onAfterItemDeserialized();
    if (!this.basicInfo.profilePhoto) {
      this.basicInfo.profilePhoto = generateSvgAvatarDataUri(this.id);
    }
  }

  displayName(): string {
    return DisplayName(this.basicInfo.firstname, this.basicInfo.lastname);
  }

  displayNameWithEmail(): string {
    return `${DisplayName(this.basicInfo.firstname, this.basicInfo.lastname)} <${this.basicInfo.email}>`;
  }

  displayNameIncludes(search: string): boolean {
    search = search.toLowerCase();
    return this.basicInfo.firstname?.toLowerCase().includes(search)
      || this.basicInfo.lastname?.toLowerCase().includes(search);
  }

  abstract type(): PracticeProfileType;

  matchesTypeId(typeId: string): boolean {
    const myTypeId = this.toTypeId();
    if (myTypeId === typeId) {
      return true;
    }
    for (const groupProfile of PracticeData.shared().groupProfiles.getDefaultList()) {
      if (
        // typeId matches this group
        groupProfile.toTypeId() === typeId &&
        // I'm a member of this group
        groupProfile.hasMember(this)) {
        return true;
      }
    }
    return false;
  }

  static toTypeId(type: PracticeProfileType, id: string): string {
    return `${type}/${id}`;
  }

  toTypeId(): string {
    return BasePracticeProfile.toTypeId(this.type(), this.id);
  }
}

abstract class BasePracticeProfiles<T extends BasePracticeProfile> extends BasePracticeListItemsLoader<T> {

  protected constructor(readonly type: PracticeProfileType, practiceId: string) {
    super(practiceId);
  }

  protected practiceDataBasePath(): string {
    return `profiles/${this.type}s`;
  }

  protected abstract getType(): Type<T>;

  protected deserializeItem(value: any): T {
    return JSON_OBJECT.deserializeObject(value, this.getType());
  }

  protected serializeItem(item: T): any {
    return JSON_OBJECT.serializeObject(item);
  }

  protected sortOrder(item1: T, item2: T): number {
    return item1.basicInfo.firstname?.localeCompare(item2.basicInfo.firstname)
      || item1.basicInfo.lastname?.localeCompare(item2.basicInfo.lastname);
  }
}

export enum ProfileKeyType {
  ID,
  TYPE_ID,
}

function ProfileKey(profile: BasePracticeProfile, keyType: ProfileKeyType): string {
  switch (keyType) {
    default:
    case ProfileKeyType.ID:
      return profile.id;
    case ProfileKeyType.TYPE_ID:
      return profile.toTypeId();
  }
}

@JsonObject()
export class BotProfile extends BasePracticeProfile {

  static createNew(name: string, profilePhoto: string): PatientProfile {
    const profile = new BotProfile(name);
    profile.basicInfo.firstname = name;
    profile.basicInfo.profilePhoto = profilePhoto;
    return profile;
  }

  type(): PracticeProfileType {
    return PracticeProfileType.BOT;
  }
}

export const ASKWYZE_BOT_PROFILE = BotProfile.createNew("Ask Wyze", "/images/askwyze_icon.png");

// This is stored separately for each practice, and is NOT the same as PatientAccountProfile, which belongs to the patient.
@JsonObject()
export class PatientProfile extends BasePracticeProfile {

  static createNew(): PatientProfile {
    const profile = new PatientProfile(md5_uuid());
    return profile;
  }

  type(): PracticeProfileType {
    return PracticeProfileType.PATIENT;
  }
}

export class PatientProfiles extends BasePracticeProfiles<PatientProfile> {

  constructor(practiceId: string) {
    super(PracticeProfileType.PATIENT, practiceId);
  }

  protected getRules(): Rule[] {
    return [
      ...super.getRules(),
      {
        indexOn: ["basicInfo/dateOfBirth"],
      },
    ];
  }

  // async loadListItems(options?: LoadListOptions): Promise<void> {
  //   return super.loadListItems({
  //     orderByChild: {
  //       key: "basicInfo/dateOfBirth",
  //       startAt: 1,
  //     },
  //   });
  // }

  protected getType(): Type<PatientProfile> {
    return PatientProfile;
  }
}

export function getAccountInfoFromEmail(email: string): { practiceId: string, entityId: string } {
  const matches = email.match(/^([a-zA-Z0-9]+)@([a-zA-Z0-9+]+)\./);
  if (matches?.length > 0) {
    return {
      practiceId: matches[2],
      entityId: matches[1],
    }
  }
}

@JsonObject()
export class StaffProfile extends BasePracticeProfile {

  @JsonProperty()
  encLoginInfo: string;

  static createNew(firstname?: string): StaffProfile {
    const profile = new StaffProfile(md5_uuid());
    profile.basicInfo.firstname = firstname;
    return profile;
  }

  type(): PracticeProfileType {
    return PracticeProfileType.STAFF;
  }
}

export class StaffProfiles extends BasePracticeProfiles<StaffProfile> {

  constructor(practiceId: string) {
    super(PracticeProfileType.STAFF, practiceId);
  }

  protected getType(): Type<StaffProfile> {
    return StaffProfile;
  }
}

export const patientProfileAutocompleteCreateAction = new Action("Add Patient", (event, input: string) => {
  EditPatientProfileHelper.addPatientProfile({firstname: input});
}, AddOutlined);

export function patientProfileAutocompleteLoaderCreator(keyType: ProfileKeyType) {
  return () => {
    const patientProfiles = PracticeData.provider().patientProfiles;
    return (async function () {
      const patients = await patientProfiles.getOrLoadListItems();
      return patients.map(provider => $KTS(ProfileKey(provider, keyType), provider.displayName()));
    })();
  };
}

export function PatientProfileFilter<T extends { patientId: string }>(patientId: string): Filter<T> {
  return item => !patientId || (item.patientId === patientId);
}

export function PatientRecordFilter<T extends { src: RecordSource }>(patientId: string): Filter<T> {
  return item => !patientId || (item.src.patientId === patientId);
}

@JsonObject()
export class ProviderProfile extends BasePracticeProfile {

  @JsonProperty()
  memberId: string;

  static createNew(oldProfile: ProviderProfile, providerPage: Page, memberId?: string): ProviderProfile {
    const providerProfile = Object.assign(new ProviderProfile(providerPage.id), oldProfile || {});
    providerProfile.copyPage(providerPage);
    providerProfile.memberId = memberId;
    return providerProfile;
  }

  constructor(id: string) {
    super(id);
  }

  type(): PracticeProfileType {
    return PracticeProfileType.PROVIDER;
  }

  matchesPage(providerPage: Page): boolean {
    const pageProfile = providerPage.providerPageProfile;
    return this.basicInfo.firstname === pageProfile.basicInfo.firstname
      && this.basicInfo.lastname === pageProfile.basicInfo.lastname
      && this.basicInfo.email === pageProfile.basicInfo.email
      && this.basicInfo.gender === pageProfile.basicInfo.gender
      && this.basicInfo.profilePhoto === pageProfile.basicInfo.profilePhoto
      && this.basicInfo.phoneNumber === pageProfile.basicInfo.phoneNumber;
  }

  private copyPage(providerPage: Page): void {
    const pageProfile = providerPage.providerPageProfile;
    this.basicInfo.firstname = pageProfile.basicInfo.firstname;
    this.basicInfo.lastname = pageProfile.basicInfo.lastname;
    this.basicInfo.email = pageProfile.basicInfo.email;
    this.basicInfo.gender = pageProfile.basicInfo.gender;
    this.basicInfo.profilePhoto = pageProfile.basicInfo.profilePhoto;
    this.basicInfo.phoneNumber = pageProfile.basicInfo.phoneNumber;
  }

  async onAfterItemDeserialized(): Promise<void> {
    await super.onAfterItemDeserialized();
    if (this.memberId) {
      this.member = await Members.getInstance().getOrLoadMember(this.memberId);
    }
  }
}

export class ProviderProfiles extends BasePracticeProfiles<ProviderProfile> {

  constructor(practiceId: string) {
    super(PracticeProfileType.PROVIDER, practiceId);
  }

  protected getType(): Type<ProviderProfile> {
    return ProviderProfile;
  }
}

export function providerProfileAutocompleteLoaderCreator(keyType: ProfileKeyType, excludeMe?: boolean): () => Promise<KeyTextString[]> {
  return () => {
    const providerProfiles = PracticeData.provider().providerProfiles;
    const myProfile = PracticeData.provider().myProfile();
    return (async function () {
      const providers = await providerProfiles.getOrLoadListItems();
      let all = [...providers];
      if (excludeMe) {
        all = all.filter(profile => profile.id !== myProfile.id);
      }
      return all.map(provider => $KTS(ProfileKey(provider, keyType), provider.displayName()));
    })();
  };
}

export function providerAndStaffProfileAutocompleteLoaderCreator(keyType: ProfileKeyType, excludeMe?: boolean): () => Promise<KeyTextString[]> {
  return () => {
    const providerProfiles = PracticeData.provider().providerProfiles;
    const staffProfiles = PracticeData.provider().staffProfiles;
    const myProfile = PracticeData.provider().myProfile();
    return (async function () {
      const providers = await providerProfiles.getOrLoadListItems();
      const staff = await staffProfiles.getOrLoadListItems();
      let all = [...providers, ...staff];
      if (excludeMe) {
        all = all.filter(profile => profile.id !== myProfile.id);
      }
      return all.map(provider => $KTS(ProfileKey(provider, keyType), provider.displayName()));
    })();
  };
}


export function myProviderAndGroupProfileAutocompleteLoaderCreator(keyType: ProfileKeyType): () => KeyTextString[] {
  return () => {
    const myProfile = PracticeData.provider().myProfile();
    const kts: KeyTextString[] = [];
    const myGroups = PracticeData.provider().groupProfiles.getDefaultList().filter(group => group.hasMember(myProfile));
    myGroups.forEach(group => kts.push($KTS(ProfileKey(group, keyType), group.displayName())));
    kts.push($KTS(ProfileKey(myProfile, keyType), myProfile.displayName()));
    return kts;
  };
}

export function ProviderProfileFilter<T extends { providerId: string }>(providerId: string): Filter<T> {
  return item => !providerId || (item.providerId === providerId);
}

export enum GroupProfileName {
  STAFF_PROVIDERS_ALL = "__staff_providers_all__", // All staff AND providers
  // PROVIDERS_ALL = "__providers_all__", // All providers
  // STAFF_ALL = "__staff_all__", // All staff (excludes providers)
  // STAFF_BILLING = "__staff_billing__", // Only billing staff
}

type GroupProfileNameMetadata = {
  text: string,
}

const GROUP_PROFILE_NAME_METADATAS = new KeyMetadatas<GroupProfileNameMetadata>([
  $KTM(GroupProfileName.STAFF_PROVIDERS_ALL, {text: ""}),
  // $KTM(GroupProfileName.PROVIDERS_ALL, {text: "(Providers)"}),
  // $KTM(GroupProfileName.STAFF_ALL, {text: "(Office)"}),
  // $KTM(GroupProfileName.STAFF_BILLING, {text: "(Billing)"}),
]);

@JsonObject()
export class GroupProfile extends BasePracticeProfile {

  private readonly members: BasePracticeProfile[] = [];

  constructor(readonly name: GroupProfileName) {
    super(name);
  }

  hasMember(member: BasePracticeProfile): boolean {
    return Boolean(this.members.find(m => m.matchesTypeId(member.toTypeId())));
  }

  setMembers(members: BasePracticeProfile[]): void {
    this.members.slice(0, this.members.length);
    this.members.push(...(members || []));
  }

  type(): PracticeProfileType {
    return PracticeProfileType.GROUP;
  }
}

export class GroupProfiles extends BasePracticeProfiles<GroupProfile> {

  private readonly defaultList: GroupProfile[] = [];

  constructor(practiceId: string) {
    super(PracticeProfileType.GROUP, practiceId);
  }

  getDefaultList(): GroupProfile[] {
    return this.defaultList;
  }

  getByName(name: GroupProfileName): GroupProfile {
    return this.defaultList.find(profile => profile.name === name);
  }

  async init(type: PracticeProfileType): Promise<void> {
    for (const value of Object.values(GroupProfileName)) {
      const groupProfile = new GroupProfile(value);
      const practicePageId = this.practiceId;
      if (practicePageId) {
        const page = await Pages.getInstance().getOrLoadItem(practicePageId);
        groupProfile.basicInfo.firstname = page.practicePageProfile.basicInfo.name;
        groupProfile.basicInfo.lastname = GROUP_PROFILE_NAME_METADATAS.findMetadata(value)?.text;
      } else {
        groupProfile.basicInfo.firstname = "[Unknown]";
      }
      this.defaultList.push(groupProfile);
    }
    switch (type) {
      case PracticeProfileType.PATIENT:
        // Keep group members empty.
        break;
      case PracticeProfileType.PROVIDER:
      case PracticeProfileType.STAFF: {
        const providers = await PracticeData.shared().providerProfiles.getOrLoadListItems();
        const staff = await PracticeData.shared().staffProfiles.getOrLoadListItems();
        // TODO The following can be uncommented if we add multiple groups (instead of only one global staff+providers).
        // this.getByName(GroupProfileName.PROVIDERS_ALL).setMembers(providers);
        this.getByName(GroupProfileName.STAFF_PROVIDERS_ALL).setMembers([...providers, ...staff]);
      }
        break;
    }
  }

  async getOrLoadItem(id: string): Promise<GroupProfile | null> {
    for (const group of this.defaultList) {
      if (group.id === id) {
        return group;
      }
    }
    return null;
  }

  // Disallow db operations for now. Only static membership via init().
  async loadListItems(): Promise<void> {
    throw new Error("Operation not allowed");
  }

  async addListItem(item: GroupProfile, ...rels): Promise<void> {
    throw new Error("Operation not allowed");
  }

  async deleteListItem(item: GroupProfile, ...rels): Promise<void> {
    throw new Error("Operation not allowed");
  }

  protected getType(): Type<GroupProfile> {
    return GroupProfile;
  }
}

export async function loadProfileForTypeId(typeId: string): Promise<BasePracticeProfile | null | undefined> {
  const index = typeId.indexOf("/");
  if (index < 0) {
    return null;
  }
  let profiles: BasePracticeProfiles<BasePracticeProfile>;
  switch (typeId.substring(0, index)) {
    case PracticeProfileType.BOT:
      return typeId.substring(index + 1) === "askwyze" ? ASKWYZE_BOT_PROFILE : null;
    case PracticeProfileType.PATIENT:
      profiles = PracticeData.shared().patientProfiles;
      break;
    case PracticeProfileType.PROVIDER:
      profiles = PracticeData.shared().providerProfiles;
      break;
    case PracticeProfileType.STAFF:
      profiles = PracticeData.shared().staffProfiles;
      break;
    case PracticeProfileType.GROUP:
      profiles = PracticeData.shared().groupProfiles;
      break;
    default:
      return null;
  }
  return profiles?.getOrLoadItem(typeId.substring(index + 1));
}

export function DisplayNameForProfile(profile: BasePracticeProfile): string {
  if (!profile) {
    return "";
  }
  switch (profile.type()) {
    case PracticeProfileType.PATIENT:
      return profile.displayNameWithEmail();
    case PracticeProfileType.PROVIDER:
      return profile.displayName();
    case PracticeProfileType.GROUP:
      return profile.displayName();
  }
  return null;
}

@JsonObject()
export class RecordSource {

  patient: PatientProfile;

  @FormGen({
    name: "Patient",
    type: "autocomplete",
    noneditable: true,
    required: true,
    autocompleteCreateAction: patientProfileAutocompleteCreateAction,
    autocompleteLoader: async function (value, inputText) {
      return patientProfileAutocompleteLoaderCreator(ProfileKeyType.ID)();
    },
  })
  @JsonProperty()
  patientId: string;

  provider: ProviderProfile;

  @FormGen({
    name: "Provider",
    type: "autocomplete",
    noneditable: true,
    required: true,
    autocompleteLoader: async function (value, inputText) {
      return providerProfileAutocompleteLoaderCreator(ProfileKeyType.ID)();
    },
  })
  @JsonProperty()
  providerId: string;

  static createNewMyProvider(patientId?: string): RecordSource {
    const src = new RecordSource();
    src.patientId = patientId;
    src.providerId = PracticeData.provider().myProfile().id;
    return src;
  }

  async onAfterItemDeserialized(): Promise<void> {
    if (this.patientId) {
      this.patient = await PracticeData.provider().patientProfiles.getOrLoadItem(this.patientId);
    }
    if (this.providerId) {
      this.provider = await PracticeData.provider().providerProfiles.getOrLoadItem(this.providerId);
    }
  }
}

// Base class for patient's health information object, which has both patient and provider ids.
@JsonObject()
export abstract class BasePracticeRecordListItem extends BasePracticeListItem {

  @JsonProperty()
  readonly src: RecordSource;

  constructor(id: string, src: RecordSource) {
    super(id);
    this.src = src;
  }

  async onAfterItemDeserialized(): Promise<void> {
    await this.src.onAfterItemDeserialized();
  }
}

export abstract class BasePracticeRecordListItemsLoader<T extends BasePracticeRecordListItem> extends BasePracticeListItemsLoader<T> {
}

// This one is stored under patientData/<patient-id>.
@JsonObject()
export abstract class BasePracticePatientListItem extends BasePracticeListItem {
}

export abstract class BasePracticePatientListItemsLoader<T extends BasePracticePatientListItem> extends BasePracticeListItemsLoader<T> {

  protected constructor(practiceId: string, readonly patientId: string) {
    super(practiceId);
  }

  protected practiceDataBasePath(): string {
    return `patientData/${this.patientId}/${this.patientDataPath()}`;
  }

  protected abstract patientDataPath(): string;
}

// This should be stored under each Practice and not globally as the patient may not have created a Dentwyze profile.
// The patient profile under each practice can be linked to a patient account (via PatientPracticeLink), which allows
// look up of their messages.
@JsonObject()
export class Message extends BasePracticeListItem {

  from: BasePracticeProfile;

  @JsonProperty()
  readonly fromId: string;

  to: BasePracticeProfile;

  @JsonProperty()
  toId: string;

  @JsonProperty()
  subject: string;

  @JsonProperty()
  message: string;

  @JsonProperty()
  files: string[];

  constructor(id: string, fromId: string, toId: string) {
    super(id);
    this.fromId = fromId;
    this.toId = toId;
  }

  async onAfterItemDeserialized(): Promise<void> {
    await super.onAfterItemDeserialized();
    this.from = await loadProfileForTypeId(this.fromId);
    this.to = await loadProfileForTypeId(this.toId);
  }

  static readonly FROM_ID_REL_NAME = "fromIds";

  fromIdsRel(): Rel {
    return new Rel(Message.FROM_ID_REL_NAME, this.fromId.toString(), this.id, true, true);
  }

  static readonly TO_ID_REL_NAME = "toIds";

  toIdsRel(): Rel {
    return new Rel(Message.TO_ID_REL_NAME, this.toId.toString(), this.id, true, true);
  }
}

export class Messages extends BasePracticeListItemsLoader<Message> {

  constructor(practiceId: string) {
    super(practiceId);
  }

  async loadListItems(): Promise<void> {
    throw new Error("Operation not allowed");
  }

  async loadMessagesFrom(profile: BasePracticeProfile): Promise<Message[]> {
    return this.loadMessagesForRelName(profile, Message.FROM_ID_REL_NAME);
  }

  async loadGroupMessagesFrom(profile: BasePracticeProfile): Promise<Message[]> {
    const messages: Message[] = [];
    for (const groupProfile of PracticeData.shared().groupProfiles.getDefaultList()) {
      if (groupProfile.hasMember(profile)) {
        messages.push(...(await this.loadMessagesFrom(groupProfile)));
      }
    }
    return messages;
  }

  async loadMessagesTo(profile: BasePracticeProfile): Promise<Message[]> {
    return this.loadMessagesForRelName(profile, Message.TO_ID_REL_NAME);
  }

  async loadGroupMessagesTo(profile: BasePracticeProfile): Promise<Message[]> {
    const messages: Message[] = [];
    for (const groupProfile of PracticeData.shared().groupProfiles.getDefaultList()) {
      if (groupProfile.hasMember(profile)) {
        messages.push(...(await this.loadMessagesTo(groupProfile)));
      }
    }
    return messages;
  }

  private async loadMessagesForRelName(profile: BasePracticeProfile, relName: string): Promise<Message[]> {
    const val = await this.getProvisioningContext().dbRef_getVal(this.listPath() + Rel.oneToOnePathFor(relName, profile.toTypeId()));
    const messages: Message[] = [];
    if (val) {
      for (const key in val) {
        let value = val[key];
        const message = await this.getOrLoadItem(value);
        if (message) {
          messages.push(message);
        }
      }
    }
    return messages;
  }

  async addListItem(item: Message, ...rels): Promise<void> {
    throw new Error("Use addMessage instead");
  }

  async addMessage(message: Message): Promise<void> {
    await super.addListItem(message, message.fromIdsRel(), message.toIdsRel());
  }

  protected practiceDataBasePath(): string {
    return "messages";
  }

  protected deserializeItem(value: any): Message {
    return JSON_OBJECT.deserializeObject(value, Message);
  }

  protected serializeItem(item: Message): any {
    return JSON_OBJECT.serializeObject(item);
  }

  protected sortOrder(item1: Message, item2: Message): number {
    return item2.created - item1.created;
  }
}

abstract class BaseComposeMessage {

  fromId: string;

  toId: string;

  constructor(fromId?: string, toId?: string) {
    this.fromId = fromId;
    this.toId = toId;
  }

  abstract toMessage(): Message;
}

export class ProviderComposeMessage extends BaseComposeMessage {

  @FormGen({
    name: "From",
    type: "autocomplete",
    required: true,
    autocompleteLoader: async function (value, inputText) {
      return myProviderAndGroupProfileAutocompleteLoaderCreator(ProfileKeyType.TYPE_ID)();
    },
  })
  readonly fromId: string = "";

  @FormGen({
    name: "To",
    type: "autocomplete",
    // noneditable: true,
    required: true,
    autocompleteCreateAction: patientProfileAutocompleteCreateAction,
    autocompleteLoader: async function (value, inputText) {
      return patientProfileAutocompleteLoaderCreator(ProfileKeyType.TYPE_ID)();
    },
  })
  toId: string = "";

  @FormGen({
    name: "Subject",
    type: "string",
    required: true,
  })
  subject: string;

  @FormGen({
    name: "Message",
    type: "text",
    required: true,
  })
  message: string;

  @FormGen({
    name: "Attachments",
    type: "array_file",
    description: "The maximum file size is 5 MB per attachment. The allowed file types are JPG, JPEG, PNG, BMP and PDF. Images should be directly relevant to your care, of adequate quality and not of individuals other than you.\n\nYour provider may or may not be able to give a diagnosis or treatment recommendation based on your attachment. Your image will be placed in the medical record and subject to disclosure consistent with Dentwyze Privacy Practices. Both Dentwyze and Care Providers reserve the right to remove posted images or information from your medical record that are deemed inappropriate to your care.",
  })
  files: string[];

  constructor(fromId: string, toId?: string) {
    super(fromId, toId);
  }

  toMessage(): Message {
    const message = new Message(md5_uuid(), this.fromId, this.toId);
    message.subject = this.subject;
    message.message = this.message;
    message.files = this.files;
    return message;
  }
}

export interface MessagingTemplateVariablesFactory {

  getTemplateVariables(templateId: MessagingTemplateId): string[];
}

export abstract class BaseComposeMessaging {

  @FormGen({
    name: "To",
    type: "autocomplete",
    noneditable: true,
    required: true,
    autocompleteCreateAction: patientProfileAutocompleteCreateAction,
    autocompleteLoader: async function (value, inputText) {
      return patientProfileAutocompleteLoaderCreator(ProfileKeyType.ID)();
    },
  })
  toId: string = "";

  templateId: MessagingTemplateId | "" = "";

  message: string = "";

  constructor(readonly factory: MessagingTemplateVariablesFactory, toId?: string) {
    this.toId = toId;
  }

  toMessage(templateId: MessagingTemplateId): string {
    const metadata = MESSAGING_TEMPLATE_KTM.values.find(value => value.key === templateId)?.metadata;
    if (!metadata) {
      return "";
    }
    const templateText = metadata.templateText;
    const variables = Boolean(templateText) && this.factory.getTemplateVariables(templateId)?.filter(Boolean);
    if (variables?.length !== metadata.templateVariablesCount) {
      console.error("Template variables do not match expected count.");
      return "";
    }
    return StringUtil.replacePlaceholders(templateText, ...variables.map((str, index) => $KTS(`${index + 1}`, str)));
  }
}

abstract class BaseProviderComposeMessagingTemplateVariablesFactory implements MessagingTemplateVariablesFactory {

  private totalBalance?: number;

  constructor(readonly provider: ProviderProfile | StaffProfile, readonly patient: PatientProfile, readonly practice: PracticePageProfile) {
  }

  async init(): Promise<void> {
    const transactions = (await PracticeData.provider().transactions.getOrLoadListItems()).filter(value => (value.patientId === this.patient.id));
    const statementData = new StatementData(this.patient.id, transactions);
    statementData.calculate();
    this.totalBalance = statementData.totalBalance;
  }

  getTemplateVariables(templateId: MessagingTemplateId): string[] {
    switch (templateId) {
      case MessagingTemplateId.PATIENT_RECALL_CHECKUP:
        return [
          this.patient.basicInfo.displayName(),
          this.practice.basicInfo.name,
          this.practice.basicInfo.phoneNumber,
        ];
      case MessagingTemplateId.PATIENT_PAYMENT_BALANCE:
        return [
          this.patient.basicInfo.displayName(),
          CurrencyText(this.totalBalance),
          this.practice.basicInfo.name,
          this.practice.basicInfo.phoneNumber,
        ];
      case MessagingTemplateId.PATIENT_TREATMENT_PRE_SURGERY:
        return [
          this.patient.basicInfo.displayName(),
          this.practice.basicInfo.name,
          this.practice.basicInfo.phoneNumber,
        ];
      case MessagingTemplateId.PATIENT_TREATMENT_POST_SURGERY:
        return [
          this.patient.basicInfo.displayName(),
          this.practice.basicInfo.name,
          this.practice.basicInfo.phoneNumber,
        ];
      case MessagingTemplateId.PATIENT_BIRTHDAY_GREETING:
        return [
          this.patient.basicInfo.displayName(),
          this.practice.basicInfo.name,
          this.practice.basicInfo.phoneNumber,
        ];
    }
    return null;
  }
}

// SMS

export class ProviderComposeSmsTemplateVariablesFactory extends BaseProviderComposeMessagingTemplateVariablesFactory {
}

const PROVIDER_COMPOSE_SMS_TEMPLATE_IDS: MessagingTemplateId[] = [
  MessagingTemplateId.PATIENT_RECALL_CHECKUP,
  MessagingTemplateId.PATIENT_PAYMENT_BALANCE,
  MessagingTemplateId.PATIENT_TREATMENT_PRE_SURGERY,
  MessagingTemplateId.PATIENT_TREATMENT_POST_SURGERY,
  MessagingTemplateId.PATIENT_BIRTHDAY_GREETING,
];

export class ProviderComposeSms extends BaseComposeMessaging {

  @FormGen({
    name: "Template",
    type: "enum",
    enumValues: ArrayUtil.toSorted(
      MESSAGING_TEMPLATE_KTM.values
        .filter(value => PROVIDER_COMPOSE_SMS_TEMPLATE_IDS.includes(value.key as MessagingTemplateId)), (v1, v2) => v1.metadata.templateName.localeCompare(v2.metadata.templateName))
      .map(value => $KTS(value.key, value.metadata.templateName)),
    required: true,
    onSetValue: (target: ProviderComposeSms, templateId: MessagingTemplateId) => {
      target.message = target.toMessage(templateId);
    }
  })
  templateId: MessagingTemplateId | "" = "";

  @FormGen({
    name: "Message",
    type: "text",
    readonly: true,
    required: true,
  })
  message: string = "";
}

// WhatsApp

export class ProviderComposeWhatsAppTemplateVariablesFactory extends BaseProviderComposeMessagingTemplateVariablesFactory {
}

const PROVIDER_COMPOSE_WHATSAPP_TEMPLATE_IDS: MessagingTemplateId[] = [
  MessagingTemplateId.PATIENT_RECALL_CHECKUP,
  MessagingTemplateId.PATIENT_PAYMENT_BALANCE,
  MessagingTemplateId.PATIENT_TREATMENT_PRE_SURGERY,
  MessagingTemplateId.PATIENT_TREATMENT_POST_SURGERY,
  MessagingTemplateId.PATIENT_BIRTHDAY_GREETING,
];

export class ProviderComposeWhatsApp extends BaseComposeMessaging {

  @FormGen({
    name: "Template",
    type: "enum",
    enumValues: ArrayUtil.toSorted(
      MESSAGING_TEMPLATE_KTM.values
        .filter(value => PROVIDER_COMPOSE_WHATSAPP_TEMPLATE_IDS.includes(value.key as MessagingTemplateId)), (v1, v2) => v1.metadata.templateName.localeCompare(v2.metadata.templateName))
      .map(value => $KTS(value.key, value.metadata.templateName)),
    required: true,
    onSetValue: (target: ProviderComposeWhatsApp, templateId: MessagingTemplateId) => {
      target.message = target.toMessage(templateId);
    }
  })
  templateId: MessagingTemplateId | "" = "";

  @FormGen({
    name: "Message",
    type: "text",
    readonly: true,
    required: true,
  })
  message: string = "";
}

export abstract class BasePatientComposeMessage extends BaseComposeMessage {

  constructor() {
    super(PracticeData.patient().myProfile().toTypeId(), PracticeData.patient().groupProfiles.getByName(GroupProfileName.STAFF_PROVIDERS_ALL).toTypeId());
  }
}

export enum PatientDocumentType {
  REPORT = "report",
  PRESCRIPTION = "prescription",
  INVOICE = "invoice",
  OTHER = "other",
}

export const PATIENT_DOCUMENT_TYPE_KTS = new KeyTextStrings([
  $KTS(PatientDocumentType.REPORT, "Report"),
  $KTS(PatientDocumentType.PRESCRIPTION, "Prescription"),
  $KTS(PatientDocumentType.INVOICE, "Invoice"),
  $KTS(PatientDocumentType.OTHER, "Other"),
]);

@JsonObject()
export class PatientDocument {

  @FormGen({
    name: "File",
    type: "file",
    required: true,
  })
  @JsonProperty()
  file: string;

  @FormGen({
    name: "Document type",
    type: "enum",
    enumValues: PATIENT_DOCUMENT_TYPE_KTS.values,
    enumDefaultKeyFirst: true,
    required: true,
  })
  @JsonProperty()
  type: string;

  @FormGen({
    name: "Name",
    type: "string",
    required: true,
  })
  @JsonProperty()
  name: string;

  @FormGen({
    name: "Date",
    type: "datenum",
    required: true,
  })
  @JsonProperty()
  date: number;

  @FormGen({
    name: "Notes",
    type: "text",
    size: "xsmall",
  })
  notes: string;

  static createNew(): PatientDocument {
    return new PatientDocument();
  }
}

@JsonObject()
export class ProviderAppointmentAvailabilitySlot {

  @JsonProperty()
  readonly available?: boolean;

  @JsonProperty()
  readonly time: number;

  @JsonProperty()
  readonly timeText: string;

  @JsonProperty()
  readonly dateText: string;

  constructor(available: boolean, time: number, timeText: string, dateText: string) {
    this.available = available;
    this.time = time;
    this.timeText = timeText;
    this.dateText = dateText;
  }
}

@JsonObject()
export class AppointmentDetails {

  @JsonProperty()
  slot: ProviderAppointmentAvailabilitySlot;

  @JsonProperty()
  practicePageId: string;

  static createNew(slot: ProviderAppointmentAvailabilitySlot, practicePageId: string): AppointmentDetails {
    const details = new AppointmentDetails();
    details.slot = slot;
    details.practicePageId = practicePageId;
    return details;
  }
}

@JsonObject()
export class ProviderAppointmentAvailability {

  @JsonProperty()
  readonly slots: ProviderAppointmentAvailabilitySlot[] = [];
}

export enum AppointmentRequestTarget {
  ME = "me",
  OTHER = "other",
}

export const APPOINTMENT_REQUEST_TARGET_KTS = new KeyTextStrings([
  $KTS(AppointmentRequestTarget.ME, "For Me"),
  $KTS(AppointmentRequestTarget.OTHER, "For Someone Else"),
]);

@JsonObject()
export class AppointmentRequestPatientProfileBasicInfo {

  @JsonProperty()
  readonly target: AppointmentRequestTarget;

  @FormGen({
    name: "First Name",
    type: "string",
    required: true,
  })
  @JsonProperty()
  firstname: string;

  @FormGen({
    name: "Last Name",
    type: "string",
    required: true,
  })
  @JsonProperty()
  lastname: string;

  @FormGen({
    name: "Email",
    type: "string",
    inputMode: "email",
    required: true,
  })
  @JsonProperty()
  email: string;

  @FormGen({
    name: "Gender",
    type: "enum",
    required: true,
    enumValues: GENDER_KTS.values,
    enumVariant: "button",
  })
  @JsonProperty()
  gender: string;

  @FormGen({
    name: "Phone Number",
    type: "phone_number",
    required: true,
  })
  @JsonProperty()
  phoneNumber: string;

  @FormGen({
    name: "Notes",
    type: "text",
    size: "xsmall",
  })
  @JsonProperty()
  notes: string;

  static createForMe(profile: PatientAccountProfile): AppointmentRequestPatientProfileBasicInfo {
    const info = new AppointmentRequestPatientProfileBasicInfo(AppointmentRequestTarget.ME);
    info.firstname = profile.basicInfo.firstname;
    info.lastname = profile.basicInfo.lastname;
    info.email = profile.basicInfo.email;
    info.gender = profile.basicInfo.gender;
    info.phoneNumber = profile.basicInfo.phoneNumber;
    return info;
  }

  constructor(target: AppointmentRequestTarget) {
    this.target = target;
  }
}

export enum AppointmentRequestStatus {
  INBOUND = "inbound",
  REJECTED = "rejected",
}

@JsonObject()
export class AppointmentRequest extends BasePracticeListItem {

  @FormGen({
    name: "First Name",
    type: "string",
    required: true,
  })
  @JsonProperty()
  firstname: string;

  @FormGen({
    name: "Last Name",
    type: "string",
    required: true,
  })
  @JsonProperty()
  lastname: string;

  @FormGen({
    name: "Email",
    type: "string",
    inputMode: "email",
    required: true,
  })
  @JsonProperty()
  email: string;

  @FormGen({
    name: "Gender",
    type: "enum",
    required: true,
    enumValues: GENDER_KTS.values,
    enumVariant: "button",
  })
  @JsonProperty()
  gender: string;

  @FormGen({
    name: "Phone Number",
    type: "phone_number",
    required: true,
  })
  @JsonProperty()
  phoneNumber: string;

  @FormGen({
    name: "Notes",
    type: "text",
    size: "xsmall",
  })
  @JsonProperty()
  notes: string;

  @FormGen({
    name: "Date",
    type: "datetimenum",
  })
  @JsonProperty()
  scheduled: number;

  @JsonProperty()
  readonly providerPageId: string;

  @JsonProperty()
  status: AppointmentRequestStatus = AppointmentRequestStatus.INBOUND;

  static createNew(providerPageId: string): AppointmentRequest {
    return new AppointmentRequest(md5_uuid(), providerPageId);
  }

  constructor(id: string, providerPageId: string) {
    super(id);
    this.providerPageId = providerPageId;
  }
}

export class AppointmentRequests extends BasePracticeListItemsLoader<AppointmentRequest> {

  constructor(practiceId: string) {
    super(practiceId);
  }

  async addRequest(request: AppointmentRequest): Promise<void> {
    await this.addListItem(request);
  }

  async deleteRequest(request: AppointmentRequest): Promise<void> {
    await this.deleteListItem(request);
  }

  protected practiceDataBasePath(): string {
    return "appointmentRequests";
  }

  protected deserializeItem(value: any): AppointmentRequest {
    return JSON_OBJECT.deserializeObject(value, AppointmentRequest);
  }

  protected serializeItem(item: AppointmentRequest): any {
    return JSON_OBJECT.serializeObject(item);
  }

  protected sortOrder(item1: AppointmentRequest, item2: AppointmentRequest): number {
    return item2.created - item1.created;
  }
}

export enum AppointmentStatus {
  CONFIRMED = "confirmed",
  CHECKIN_CONFIRMED = "checkin_confirmed",
  COMPLETED = "completed",
  CANCELED = "canceled",
}

export const APPOINTMENT_STATUS_KTS = new KeyTextStrings([
  $KTS(AppointmentStatus.CONFIRMED, "Confirmed"),
  $KTS(AppointmentStatus.COMPLETED, "Completed"),
  $KTS(AppointmentStatus.CANCELED, "Canceled"),
]);

export const APPOINTMENT_STATUS_KTM_COLORS = new KeyMetadatas<ColorValue>([
  $KTM<ColorValue>(AppointmentStatus.CONFIRMED, new ColorValue(black, colorPastelYellow)),
  $KTM<ColorValue>(AppointmentStatus.CANCELED, new ColorValue(white, mediumGray)),
]);

@JsonObject()
export class Appointment extends BasePracticeListItem {

  patient: PatientProfile;

  @FormGen({
    name: "Patient",
    type: "autocomplete",
    noneditable: true,
    required: true,
    autocompleteCreateAction: patientProfileAutocompleteCreateAction,
    autocompleteLoader: async function (value, inputText) {
      return patientProfileAutocompleteLoaderCreator(ProfileKeyType.ID)();
    },
  })
  @JsonProperty()
  patientId: string;

  provider: ProviderProfile;

  @FormGen({
    name: "Provider",
    type: "autocomplete",
    noneditable: true,
    required: true,
    autocompleteLoader: async function (value, inputText) {
      return providerProfileAutocompleteLoaderCreator(ProfileKeyType.ID)();
    },
  })
  @JsonProperty()
  providerId: string;

  @FormGen({
    name: "Date",
    type: "datetimenum",
  })
  @JsonProperty()
  scheduled: number;

  @FormGen({type: "string", name: "Reason", required: true})
  @JsonProperty()
  reason: string;

  @FormGen({
    type: "enum", name: "Duration",
    enumValues: APPOINTMENT_DURATION_KTM.kts().values,
    enumDefaultKeyFirst: true,
  })
  @JsonProperty()
  duration: string;

  @FormGen({
    type: "enum",
    name: "Visit type",
    enumValues: [
      $KTS("new", "New visit"),
      $KTS("followup", "Follow up visit"),
    ],
    enumDefaultKey: "new",
  })
  @JsonProperty()
  visitType: string = "new";

  visitTypeText(): string {
    return enumValue(this, "visitType");
  }

  @FormGen({
    type: "enum",
    name: "Status",
    enumValues: APPOINTMENT_STATUS_KTS.values,
    enumDefaultKey: AppointmentStatus.CONFIRMED,
  })
  @JsonProperty()
  status: string = AppointmentStatus.CONFIRMED;

  @FormGen({name: "Notes", type: "text", size: "small"})
  @JsonProperty()
  notes: string;

  static fromRequest(request: AppointmentRequest, selectedPatientProfile: PatientProfile): Appointment | null {
    const appointment = new Appointment(request.id);
    // Only providers / staff should create appointments.
    if (!PracticeData.shared().providerProfiles.getListItem(request.providerPageId)) {
      return null;
    }
    appointment.providerId = request.providerPageId;
    appointment.patientId = selectedPatientProfile.id;
    appointment.scheduled = request.scheduled;
    return appointment;
  }

  static createNew(): Appointment {
    return new Appointment(md5_uuid());
  }

  static readonly PATIENT_ID_REL_NAME = "patientIds";

  patientIdRel(): Rel {
    return new Rel(Appointment.PATIENT_ID_REL_NAME, this.patientId, this.id, true, true);
  }

  summary(): string {
    return "Appointment";
  }
}

export class Appointments extends BasePracticeListItemsLoader<Appointment> {

  constructor(practiceId: string) {
    super(practiceId);
  }

  async loadAppointmentsByPatientProfile(patientProfile: PatientProfile): Promise<Appointment[]> {
    const appointments: Appointment[] = [];
    const val = await this.getProvisioningContext().dbRef_getVal(this.listPath() + Rel.oneToOnePathFor(Appointment.PATIENT_ID_REL_NAME, patientProfile.id));
    if (val) {
      for (const key of Object.keys(val)) {
        appointments.push(await this.loadListItem(key));
      }
    }
    return appointments;
  }

  async addAppointment(appointment: Appointment): Promise<void> {
    await this.addListItem(appointment, appointment.patientIdRel());
  }

  async deleteAppointment(appointment: Appointment): Promise<void> {
    await this.deleteListItem(appointment, appointment.patientIdRel());
  }

  protected async onAfterItemDeserialized(item: Appointment, val?: any): Promise<Appointment> {
    await super.onAfterItemDeserialized(item, val);
    item.patient = await PracticeData.shared().patientProfiles.getOrLoadItem(item.patientId);
    item.provider = await PracticeData.shared().providerProfiles.getOrLoadItem(item.providerId);
    return item;
  }

  protected practiceDataBasePath(): string {
    return "appointments";
  }

  protected deserializeItem(value: any): Appointment {
    return JSON_OBJECT.deserializeObject(value, Appointment);
  }

  protected serializeItem(item: Appointment): any {
    return JSON_OBJECT.serializeObject(item);
  }

  protected sortOrder(item1: Appointment, item2: Appointment): number {
    return item2.created - item1.created;
  }
}

export enum OnboardingStep {
  SETUP_PROVIDER,
  SETUP_PRACTICE,
  VERIFY_PROVIDER,
  VERIFY_PRACTICE,
}

export enum QuickOnboardingStep {
  QUICK_SETUP_PROVIDER,
  QUICK_SETUP_PRACTICE,
}

/// Calls

export const TOKEN_URL = "https://superfyne-apps-calltoken-service.web.app/generate";

export enum CallChannel {
  AUDIO = "audio",
  VIDEO = "video",
}

export type CallChannelMetadata = {
  text: string,
  iconType: typeof SvgIcon,
}

export const CALL_CHANNEL_METADATAS_KTM = new KeyMetadatas<CallChannelMetadata>([
  $KTM(CallChannel.AUDIO, {text: "Audio call", iconType: PhoneOutlined}),
  $KTM(CallChannel.VIDEO, {text: "Video call", iconType: VideoCallOutlined}),
])

export enum CallType {
  NONE = "none",
  CREATE = "create",
  CONNECT = "connect",
  START = "start",
  END = "end",
}

export enum CallEndReason {
  NO_ANSWER,
  HANG_UP,
  TIME_OUT,
  BUSY,
  NAVIGATED_AWAY,
  NO_PERMISSIONS,
}

@JsonObject()
export class Call extends BasePracticeListItem {

  @JsonProperty()
  type: CallType;

  @JsonProperty()
  channel: CallChannel;

  @JsonProperty()
  time: number; // Last updated.

  started?: number;

  @JsonProperty()
  ended?: number;

  @JsonProperty()
  endReason?: CallEndReason;

  static createNew(id: string, channel: CallChannel) {
    const call = new Call(id, channel);
    call.type = CallType.CREATE;
    call.time = call.created;
    return call;
  }

  static updateConnect(call: Call): Call {
    const copy = new Call(call.id, call.channel);
    copy.type = CallType.CONNECT;
    copy.started = Date.now();
    copy.time = copy.started;
    return copy;
  }

  static updateStart(call: Call): Call {
    const copy = new Call(call.id, call.channel);
    copy.type = CallType.START;
    copy.started = Date.now();
    copy.time = copy.started;
    return copy;
  }

  static updateEnd(call: Call, endReason: CallEndReason): Call {
    const copy = new Call(call.id, call.channel);
    copy.type = CallType.END;
    copy.ended = Date.now();
    copy.time = copy.ended;
    copy.endReason = endReason;
    return copy;
  }

  constructor(id: string, channel: CallChannel) {
    super(id);
    this.channel = channel;
  }
}

export const CALL_END_REASON_TEXT_FROM_ME = new Map<CallEndReason, string>([
    [
      CallEndReason.NO_ANSWER, "Did not answer"
    ],
    [
      CallEndReason.HANG_UP, "Call ended"
    ],
    [
      CallEndReason.TIME_OUT, "Lost connection"
    ],
    [
      CallEndReason.BUSY, "On another call",
    ],
    [
      CallEndReason.NAVIGATED_AWAY, "Left",
    ],
    [
      CallEndReason.NO_PERMISSIONS, "Permissions not granted",
    ],
  ]
)

export const CALL_END_REASON_TEXT_FROM_OTHER = new Map<CallEndReason, string>([
    [
      CallEndReason.NO_ANSWER, "You missed the call"
    ],
    [
      CallEndReason.HANG_UP, "Call ended"
    ],
    [
      CallEndReason.TIME_OUT, "Lost connection"
    ],
    [
      CallEndReason.BUSY, "On another call",
    ],
    [
      CallEndReason.NAVIGATED_AWAY, "Left",
    ],
    [
      CallEndReason.NO_PERMISSIONS, "Permissions not granted",
    ],
  ]
)

const IS_MOBILE = (() => {
  if (typeof navigator === 'undefined' || typeof navigator.userAgent !== 'string') {
    return false;
  }
  return /Mobile/.test(navigator.userAgent);
})();

const DEFAULT_CONNECT_OPTIONS = {
  // Comment this line if you are playing music.
  maxAudioBitrate: 16000,

  // Capture 720p video @ 24 fps.
  video: {height: 720, frameRate: 24, width: 720}
};

export interface CallListener {

  onConnected(room: Room);

  onAttachTrack(room: Room, track: LocalTrack | RemoteTrack, participant: LocalParticipant | RemoteParticipant);

  onDetachTrack(room: Room, track: LocalTrack | RemoteTrack, participant: LocalParticipant | RemoteParticipant);

  onDisconnected(room: Room);

  onCallChanged(currentCall: Call | null);
}

export class Calls extends BasePracticeListItemsLoader<Call> implements OnListItemsListener<Call> {

  private currentCall: Call;
  private callListener: CallListener;

  constructor(practiceId: string) {
    super(practiceId);
    this.registerObserver(this);
  }

  async init(appointment: Appointment): Promise<void> {
    await this.maybeEndCurrentCall();
    this.currentCall = await this.getOrLoadItem(appointment.id);
    if (this.currentCall?.type === CallType.END) {
      await this.maybeEndCurrentCall();
    }
  }

  getCurrentCall(): Call | null | undefined {
    return this.currentCall;
  }

  protected practiceDataBasePath(): string {
    return "calls";
  }

  onItemChanged(item: Call, change: ListItemChange): void {
    if (item.id === this.currentCall?.id) {
      this.updateCurrentCall(item);
    }
  }

  private updateCurrentCall(call: Call) {
    this.currentCall = call;
    this.callListener?.onCallChanged(call);
    if (this.currentCall?.type === CallType.END) {
      this.room?.disconnect();
      this.room = null;
      switch (this.currentCall.endReason) {
        case CallEndReason.HANG_UP:
          setTimeout(() => {
            this.clearCurrentCall();
          }, 1000);
          break;
        case CallEndReason.NO_ANSWER:
        case CallEndReason.TIME_OUT:
        case CallEndReason.BUSY:
        case CallEndReason.NAVIGATED_AWAY:
        case CallEndReason.NO_PERMISSIONS:
          // Nothing. UI should present a button to clear out current call.
          break;
        default:
          this.clearCurrentCall();
          break;
      }
    }
  }

  protected deserializeItem(value: any): Call {
    return JSON_OBJECT.deserializeObject(value, Call);
  }

  protected serializeItem(item: Call): any {
    return JSON_OBJECT.serializeObject(item);
  }

  protected sortOrder(item1: Call, item2: Call): number {
    return item2.created - item1.created;
  }

  clearCurrentCall() {
    this.currentCall = null;
    this.callListener?.onCallChanged(null);
  }

  private async maybeEndCurrentCall(): Promise<void> {
    if (this.currentCall) {
      if (this.currentCall.type !== CallType.END) {
        await this.endOtherCall(this.currentCall, CallEndReason.NAVIGATED_AWAY);
      }
      await this.deleteListItemById(this.currentCall.id);
      this.clearCurrentCall();
    }
  }

  // CALLER ONLY
  async createCall(appointment: Appointment, callListener: CallListener) {
    if (this.currentCall) {
      throw new Error("Cannot call createCall with ongoing call. Call init() first.");
    }
    const createCall = this.currentCall = Call.createNew(appointment.id, CallChannel.VIDEO);
    this.callListener = callListener;
    await this.addListItem(createCall);
    await this.setupAndJoinRoom();
    // this.currenCallTimeoutId = setTimeout(() => this.endCurrentCall(CallEndReason.NO_ANSWER), 10 * 60000);
  }

  // CALLEE ONLY
  async connectAndStartCurrentCall(appointment: Appointment, callListener: CallListener) {
    if (!this.currentCall) {
      throw new Error("Cannot call connectAndStartCurrentCall with NO ongoing call. Call init() first.");
    }
    this.callListener = callListener;
    const connectCall = Call.updateConnect(this.currentCall);
    await this.addListItem(connectCall);

    await this.setupAndJoinRoom();

    const startCall = Call.updateStart(this.currentCall);
    this.currentCall = startCall;
    await this.addListItem(startCall);
  }

  private async setupAndJoinRoom() {
    const roomId = this.currentCall.id;
    const response = await fetch(TOKEN_URL + `?identity=${getMemberAuth().getMemberId()}&room=${roomId}`);
    const token = await response.text();
    const deviceIds = {
      audio: IS_MOBILE ? null : localStorage.getItem('audioDeviceId'),
      video: IS_MOBILE ? null : localStorage.getItem('videoDeviceId')
    };
    const connectOptions: any = {
      ...DEFAULT_CONNECT_OPTIONS,
    }
    connectOptions.audio = {deviceId: {exact: deviceIds.audio}};
    connectOptions.name = roomId;
    connectOptions.video.deviceId = {exact: deviceIds.video};
    await this.joinRoom(token, connectOptions);
  }

// CALLER OR CALLEE
  async endCurrentCall(reason: CallEndReason) {
    if (!this.currentCall) {
      throw Error;
    }
    const endCall = Call.updateEnd(this.currentCall, reason);
    this.currentCall = endCall;
    await this.addListItem(endCall);
  }

  private async endOtherCall(otherCall: Call, reason: CallEndReason) {
    const endCall = Call.updateEnd(otherCall, reason);
    this.currentCall = endCall;
    await this.addListItem(endCall);
  }

  private attachTrack(track: LocalTrack | RemoteTrack, participant: LocalParticipant | RemoteParticipant) {
    this.callListener?.onAttachTrack(this.room, track, participant);
  }

  private detachTrack(track: LocalTrack | RemoteTrack, participant: LocalParticipant | RemoteParticipant) {
    this.callListener?.onDetachTrack(this.room, track, participant);
  }

  private participantConnected(participant: LocalParticipant | RemoteParticipant) {
    // Handle the TrackPublications already published by the Participant.
    participant.tracks.forEach(publication => {
      this.trackPublished(publication, participant);
    });

    // Handle theTrackPublications that will be published by the Participant later.
    participant.on('trackPublished', publication => {
      this.trackPublished(publication, participant);
    });
  }

  private participantDisconnected(participant: LocalParticipant | RemoteParticipant) {
  }

  private trackPublished(publication: LocalTrackPublication | RemoteTrackPublication, participant: LocalParticipant | RemoteParticipant) {
    // If the TrackPublication is already subscribed to, then attach the Track to the DOM.
    if (publication.track) {
      this.attachTrack(publication.track, participant);
    }

    // Once the TrackPublication is subscribed to, attach the Track to the DOM.
    publication.on('subscribed', track => {
      this.attachTrack(track, participant);
    });

    // Once the TrackPublication is unsubscribed from, detach the Track from the DOM.
    publication.on('unsubscribed', track => {
      this.detachTrack(track, participant);
    });
  }

  private room: Room | null;

  private async joinRoom(token: string, connectOptions: any) {
    // Comment the next two lines to disable verbose logging.
    const logger = Logger.getLogger('twilio-video');
    logger.setLevel('error');

    // Join to the Room with the given AccessToken and ConnectOptions.
    const room = await connect(token, connectOptions);
    this.room = room;
    this.callListener?.onConnected(this.room);

    // Save the LocalVideoTrack.
    const localVideoTrack = Array.from(room.localParticipant.videoTracks.values())[0].track;

    // Make the Room available in the JavaScript console for debugging.
    // window.room = room;

    // Handle the LocalParticipant's media.
    this.participantConnected(room.localParticipant);

    // Subscribe to the media published by RemoteParticipants already in the Room.
    room.participants.forEach(participant => {
      this.participantConnected(participant);
    });

    // Subscribe to the media published by RemoteParticipants joining the Room later.
    room.on('participantConnected', participant => {
      this.participantConnected(participant);
    });

    // Handle a disconnected RemoteParticipant.
    room.on('participantDisconnected', participant => {
      this.participantDisconnected(participant);
    });

    // TODO: This is throwing an error on cloud builds. Ignore for now.
    //@ts-ignore
    room.once('disconnected', (room, error) => {
      // Stop the LocalVideoTrack.
      localVideoTrack.stop();

      // Handle the disconnected LocalParticipant.
      this.participantDisconnected(room.localParticipant);

      // Handle the disconnected RemoteParticipants.
      room.participants.forEach(participant => {
        this.participantDisconnected(participant);
      });

      this.callListener?.onDisconnected(this.room);
    });
  }

  getOtherParticipantCount(): number {
    return this.room?.participants.size || 0;
  }
}


/// Labs
// These are types and not classes as we don't store them in the db yet (instead they're read from a json file)

export const LAB_TEST_TEMPLATE_TYPE_KTS = new KeyTextStrings([
  $KTS(LabTestTemplateType.SINGLE, "Single Parameter", "For tests that have a single result value, e.g. Haemoglobin, TLC"),
  $KTS(LabTestTemplateType.MULTI, "Multi Parameter", "For tests that have multiple result values, e.g. DLC"),
  $KTS(LabTestTemplateType.NESTED, "Nested", "For tests that have one or more nested result values, e.g. Urine Routine"),
  $KTS(LabTestTemplateType.FREEFORM, "Freeform", "For tests that are reported as freeform text. The text is fully editable with rich formatting options, e.g. Histopathology"),
]);

export enum LabTestTemplateParameterUnit {
  PERCENT = "percent",
  MILL_MM3 = "mill_mm3",
  THOU_MM3 = "thou_mm3",
  PG = "pg",
  MIU_ML = "miu_ml",
  UIU_ML = "uiu_ml",
  AU_ML = "au_ml",
  IU_ML = "iu_ml",
  U_ML = "u_ml",
  UU_ML = "uu_ml",
  NG_ML = "ng_ml",
  PG_ML = "pg_ml",
  G_DL = "g_dl",
  MG_DL = "mg_dl",
  UG_DL = "ug_dl",
  NG_DL = "ng_dl",
  CELLS_HPF = "cells_hpf",
  ML_MIN = "ml_min",
  MG_L = "mg_l",
  MEQ_L = "meq_l",
  NMOL_L = "nmol_l",
  U_L = "u_l",
  UG_L = "ug_l",
  UMOL_L = "umol_l",
  MMOL_L = "mmol_l",
  KUA_L = "kua_l",
  MG_G_CREATININE = "mg_g_creatinine",
  FL = "fl",
  MM_HR = "mm_hr",
  SEC = "sec",
  INDEX = "index",
}

export const LAB_TEST_TEMPLATE_PARAMETER_UNIT_KTS = new KeyTextStrings([
  $KTS(LabTestTemplateParameterUnit.PERCENT, "%"),
  $KTS(LabTestTemplateParameterUnit.MILL_MM3, "mill/mm³"),
  $KTS(LabTestTemplateParameterUnit.THOU_MM3, "thou/mm³"),
  $KTS(LabTestTemplateParameterUnit.PG, "pg"),
  $KTS(LabTestTemplateParameterUnit.AU_ML, "AU/mL"),
  $KTS(LabTestTemplateParameterUnit.IU_ML, "IU/mL"),
  $KTS(LabTestTemplateParameterUnit.U_ML, "U/mL"),
  $KTS(LabTestTemplateParameterUnit.UU_ML, "µU/mL"),
  $KTS(LabTestTemplateParameterUnit.MIU_ML, "mIU/mL"),
  $KTS(LabTestTemplateParameterUnit.UIU_ML, "µIU/mL"),
  $KTS(LabTestTemplateParameterUnit.NG_ML, "ng/mL"),
  $KTS(LabTestTemplateParameterUnit.PG_ML, "pg/mL"),
  $KTS(LabTestTemplateParameterUnit.G_DL, "g/dL"),
  $KTS(LabTestTemplateParameterUnit.MG_DL, "mg/dL"),
  $KTS(LabTestTemplateParameterUnit.UG_DL, "µg/dL"),
  $KTS(LabTestTemplateParameterUnit.NG_DL, "ng/dL"),
  $KTS(LabTestTemplateParameterUnit.MG_L, "mg/L"),
  $KTS(LabTestTemplateParameterUnit.MEQ_L, "mEq/L"),
  $KTS(LabTestTemplateParameterUnit.NMOL_L, "nmol/L"),
  $KTS(LabTestTemplateParameterUnit.U_L, "U/L"),
  $KTS(LabTestTemplateParameterUnit.UG_L, "ug/L"),
  $KTS(LabTestTemplateParameterUnit.UMOL_L, "µmol/L"),
  $KTS(LabTestTemplateParameterUnit.MMOL_L, "mmol/L"),
  $KTS(LabTestTemplateParameterUnit.KUA_L, "kUA/L"),
  $KTS(LabTestTemplateParameterUnit.ML_MIN, "mL/min"),
  $KTS(LabTestTemplateParameterUnit.MG_G_CREATININE, "mg/g Creatinine"),
  $KTS(LabTestTemplateParameterUnit.MM_HR, "mm/hr"),
  $KTS(LabTestTemplateParameterUnit.FL, "fL"),
  $KTS(LabTestTemplateParameterUnit.CELLS_HPF, "cells/hpf"),
  $KTS(LabTestTemplateParameterUnit.SEC, "sec"),
  $KTS(LabTestTemplateParameterUnit.INDEX, "Index"),
]);

export enum LabTestTemplateParameterInputType {
  SINGLE = "single",
  MULTI = "multi",
  POS_NEG = "pos_neg",
}

export const LAB_TEST_TEMPLATE_PARAMETER_INPUT_TYPE_KTS = new KeyTextStrings([
  $KTS(LabTestTemplateParameterInputType.SINGLE, "Single-line"),
  $KTS(LabTestTemplateParameterInputType.MULTI, "Multi-line"),
  $KTS(LabTestTemplateParameterInputType.POS_NEG, "Positive / Negative"),
]);

@JsonObject()
export class LabTestTemplateParametersValueType {

  @FormGen({
    name: "Optional",
    type: "boolean",
    required: true,
  })
  @JsonProperty()
  optional: boolean;

  @FormGen({
    name: "Unit",
    type: "enum",
    enumValues: LAB_TEST_TEMPLATE_PARAMETER_UNIT_KTS.values,
    enumDefaultKeyFirst: true,
    required: true,
  })
  @JsonProperty()
  unit: string;

  @FormGen({
    name: "Input Type",
    type: "enum",
    enumValues: LAB_TEST_TEMPLATE_PARAMETER_INPUT_TYPE_KTS.values,
    enumDefaultKeyFirst: true,
    required: true,
  })
  @JsonProperty()
  inputType: string;

  @FormGen({
    name: "Default Value",
    type: "text",
    size: "xsmall",
  })
  @JsonProperty()
  defaultValue: string;

  @FormGen({
    name: "Reference",
    type: "string",
  })
  @JsonProperty()
  reference: string;
}

@JsonObject()
export class LabTestTemplateParametersItem {

  @JsonProperty()
  slug: string;

  @FormGen({
    name: "Name",
    type: "string",
    required: true,
  })
  @JsonProperty()
  name: string;

  @FormGen({
    name: null,
    type: "object",
    required: true,
  })
  @JsonProperty()
  readonly value: LabTestTemplateParametersValueType = new LabTestTemplateParametersValueType();

  onBeforeItemSerialized(): void {
    let name = this.name?.trim()
    if (!this.slug && name) {
      this.slug = StringUtil.toSlug(name);
    }
  }
}

@JsonObject()
export class LabTestTemplateParametersItemGroup {

  @FormGen({
    name: "Name",
    type: "string",
    required: true,
  })
  @JsonProperty()
  name?: string;

  @FormGen({
    name: "Items",
    type: "array_object",
    arrayObjectCreator: () => new LabTestTemplateParametersItem(),
    required: true,
  })
  @JsonProperty({dataStructure: "array", type: LabTestTemplateParametersItem})
  readonly items: LabTestTemplateParametersItem[] = [];

  onBeforeItemSerialized(): void {
    for (const item of this.items) {
      item.onBeforeItemSerialized();
    }
  }
}

export interface LabTestTemplateParametersType {

  readonly paramsType;

  onBeforeItemSerialized(): void;
}

@JsonObject()
export class LabTestTemplateParametersSingle extends LabTestTemplateParametersValueType implements LabTestTemplateParametersType {

  static readonly DEFAULT_SLUG = "default";

  readonly paramsType = LabTestTemplateType.SINGLE;

  @JsonProperty()
  readonly slug: string = LabTestTemplateParametersSingle.DEFAULT_SLUG;

  onBeforeItemSerialized(): void {
  }
}

@JsonObject()
export class LabTestTemplateParametersMulti implements LabTestTemplateParametersType {

  readonly paramsType = LabTestTemplateType.MULTI;

  @FormGen({
    name: "Items",
    type: "array_object",
    arrayObjectCreator: () => new LabTestTemplateParametersItem(),
    required: true,
  })
  @JsonProperty({dataStructure: "array", type: LabTestTemplateParametersItem})
  readonly items: LabTestTemplateParametersItem[] = [];

  onBeforeItemSerialized(): void {
    for (const item of this.items) {
      item.onBeforeItemSerialized();
    }
  }
}

@JsonObject()
export class LabTestTemplateParametersNested implements LabTestTemplateParametersType {

  readonly paramsType = LabTestTemplateType.NESTED;

  @FormGen({
    name: "Groups",
    type: "array_object",
    arrayObjectCreator: () => new LabTestTemplateParametersItemGroup(),
    required: true,
  })
  @JsonProperty({dataStructure: "array", type: LabTestTemplateParametersItemGroup})
  readonly groups: LabTestTemplateParametersItemGroup[] = [];

  onBeforeItemSerialized(): void {
    for (const group of this.groups) {
      group.onBeforeItemSerialized();
    }
  }
}

@JsonObject()
export class LabTestTemplateParametersFreeform implements LabTestTemplateParametersType {

  static readonly DEFAULT_SLUG = "default";

  readonly paramsType = LabTestTemplateType.FREEFORM;

  @JsonProperty()
  readonly slug: string = LabTestTemplateParametersFreeform.DEFAULT_SLUG;

  @FormGen({
    name: "Content",
    type: "rich_content",
    required: true,
  })
  @JsonProperty()
  readonly content: string;

  onBeforeItemSerialized(): void {
  }
}

export type LabTestInterface = {
  name: string;
}

@JsonObject()
export class LabTestTemplate {

  static multiStepConfigSteps(): FormGenMultiStepConfigStep[] {
    return [
      {text: "Type"},
      {text: "Parameters"},
      {text: "Preview"},
    ];
  }

  labTest: LabTestInterface;

  @FormGen({
    name: "Template type",
    type: "enum",
    required: true,
    noneditable: true,
    enumVariant: "radio",
    enumValues: LAB_TEST_TEMPLATE_TYPE_KTS.values,
    enumDefaultKey: LabTestTemplateType.SINGLE,
    multiStep: true,
    multiStepIndex: 0,
  })
  @JsonProperty()
  type: LabTestTemplateType = LabTestTemplateType.SINGLE;

  @FormGen({
    name: "Parameters",
    type: "custom",
    customRenderer: (target, value, setValue) => {
      return renderLabTestTemplateParameters((target as LabTestTemplate).type, value, setValue);
    },
    multiStep: true,
    multiStepIndex: 1,
  })
  @JsonProperty()
  _parameters: any;

  private _parametersSingle?: LabTestTemplateParametersSingle;
  private _parametersMulti?: LabTestTemplateParametersMulti;
  private _parametersNested?: LabTestTemplateParametersNested;
  private _parametersFreeform?: LabTestTemplateParametersFreeform;

  @FormGen({
    name: "Notes / Interpretation",
    type: "rich_content",
    multiStep: true,
    multiStepIndex: 2,
  })
  @JsonProperty()
  notes: string; // In markdown format.

  @FormGen({
    name: "Preview",
    type: "custom",
    customRenderer: (target, value, setValue) => {
      return renderLabTestTemplatePreview(target as LabTestTemplate);
    },
    multiStep: true,
    multiStepIndex: 3,
  })
  private _preview: void;

  static createNewForLabTest(labTest: LabTestInterface): LabTestTemplate {
    const template = new LabTestTemplate();
    template.labTest = labTest;
    return template;
  }

  getParameters(): LabTestTemplateParametersType {
    switch (this.type) {
      case LabTestTemplateType.SINGLE:
        if (!this._parametersSingle) {
          this._parametersSingle = this._parameters instanceof LabTestTemplateParametersSingle
            ? this._parameters
            : JSON_OBJECT.deserializeObject(this._parameters, LabTestTemplateParametersSingle);
        }
        return this._parametersSingle;
      case LabTestTemplateType.MULTI:
        if (!this._parametersMulti) {
          this._parametersMulti = this._parameters instanceof LabTestTemplateParametersMulti
            ? this._parameters
            : JSON_OBJECT.deserializeObject(this._parameters, LabTestTemplateParametersMulti);
        }
        return this._parametersMulti;
      case LabTestTemplateType.NESTED:
        this._parametersNested = this._parameters instanceof LabTestTemplateParametersNested
          ? this._parameters
          : JSON_OBJECT.deserializeObject(this._parameters, LabTestTemplateParametersNested);
        return this._parametersNested;
      case LabTestTemplateType.FREEFORM:
        this._parametersFreeform = this._parameters instanceof LabTestTemplateParametersFreeform
          ? this._parameters
          : JSON_OBJECT.deserializeObject(this._parameters, LabTestTemplateParametersFreeform);
        return this._parametersFreeform;
    }
    return null;
  }
}

export type LabTestParameter = {
  name: string;
}

export type LabTestDocument = {
  id: string;
  name: string;
  required: boolean;
}

export type LabTestInstruction = {
  text: string;
}

export type LabTestInfo = {
  specimen: string;
  room: string;
  preparation: string;
  components: string;
  report: string;
  category: string;
  refrigerator: string;
  frozen: string;
  method: string;
  indication: string;
}

export type LabTestTypeMini = {
  id: string;
  slug: string;
  name: string;
}

export type LabTestType = LabTestTypeMini & {
  lpId: string;
  itemType: number;
  description?: string;
  hc?: boolean; // home collection
  tags?: string[];
  componentsCount?: number;
  minAge?: number;
  maxAge?: number;
  fromDays?: number;
  toDays?: number;
  dep?: string;
  subdep?: string;
  category?: number;
  categoryName?: string;
  price?: string;
  iconName?: string;
  recommendedFor?: string[];
  parameters: LabTestParameter[];
  info: LabTestInfo;
  documents: LabTestDocument[];
  instructions: LabTestInstruction[];
  suppIds?: string[];
}

export function LabTestUrl(labTest: LabTestType): string {
  return `/labs/tests/${labTest.slug}`;
}

@JsonObject()
export class LabTest extends BasePracticeListItem {

  @JsonProperty()
  readonly typeId: string; // This is LabTestType.id if a pre-defined test is used (via createNewFromType)

  @FormGen({
    name: "Name",
    type: "string",
    required: true
  })
  @JsonProperty()
  name: string;

  @FormGen({
    name: "Code",
    type: "string",
    required: true,
    noneditable: true,
  })
  @JsonProperty()
  code: string;

  @FormGen({
    name: "Amount",
    type: "currency",
    required: true
  })
  @JsonProperty()
  amount: number;

  @FormGen({
    name: "Enabled",
    type: "boolean",
    required: true
  })
  @JsonProperty()
  enabled: boolean;

  @FormGen({
    name: "Template",
    type: "custom",
    customRenderer: (target, value: LabTestTemplate[], setValue: (templates: LabTestTemplate[]) => void) => {
      return renderEditLabTestTemplateButton(target as LabTest, value, setValue);
    }
  })
  @JsonProperty({dataStructure: "array", type: LabTestTemplate})
  templates: LabTestTemplate[] = [];

  static createNew(name?: string, code?: string): LabTest {
    const labTest = new LabTest(md5_uuid(), null);
    labTest.name = name;
    labTest.code = code;
    return labTest;
  }

  static createNewFromType(type: LabTestTypeMini): LabTest {
    const labTest = new LabTest(md5_uuid(), type.id);
    labTest.name = type.name;
    labTest.code = type.id.toUpperCase();
    return labTest;
  }

  constructor(id: string, typeId: string) {
    super(id);
    this.typeId = typeId;
  }
}

export class LabTests extends BasePracticeListItemsLoader<LabTest> {

  constructor(practiceId: string) {
    super(practiceId);
  }

  protected practiceDataBasePath(): string {
    return "labTests";
  }

  protected async onBeforeItemSerialized(item: LabTest): Promise<LabTest> {
    for (const template of item.templates) {
      const parameters = template.getParameters();
      if (parameters) {
        parameters.onBeforeItemSerialized(); // Sets a slug if not already set.
        template._parameters = JSON_OBJECT.serializeObject(parameters);
      }
    }
    return await super.onBeforeItemSerialized(item);
  }

  protected async onAfterItemDeserialized(item: LabTest, val?: any): Promise<LabTest> {
    await super.onAfterItemDeserialized(item, val);
    item.templates?.forEach(template => {
      template.labTest = item;
    });
    return item;
  }

  protected deserializeItem(value: any): LabTest {
    return JSON_OBJECT.deserializeObject(value, LabTest);
  }

  protected serializeItem(item: LabTest): any {
    return JSON_OBJECT.serializeObject(item);
  }

  protected sortOrder(item1: LabTest, item2: LabTest): number {
    return item1.name.localeCompare(item2.name);
  }
}

export const labTestAutocompleteLoader = (): Promise<KeyTextString[]> => {
  const labTests = PracticeData.provider().labTests;
  return new Promise<KeyTextString[]>((resolve, reject) => {
    labTests.getOrLoadListItems()
      .then(procs => resolve(procs.filter(proc => proc.enabled).map(proc => $KTS(proc.id, `${proc.code} ${proc.name}`))))
      .catch(reason => reject(reason));
  });
}

export enum LabTestResultStatus {
  INCOMPLETE = "incomplete",
  COMPLETED = "completed",
  VERIFIED = "verified",
  CANCELED = "canceled",
}

export type LabTestResultStatusMetadata = {
  text: string,
  color: ColorValue,
}

export const LAB_TEST_RESULT_STATUS_KTM = new KeyMetadatas<LabTestResultStatusMetadata>([
  $KTM(LabTestResultStatus.INCOMPLETE, {text: "Incomplete", color: new ColorValue("black", colorYellow)}),
  $KTM(LabTestResultStatus.COMPLETED, {text: "Completed", color: new ColorValue("black", colorPastelBlue)}),
  $KTM(LabTestResultStatus.VERIFIED, {text: "Verified", color: new ColorValue("black", colorPastelGreen)}),
  $KTM(LabTestResultStatus.CANCELED, {text: "Canceled", color: new ColorValue("black", colorPastelRed)}),
]);

@JsonObject()
export class LabTestResult extends BasePracticeListItem {

  // LabTestResult.id matches LabTest.id

  @JsonProperty()
  status: LabTestResultStatus = LabTestResultStatus.INCOMPLETE;

  labTest: LabTest;

  @JsonProperty()
  readonly labTestTemplateIndex: number;

  @JsonProperty()
  values: any = {};

  // overrideTemplate is only used during creating a new template.
  static createNewForLabTest(labTest: LabTest, overrideTemplate?: LabTestTemplate): LabTestResult {
    if (!overrideTemplate && !(labTest.templates?.length > 0)) {
      throw new Error("No templates defined for lab test.");
    }
    const result = new LabTestResult(labTest.id, Boolean(overrideTemplate) ? -1 : labTest.templates.length - 1, overrideTemplate);
    result.labTest = labTest;
    return result;
  }

  constructor(labTestId: string, labTestTemplateIndex: number, readonly overrideTemplate?: LabTestTemplate) {
    super(labTestId);
    this.labTestTemplateIndex = labTestTemplateIndex;
  }
}

export class LabTestResults extends BasePracticeListItemsLoader<LabTestResult> {

  constructor(practiceId: string, readonly labOrderId: string) {
    super(practiceId);
  }

  protected practiceDataBasePath(): string {
    return `labTestResults/${this.labOrderId}`;
  }

  protected async onAfterItemDeserialized(item: LabTestResult, val?: any): Promise<LabTestResult> {
    item = await super.onAfterItemDeserialized(item, val);
    item.labTest = await PracticeData.provider().labTests.getOrLoadItem(item.id);
    return item;
  }

  protected deserializeItem(value: any): LabTestResult {
    return JSON_OBJECT.deserializeObject(value, LabTestResult);
  }

  protected serializeItem(item: LabTestResult): any {
    return JSON_OBJECT.serializeObject(item);
  }

  protected sortOrder(item1: LabTestResult, item2: LabTestResult): number {
    return 0;
  }
}

const LabTestResultOrder = {
  [LabTestResultStatus.INCOMPLETE]: 0,
  [LabTestResultStatus.COMPLETED]: 10,
  [LabTestResultStatus.VERIFIED]: 20,
  [LabTestResultStatus.CANCELED]: -1,
};

export class LabReport {

  private readonly loader: LabTestResults;

  readonly labTests: LabTest[] = [];
  readonly labTestResults: LabTestResult[] = [];

  private _labOrder: LabOrder;

  static async createAndLoad(labOrderId: string): Promise<LabReport> {
    const report = new LabReport(labOrderId);
    await report.load();
    return report;
  }

  constructor(readonly labOrderId: string) {
    this.loader = new LabTestResults(PracticeData.provider().practiceId, labOrderId);
  }

  labOrder(): LabOrder | null {
    if (this._labOrder === undefined) {
      throw new Error("Must call load() first");
    }
    return this._labOrder;
  }

  getStatus(): LabTestResultStatus {
    return this.labTestResults.reduce((status, result) => {
      return Object.keys(LabTestResultOrder).find(key => LabTestResultOrder[key] === Math.min(LabTestResultOrder[status], LabTestResultOrder[result.status])) as LabTestResultStatus;
    }, LabTestResultStatus.VERIFIED)
  }

  async load(): Promise<void> {
    const labTests = PracticeData.provider().labTests;
    const labOrder = await PracticeData.provider().labOrders.getOrLoadItem(this.labOrderId);
    if (labOrder) {
      for (const testId of labOrder.testIds) {
        const labTest = await labTests.getOrLoadItem(testId);
        this.labTests.push(labTest);
        this.labTestResults.push((await this.loader.getOrLoadItem(testId)) || LabTestResult.createNewForLabTest(labTest));
      }
      this._labOrder = labOrder;
    } else {
      this._labOrder = null;
    }
  }

  async save(labTestId?: string): Promise<void> {
    const labOrder = await PracticeData.provider().labOrders.getOrLoadItem(this.labOrderId);
    if (labOrder) {
      for (const result of this.labTestResults) {
        if (!labTestId || (labTestId === result.id)) {
          await this.loader.addListItem(result);
        }
      }
    }
  }
}

@JsonObject()
export class LabReportFile {

  @JsonProperty()
  readonly url: string;

  static createNew(url: string): LabReportFile {
    return new LabReportFile(url);
  }

  constructor(url: string) {
    this.url = url;
  }
}

export enum LabOrderBillingStatus {
  DUE = "due",
  PAID = "paid",
}

export type LabOrderBillingMetadata = {
  text: string,
  color: ColorValue,
}

export const LAB_ORDER_BILLING_KTM = new KeyMetadatas<LabOrderBillingMetadata>([
  $KTM(LabOrderBillingStatus.DUE, {text: "Due", color: new ColorValue("white", colorRed)}),
  $KTM(LabOrderBillingStatus.PAID, {text: "Paid", color: new ColorValue("white", colorDarkGreen)}),
]);

@JsonObject()
export class LabOrder extends BasePracticeRecordListItem {

  transactions: Transaction[];

  labTests: LabTest[];

  labReport: LabReport;

  @JsonProperty()
  testIds: string[];

  @JsonProperty()
  file: LabReportFile;

  static createNew(src: RecordSource): LabOrder {
    const labOrder = new LabOrder(md5_uuid().substring(0, 8).toUpperCase(), src);
    return labOrder;
  }

  description(): string {
    return this.labTests?.map(test => test.name).join(", ") || "-";
  }

  getAmountTotal(): number {
    return this.transactions?.filter(transaction => transaction.getSignedAmount() >= 0)
      .reduce((amount, transaction) => amount + transaction.getSignedAmount(), 0);
  }

  getAmountTotalText(): string {
    return CurrencyText(this.getAmountTotal());
  }

  getAmountPaid(): number {
    return this.transactions?.filter(transaction => transaction.getSignedAmount() < 0)
      .reduce((amount, transaction) => amount - transaction.getSignedAmount(), 0);
  }

  getAmountPaidText(): string {
    return CurrencyText(this.getAmountPaid());
  }

  getAmountDue(): number {
    return this.getAmountTotal() - this.getAmountPaid();
  }

  getAmountDueText(): string {
    return CurrencyText(this.getAmountDue());
  }

  getBillingStatus(): LabOrderBillingStatus {
    return this.getAmountDue() <= 0 ? LabOrderBillingStatus.PAID : LabOrderBillingStatus.DUE;
  }

  async onAfterItemDeserialized(): Promise<void> {
    await super.onAfterItemDeserialized();
    this.transactions = await PracticeData.provider().transactions.loadTransactionsByLabOrderId(this.id);
    const labTests: LabTest[] = [];
    if (this.testIds) {
      for (const id of this.testIds) {
        labTests.push(await PracticeData.provider().labTests.getOrLoadItem(id));
      }
    }
    this.labTests = labTests.filter(Boolean);
  }
}

export class LabOrders extends BasePracticeRecordListItemsLoader<LabOrder> {

  constructor(practiceId: string) {
    super(practiceId);
  }

  protected practiceDataBasePath(): string {
    return "labOrders";
  }

  protected deserializeItem(value: any): LabOrder {
    return JSON_OBJECT.deserializeObject(value, LabOrder);
  }

  protected serializeItem(item: LabOrder): any {
    return JSON_OBJECT.serializeObject(item);
  }

  protected sortOrder(item1: LabOrder, item2: LabOrder): number {
    return item2.created - item1.created;
  }
}

export const labOrderAutocompleteLoader = (): Promise<KeyTextString[]> => {
  const labOrders = PracticeData.provider().labOrders;
  return new Promise<KeyTextString[]>((resolve, reject) => {
    labOrders.getOrLoadListItems()
      .then(labOrder => resolve(labOrder.map(labOrder => $KTS(labOrder.id, `${DateUtil.formatDateTime(labOrder.created)} ${labOrder.src.patient.displayName()}`))))
      .catch(reason => reject(reason));
  });
}

@JsonObject()
export class LabOrderItem {

  @JsonProperty()
  readonly name: string;

  @JsonProperty()
  readonly amount: number;

  constructor(name: string, amount: number) {
    this.name = name;
    this.amount = amount;
  }
}

@JsonObject()
export class LabOrderBillData {

  @JsonProperty()
  readonly name: string;

  readonly patientId: string;

  @JsonProperty()
  transactionIds: string[];

  @JsonProperty()
  readonly labOrderItems: LabOrderItem[] = [];

  @JsonProperty()
  amount: number;

  @JsonProperty()
  paid: number;

  @JsonProperty()
  due: number;

  transactions: Transaction[];

  static createNew(patientId: string, labOrder: LabOrder): LabOrderBillData {
    return new LabOrderBillData(
      patientId,
      labOrder.labTests.map(test => new LabOrderItem(test.name, test.amount)),
      labOrder.transactions);
  }

  constructor(patientId: string, labOrderItems: LabOrderItem[], transactions: Transaction[]) {
    this.name = `Bill-${DateUtil.toDatestamp(new Date())}`;
    this.patientId = patientId;
    this.labOrderItems = labOrderItems;
    this.transactionIds = transactions?.map(transaction => transaction.id);
    this.transactions = transactions;
  }

  calculate(): void {
    let paid = 0, amount = 0, due = 0;
    const transactionIds: string[] = [];
    if (this.transactions?.length > 0) {
      // Sort oldest to newest.
      const transactions = [...this.transactions];
      transactions.sort((a, b) => a.transactionAt - b.transactionAt);
      let index = 0;
      for (const transaction of transactions) {
        const signedAmount = transaction.getSignedAmount();
        if (signedAmount > 0) {
          amount += signedAmount;
        } else {
          paid += -signedAmount;
        }
        due += signedAmount;
        transactionIds.push(transaction.id);
        index++;
      }
    }
    this.paid = paid;
    this.amount = amount;
    this.due = due;
    this.transactionIds = transactionIds;
  }
}

@JsonObject()
export class LabOrderBill extends BasePracticeListItem {

  patient: PatientProfile;

  @JsonProperty()
  readonly data: LabOrderBillData;

  @JsonProperty()
  readonly fileUrl: string;

  static createNew(data: LabOrderBillData, fileUrl: string): LabOrderBill {
    return new LabOrderBill(md5_uuid(), data, fileUrl);
  }

  constructor(id: string, data: LabOrderBillData, fileUrl: string) {
    super(id);
    this.data = data;
    this.fileUrl = fileUrl;
  }
}

/// Documents

export enum DocumentCabinet {
  DOCUMENTS = "documents",
  TRASH = "trash",
  ARCHIVE = "archive",
}

@JsonObject()
export class DocumentFile {

  @JsonProperty()
  readonly url: string;

  @JsonProperty()
  readonly created: number;

  @JsonProperty()
  readonly mimeType: string;

  @JsonProperty()
  readonly fileSize: number;

  @JsonProperty()
  name: string;

  constructor(url: string, created: number, mimeType: string, fileSize: number, name?: string) {
    this.url = url;
    this.created = created;
    this.mimeType = mimeType;
    this.fileSize = fileSize;
    this.name = name;
  }
}

@JsonObject()
export class Document extends BasePracticeRecordListItem {

  @JsonProperty()
  cabinet: DocumentCabinet = DocumentCabinet.DOCUMENTS;

  @FormGen({name: "Name", type: "string", required: true})
  @JsonProperty()
  name: string;

  @JsonProperty()
  readonly files: DocumentFile[] = [];

  static createNew(src: RecordSource): Document {
    return new Document(md5_uuid(), src);
  }
}

export class Documents extends BasePracticeRecordListItemsLoader<Document> {

  constructor(practiceId: string) {
    super(practiceId);
  }

  protected practiceDataBasePath(): string {
    return "documents";
  }

  protected deserializeItem(value: any): Document {
    return JSON_OBJECT.deserializeObject(value, Document);
  }

  protected serializeItem(item: Document): any {
    return JSON_OBJECT.serializeObject(item);
  }

  protected sortOrder(item1: Document, item2: Document): number {
    return item2.created - item1.created;
  }
}

export function DocumentCabinetFilter(cabinet: DocumentCabinet): Filter<Document> {
  return (item: Document) => !item.cabinet || (item.cabinet === cabinet);
}


/// Consults

export class PresetSymptomType extends Preset {

  constructor(readonly name: string) {
    super(StringUtil.toSlug(name));
  }

  toString(): string {
    return this.name;
  }
}

function $PRST(name: string): PresetSymptomType {
  return new PresetSymptomType(name);
}

const SYMPTOM_TYPE_PRESETS = new DefinedPresets<PresetSymptomType>(new PresetsKey(PresetListKey.SYMPTOM_TYPES), [
  $PRST("Fever"),
  $PRST("Cough"),
  $PRST("Cold"),
  $PRST("Chest Congestion"),
  $PRST("Constipation"),
  $PRST("Dry Skin"),
  $PRST("Anxiety"),
  $PRST("Frequent Bowel Movements"),
  $PRST("Hot Flashes"),
  $PRST("Rash in Children"),
  $PRST("Vomiting"),
  $PRST("Arm Weakness"),
  $PRST("Abdominal Distention"),
  $PRST("Arrhythmia"),
  $PRST("Abdominal Bloating"),
  $PRST("Ankle Pain"),
  $PRST("Abdominal Pain in Children"),
  $PRST("Abnormal Gait"),
  $PRST("Breast Rash"),
  $PRST("Burning Eyes"),
  $PRST("Black Fungus"),
  $PRST("Black Spots on Legs"),
  $PRST("Breast Lumps"),
  $PRST("Bad Breath"),
  $PRST("Bleeding Gums"),
  $PRST("Blurred Vision"),
  $PRST("Breast Cyst"),
  $PRST("Black Spots on Nails"),
  $PRST("Breast Pain (Mastalgia)"),
  $PRST("Bitter Taste in Mouth"),
  $PRST("Bad Taste in Mouth"),
  $PRST("Bruxism"),
  $PRST("Black Eye"),
  $PRST("Bladder Problems in Men"),
  $PRST("Bleeding Inside Brain"),
  $PRST("Bump on Head"),
  $PRST("Chest Infection"),
  $PRST("Cholecystitis"),
  $PRST("Cerebral Atrophy"),
  $PRST("Celiac Disease (Gluten Intolerance)"),
  $PRST("Cold Allergy"),
  $PRST("Chapped Lips (Cheilitis)"),
  $PRST("Chills"),
  $PRST("Calf Pain"),
  $PRST("Cold Sweats"),
  $PRST("Canker Sores"),
  $PRST("Cold Hands"),
  $PRST("Cystic Acne"),
  $PRST("Chest Pressure"),
  $PRST("Crohn's Disease"),
  $PRST("Cervicogenic Headache"),
  $PRST("Clitoris Pain"),
  $PRST("Chills Without Fever"),
  $PRST("Coughing up Phlegm"),
  $PRST("Dysuria"),
  $PRST("Dark Urine"),
  $PRST("Difference between single and twin pregnancy symptoms"),
  $PRST("Drooling"),
  $PRST("Dry Eyes"),
  $PRST("Diarrhoea in Children"),
  $PRST("Ear Pain"),
  $PRST("Excessive Sweating"),
  $PRST("Ear Swelling"),
  $PRST("Eye Twitching"),
  $PRST("Encephalopathy"),
  $PRST("Enlarged Prostate"),
  $PRST("Ear Bleeding"),
  $PRST("Epigastric Pain"),
  $PRST("Forearm Pain"),
  $PRST("Fatty Liver"),
  $PRST("Frozen Shoulder"),
  $PRST("Face Swelling"),
  $PRST("Fainting"),
  $PRST("Food Poisoning"),
  $PRST("Flank Pain"),
  $PRST("Facial Paralysis"),
  $PRST("Glaucoma"),
  $PRST("Growth Delays in Children"),
  $PRST("Gastritis"),
  $PRST("Gout"),
  $PRST("Hand Pain"),
  $PRST("High Eosinophils"),
  $PRST("Heart Blockage"),
  $PRST("High Cholesterol Levels"),
  $PRST("Hyperglycemia (High Blood Sugar)"),
  $PRST("Heel Pain"),
  $PRST("Haemorrhoids"),
  $PRST("Hand Foot and Mouth Disease"),
  $PRST("Hoarseness"),
  $PRST("High Cortisol Levels in Females"),
  $PRST("Hip Pain"),
  $PRST("Hypoxemia"),
  $PRST("High TSH"),
  $PRST("Hyperemia"),
  $PRST("Hypothermia"),
  $PRST("High Blood Protein"),
  $PRST("Heart Rate Variability"),
  $PRST("Itchy Eyes"),
  $PRST("Ingrown Toenail"),
  $PRST("Itchy Throat"),
  $PRST("Implantation Bleeding"),
  $PRST("Joint Effusion"),
  $PRST("Kidney Pain"),
  $PRST("Left Side Stomach Pain"),
  $PRST("Low Urine Output"),
  $PRST("Liver Swelling"),
  $PRST("Loss of Appetite"),
  $PRST("Left Side Chest Pain"),
  $PRST("Light-Headedness or Dizziness"),
  $PRST("Lymphoma"),
  $PRST("Loss of Smell"),
  $PRST("Lump Under the Armpit"),
  $PRST("Loss of Taste"),
  $PRST("Lump on Neck"),
  $PRST("Lymphocytosis"),
  $PRST("Low Levels of Neutrophils"),
  $PRST("Lump in the Throat"),
  $PRST("Lump in the Groin"),
  $PRST("Miscarriage"),
  $PRST("Muscle Stiffness"),
  $PRST("Mood Swings"),
  $PRST("Muscle Cramps"),
  $PRST("Muscle Pain"),
  $PRST("Malaria"),
  $PRST("Metallic Taste in Mouth"),
  $PRST("Magnesium Deficiency"),
  $PRST("Neck Swelling"),
  $PRST("Nipple Itching"),
  $PRST("Night Sweats"),
  $PRST("Numbness in Hand"),
  $PRST("Nocturia"),
  $PRST("Nitrite in Urine"),
  $PRST("Omega 3 Deficiency Symptoms"),
  $PRST("Ovarian Cancer"),
  $PRST("Oral Thrush (Oropharyngeal Candidiasis)"),
  $PRST("Obsessive-Compulsive Disorder (OCD)"),
  $PRST("Ocular Hypertension"),
  $PRST("Occipital Neuralgia"),
  $PRST("Oesophageal Varices"),
  $PRST("Ovary Pain"),
  $PRST("Orthostatic Hypotension"),
  $PRST("Puffy Eyes"),
  $PRST("Pain in Back of Head"),
  $PRST("Pale Skin"),
  $PRST("Protein in Urine"),
  $PRST("Pedal Edema"),
  $PRST("Pelvic Pain"),
  $PRST("Psoriasis Symptoms"),
  $PRST("Paralytic Attack"),
  $PRST("Painful Swallowing (Odynophagia)"),
  $PRST("Postnasal Drip (PND)"),
  $PRST("Paranoia"),
  $PRST("Panic Attacks"),
  $PRST("Photophobia"),
  $PRST("Pale Stool"),
  $PRST("Painful Bowel Movements"),
  $PRST("Pus in Ear"),
  $PRST("Paraplegia"),
  $PRST("Polyuria"),
  $PRST("Pain in Back of Knee"),
  $PRST("Rib Pain"),
  $PRST("Right Side Chest Pain"),
  $PRST("Round Ligament Pain"),
  $PRST("Red Eyes"),
  $PRST("Runny Nose"),
  $PRST("Rheumatic Fever"),
  $PRST("Right Upper Quadrant Pain"),
  $PRST("Regurgitation"),
  $PRST("Slurred Speech"),
  $PRST("Splinter Hemorrhages"),
  $PRST("Sore Tongue"),
  $PRST("Skin Discolouration"),
  $PRST("Swollen Lips"),
  $PRST("Shortness of Breath"),
  $PRST("Sepsis"),
  $PRST("Slipped Disc"),
  $PRST("Stomach Cancer"),
  $PRST("Skin Peeling"),
  $PRST("Shingles"),
  $PRST("Sleep Disorders"),
  $PRST("Snoring"),
  $PRST("Swollen Tongue"),
  $PRST("Sneezing"),
  $PRST("Stye"),
  $PRST("Slow Heart Rate"),
  $PRST("Skin Redness"),
  $PRST("Strep Throat"),
  $PRST("Swollen Eyelid"),
  $PRST("Swollen Knee"),
  $PRST("Swelling in the Body"),
  $PRST("Throat Cancer"),
  $PRST("Thigh Pain"),
  $PRST("Tachycardia"),
  $PRST("Unexplained Weight Loss"),
  $PRST("Uric Acid Symptoms"),
  $PRST("Upper Back Pain"),
  $PRST("Umbilical Hernia"),
  $PRST("Uterine Fibroids"),
  $PRST("Uterine Prolapse"),
  $PRST("Uterine Cancer (Endometrial Cancer)"),
  $PRST("Ulcerative Colitis"),
  $PRST("Vomiting blood"),
  $PRST("Vitamin B12 deficiency"),
  $PRST("Viral Infection"),
  $PRST("Vertigo"),
  $PRST("Vaginal Dryness"),
  $PRST("Vaginal Discharge"),
  $PRST("Vaginal Itching"),
  $PRST("White Tongue"),
  $PRST("Watery Eyes"),
  $PRST("Wheezing"),
  $PRST("Water Retention"),
  $PRST("Wrist Pain"),
  $PRST("Wet Cough"),
  $PRST("Weakness"),
  $PRST("Yellow Tongue"),
  $PRST("Yellow Skin"),
  $PRST("Yellow Vomit"),
]);

PRESET_LIST_KTM.add(
  $KTM(PresetListKey.SYMPTOM_TYPES, {type: "defined", definedPresets: SYMPTOM_TYPE_PRESETS}),
);

export class PresetFindingType extends Preset {

  constructor(readonly name: string) {
    super(StringUtil.toSlug(name));
  }

  toString(): string {
    return this.name;
  }
}

function $PRFT(name: string): PresetFindingType {
  return new PresetFindingType(name);
}

const FINDING_TYPE_PRESETS = new DefinedPresets<PresetFindingType>(new PresetsKey(PresetListKey.FINDING_TYPES), [
  $PRFT("Congested throat"),
  $PRFT("Soft abdomen"),
  $PRFT("Wheezing"),
  $PRFT("Acne"),
  $PRFT("Cavities"),
  $PRFT("Diabetes"),
  $PRFT("Depression"),
  $PRFT("Flu"),
  $PRFT("Allergies in Children"),
  $PRFT("Leg Pain"),
  $PRFT("Acute Bronchitis"),
  $PRFT("Amenorrhea"),
  $PRFT("Anal Fissures"),
  $PRFT("Angina"),
  $PRFT("Allergic Rhinitis"),
  $PRFT("Amnesia"),
  $PRFT("Abnormal Uterine Bleeding"),
  $PRFT("Arthritis"),
  $PRFT("Asthma in Children"),
  $PRFT("Appendicitis in Children"),
  $PRFT("Anaphylaxis"),
  $PRFT("Ascites"),
  $PRFT("Alopecia Areata"),
  $PRFT("Aortic Stenosis"),
  $PRFT("Atherosclerosis"),
  $PRFT("Atrial Fibrillation"),
  $PRFT("Amblyopia"),
  $PRFT("Amyloidosis"),
  $PRFT("Age Spots"),
  $PRFT("Astrocytoma"),
  $PRFT("Acrocyanosis"),
  $PRFT("Avascular Necrosis (Osteonecrosis)"),
  $PRFT("Arthrodesis (Joint Fusion)"),
  $PRFT("Adenomyosis"),
  $PRFT("Abscess"),
  $PRFT("Bladder Infection"),
  $PRFT("Burning Mouth Syndrome"),
  $PRFT("Broken Ankle"),
  $PRFT("Balanitis"),
  $PRFT("Baker's Cyst"),
  $PRFT("Bacterial Vaginosis (BV)"),
  $PRFT("Bedwetting"),
  $PRFT("Black Stools"),
  $PRFT("Boils on Skin"),
  $PRFT("Blepharospasm"),
  $PRFT("Broken Arm"),
  $PRFT("Blood in Semen"),
  $PRFT("Brown Discharge"),
  $PRFT("Broken Nose"),
  $PRFT("Bleeding Diathesis"),
  $PRFT("Bedsores"),
  $PRFT("Benign Tumour"),
  $PRFT("Cholecystitis"),
  $PRFT("Cirrhosis"),
  $PRFT("Chronic Kidney Disease (CKD)"),
  $PRFT("Cholangitis"),
  $PRFT("Croup Cough"),
  $PRFT("Cervical Dysplasia"),
  $PRFT("Colitis"),
  $PRFT("Cervical Dystonia"),
  $PRFT("Club Foot Disease"),
  $PRFT("Colour Blindness"),
  $PRFT("Crystals in Urine"),
  $PRFT("Costochondritis"),
  $PRFT("Cracked Heels"),
  $PRFT("Cystinuria"),
  $PRFT("Cardiomyopathy"),
  $PRFT("Chronic Cough"),
  $PRFT("Cholesteatoma"),
  $PRFT("Corneal Ulcer"),
  $PRFT("Carpal Tunnel Syndrome"),
  $PRFT("Chlamydia Infection"),
  $PRFT("Cholera"),
  $PRFT("Cervicitis"),
  $PRFT("Corns on Feet"),
  $PRFT("Cerebral Venous Sinus Thrombosis"),
  $PRFT("Double Vision"),
  $PRFT("Dementia"),
  $PRFT("Dysmenorrhea"),
  $PRFT("Dystonia"),
  $PRFT("Diabetes in Pregnancy"),
  $PRFT("Dislocation"),
  $PRFT("Diabetes Insipidus"),
  $PRFT("Delirium"),
  $PRFT("Diverticulitis"),
  $PRFT("Diabetic Ketoacidosis"),
  $PRFT("Diabetic Neuropathy"),
  $PRFT("Diabetic Retinopathy"),
  $PRFT("Dupuytren's Contracture"),
  $PRFT("Ectopic Pregnancy"),
  $PRFT("Endometriosis"),
  $PRFT("Eating Disorder"),
  $PRFT("Endometrial Hyperplasia"),
  $PRFT("Eye Stroke"),
  $PRFT("Eye Allergies"),
  $PRFT("Endocarditis"),
  $PRFT("Emphysema"),
  $PRFT("Eosinophilia"),
  $PRFT("Epicondylitis"),
  $PRFT("Flatulence"),
  $PRFT("Fungal Infections"),
  $PRFT("Fungal Skin Infections"),
  $PRFT("Fungal Nail Infection"),
  $PRFT("Female Infertility"),
  $PRFT("Folliculitis"),
  $PRFT("Gastrointestinal Infections"),
  $PRFT("Groin Pain"),
  $PRFT("Gastrointestinal Bleeding"),
  $PRFT("Gonorrhoea"),
  $PRFT("Glomerulonephritis"),
  $PRFT("Graves Disease"),
  $PRFT("Guillain-Barre Syndrome"),
  $PRFT("Hip Bursitis"),
  $PRFT("Hernia"),
  $PRFT("Herpes"),
  $PRFT("Hearing Loss"),
  $PRFT("Hydrocephalus"),
  $PRFT("Hydronephrosis"),
  $PRFT("Hypoglycaemia"),
  $PRFT("Hypercoagulation"),
  $PRFT("Hyperparathyroidism"),
  $PRFT("Heart Palpitations"),
  $PRFT("Hand Fractures"),
  $PRFT("Hiatal Hernia"),
  $PRFT("Hyperkalemia"),
  $PRFT("Hydrocele"),
  $PRFT("Hyperprolactinemia"),
  $PRFT("Haemophilia"),
  $PRFT("Haemolytic Uremic Syndrome"),
  $PRFT("Herpangina"),
  $PRFT("Head and Neck Hemangioma"),
  $PRFT("Hypercalcemia"),
  $PRFT("Hyperthyroidism"),
  $PRFT("Itching"),
  $PRFT("Inguinal Hernia"),
  $PRFT("Interstitial Cystitis"),
  $PRFT("Intracranial Pressure"),
  $PRFT("Immune Thrombocytopenia"),
  $PRFT("Iodine Deficiency"),
  $PRFT("Kidney Cysts"),
  $PRFT("Knock Knees"),
  $PRFT("Knee Ligament Injury"),
  $PRFT("Leprosy (Hansen's Disease)"),
  $PRFT("Left Ventricular Hypertrophy"),
  $PRFT("Long QT Syndrome"),
  $PRFT("Low Sperm Motility"),
  $PRFT("Mitral Valve Prolapse"),
  $PRFT("Meningitis"),
  $PRFT("Myositis"),
  $PRFT("Myopia"),
  $PRFT("NASH (Nonalcoholic Steatohepatitis)"),
  $PRFT("Nasal Polyps"),
  $PRFT("Narcolepsy"),
  $PRFT("Osteomyelitis"),
  $PRFT("Osteoarthritis (OA)"),
  $PRFT("Onychomycosis (Nail Fungus)"),
  $PRFT("Oesophagitis"),
  $PRFT("Osteosarcoma"),
  $PRFT("Osteomalacia"),
  $PRFT("Osteopenia"),
  $PRFT("Pulmonary Hypertension"),
  $PRFT("Pericarditis"),
  $PRFT("Perimenopause"),
  $PRFT("Paronychia"),
  $PRFT("Pituitary Tumour"),
  $PRFT("Pleural Effusion"),
  $PRFT("Polycystic Kidney Disease"),
  $PRFT("Pulmonary Oedema"),
  $PRFT("Plantar Fasciitis"),
  $PRFT("Pulmonary Embolism"),
  $PRFT("Panic Attacks"),
  $PRFT("Patellar Tendinopathy"),
  $PRFT("Rabies"),
  $PRFT("Restless Legs Syndrome"),
  $PRFT("Rotator Cuff Tendinitis"),
  $PRFT("Scalp Psoriasis"),
  $PRFT("Sleep Paralysis"),
  $PRFT("Skin Ulcers"),
  $PRFT("Skin Tag"),
  $PRFT("Sarcoidosis"),
  $PRFT("Sarcomas"),
  $PRFT("Spondylolisthesis"),
  $PRFT("Shoulder Bursitis"),
  $PRFT("Scabies"),
  $PRFT("Tapeworm Infection"),
  $PRFT("Tinnitus"),
  $PRFT("Tailbone Pain"),
  $PRFT("Triple Negative Breast Cancer"),
  $PRFT("Tonsil Stones"),
  $PRFT("Torn Meniscus"),
  $PRFT("Typhoid"),
  $PRFT("Uremia"),
  $PRFT("Ulcerative Colitis"),
  $PRFT("Ureteral Stones"),
  $PRFT("Vitiligo"),
  $PRFT("Varicocele"),
  $PRFT("Vitamin D Deficiency"),
  $PRFT("Ventral Hernia"),
  $PRFT("Vesicoureteral Reflux"),
]);

PRESET_LIST_KTM.add(
  $KTM(PresetListKey.FINDING_TYPES, {type: "defined", definedPresets: FINDING_TYPE_PRESETS}),
);

export class PresetDiagnosisType extends Preset {

  constructor(readonly name: string) {
    super(StringUtil.toSlug(name));
  }

  toString(): string {
    return this.name;
  }
}

function $PRDT(name: string): PresetDiagnosisType {
  return new PresetDiagnosisType(name);
}

const DIAGNOSIS_TYPE_PRESETS = new DefinedPresets<PresetDiagnosisType>(new PresetsKey(PresetListKey.DIAGNOSIS_TYPES), [
  $PRDT("COVID-19"),
]);

PRESET_LIST_KTM.add(
  $KTM(PresetListKey.DIAGNOSIS_TYPES, {type: "defined", definedPresets: DIAGNOSIS_TYPE_PRESETS}),
);

export enum MedicineForm {
  TAB = "tab",
  CAP = "cap",
}

export class PresetMedicineType extends Preset {

  constructor(readonly name: string, readonly form: MedicineForm) {
    super(StringUtil.toSlug(name));
  }

  toString(): string {
    return this.name;
  }
}

export enum ConsultSection {
  SYMPTOMS = "symptoms",
  FINDINGS = "findings",
  DIAGNOSIS = "diagnosis",
  MEDICINES = "medicines",
  LABTESTS = "labtests",
}

export type ConsultSectionMetadata = {
  text: string,
  presetListKey: PresetListKey,
}

export const CONSULT_SECTION_METADATAS = new KeyMetadatas<ConsultSectionMetadata>([
  $KTM(ConsultSection.SYMPTOMS, {
    text: "Symptoms",
    presetListKey: PresetListKey.SYMPTOM_TYPES,
  }),
  $KTM(ConsultSection.FINDINGS, {
    text: "Findings",
    presetListKey: PresetListKey.FINDING_TYPES,
  }),
  $KTM(ConsultSection.DIAGNOSIS, {
    text: "Diagnosis",
    presetListKey: PresetListKey.DIAGNOSIS_TYPES,
  }),
  $KTM(ConsultSection.MEDICINES, {
    text: "Medicines",
    presetListKey: PresetListKey.POPULAR_MEDICINE_TYPES,
  }),
  $KTM(ConsultSection.LABTESTS, {
    text: "Lab Tests",
    presetListKey: PresetListKey.POPULAR_LABTEST_TYPES,
  }),
]);

export enum ConditionSeverity {
  MILD = "mild",
  MODERATE = "moderate",
  SEVERE = "severe",
}

const CONDITION_SEVERITY_KTS = new KeyTextStrings([
  $KTS(ConditionSeverity.MILD, "Mild"),
  $KTS(ConditionSeverity.MODERATE, "Moderate"),
  $KTS(ConditionSeverity.SEVERE, "Severe"),
]);

export enum ConditionSince {
  UNSURE = "unsure",
  DAYS_1 = "days_1",
  DAYS_2 = "days_2",
  DAYS_3 = "days_3",
  DAYS_4 = "days_4",
  DAYS_5 = "days_5",
  DAYS_6 = "days_6",
  WEEKS_1 = "weeks_1",
  WEEKS_2 = "weeks_1",
  DAYS_10 = "days_10",
  MONTHS_1 = "months_1",
  MONTHS_2 = "months_2",
  MONTHS_3 = "months_3",
  MONTHS_6 = "months_6",
  YEARS_1 = "years_1",
  YEARS_MANY = "years_many",
}

const CONDITION_SINCE_KTS = new KeyTextStrings([
  $KTS(ConditionSince.UNSURE, "Unsure"),
  $KTS(ConditionSince.DAYS_1, "1 Day"),
  $KTS(ConditionSince.DAYS_2, "2 Days"),
  $KTS(ConditionSince.DAYS_3, "3 Days"),
  $KTS(ConditionSince.DAYS_4, "4 Days"),
  $KTS(ConditionSince.DAYS_5, "5 Days"),
  $KTS(ConditionSince.DAYS_6, "6 Days"),
  $KTS(ConditionSince.WEEKS_1, "1 Week"),
  $KTS(ConditionSince.DAYS_10, "10 Days"),
  $KTS(ConditionSince.WEEKS_2, "2 Weeks"),
  $KTS(ConditionSince.MONTHS_1, "1 Month"),
  $KTS(ConditionSince.MONTHS_2, "2 Months"),
  $KTS(ConditionSince.MONTHS_3, "3 Months"),
  $KTS(ConditionSince.MONTHS_6, "6 Months"),
  $KTS(ConditionSince.YEARS_1, "1 Year"),
  $KTS(ConditionSince.YEARS_MANY, "Many Years"),
]);

@JsonObject()
export class ConsultSymptom {

  @JsonProperty()
  readonly key: string;

  @FormGen({
    name: "Note",
    type: "text",
    size: "xsmall",
  })
  @JsonProperty()
  note: string;

  @FormGen({
    name: "Since",
    type: "enum",
    enumValues: CONDITION_SINCE_KTS.values,
  })
  @JsonProperty()
  since: string;

  @FormGen({
    name: "Severity",
    type: "enum",
    enumValues: CONDITION_SEVERITY_KTS.values,
    enumVariant: "radio",
  })
  @JsonProperty()
  severity: string;

  constructor(key: string) {
    this.key = key;
  }

  toStringExtras(): string {
    return [
      this.note,
      this.since ? `Since ${CONDITION_SINCE_KTS.findText(this.since)}` : null,
      this.severity ? `Severity: ${CONDITION_SEVERITY_KTS.findText(this.severity)}` : null,
    ].filter(Boolean).join(", ");
  }
}

@JsonObject()
export class ConsultFinding {

  @JsonProperty()
  readonly key: string;

  @FormGen({
    name: "Note",
    type: "text",
    size: "xsmall",
  })
  @JsonProperty()
  note: string;

  @FormGen({
    name: "Since",
    type: "enum",
    enumValues: CONDITION_SINCE_KTS.values,
  })
  @JsonProperty()
  since: string;

  @FormGen({
    name: "Severity",
    type: "enum",
    enumValues: CONDITION_SEVERITY_KTS.values,
    enumVariant: "radio",
  })
  @JsonProperty()
  severity: string;

  constructor(key: string) {
    this.key = key;
  }

  toStringExtras(): string {
    return [
      this.note,
      this.since ? `Since ${CONDITION_SINCE_KTS.findText(this.since)}` : null,
      this.severity ? `Severity: ${CONDITION_SEVERITY_KTS.findText(this.severity)}` : null,
    ].filter(Boolean).join(", ");
  }
}


export enum DiagnosisDescription {
  RULE_OUT = "rule_out",
  SUSPECTED = "suspected",
  FOLLOW_UP = "follow_up",
  OTHER = "other",
}

const DIAGNOSIS_DESCRIPTION_KTS = new KeyTextStrings([
  $KTS(DiagnosisDescription.RULE_OUT, "Rule Out"),
  $KTS(DiagnosisDescription.SUSPECTED, "Suspected"),
  $KTS(DiagnosisDescription.FOLLOW_UP, "Follow Up"),
  $KTS(DiagnosisDescription.OTHER, "Other"),
]);

@JsonObject()
export class ConsultDiagnosis {

  @JsonProperty()
  readonly key: string;

  @FormGen({
    name: "Note",
    type: "text",
    size: "xsmall",
  })
  @JsonProperty()
  note: string;

  @FormGen({
    name: "Location",
    type: "text",
    size: "xsmall",
  })
  @JsonProperty()
  location: string;

  @FormGen({
    name: "Description",
    type: "enum",
    enumValues: DIAGNOSIS_DESCRIPTION_KTS.values,
    enumVariant: "radio",
  })
  @JsonProperty()
  description: string;

  @FormGen({
    name: "Description detail",
    type: "string",
  })
  @JsonProperty()
  descriptionDetail: string;

  constructor(key: string) {
    this.key = key;
  }

  toStringExtras(): string {
    return [
      this.note,
      this.location ? `Location: ${this.location}` : null,
      this.description ? `${DIAGNOSIS_DESCRIPTION_KTS.findText(this.description)} ${this.descriptionDetail ?? "-"}` : null,
    ].filter(Boolean).join(", ");
  }
}

export enum MedicineQuantity {
  U_1 = "u_1",
  U_2 = "u_2",
}

export const MEDICINE_QUANTITY_KTS = new KeyTextStrings([
  $KTS(MedicineQuantity.U_1, "1"),
  $KTS(MedicineQuantity.U_2, "2"),
]);

export enum MedicineFrequency {
  T_4H = "t_4h",
  T_6H = "t_6h",
  T_8H = "t_8h",
  T_10H = "t_10h",
  T_12H = "t_12h",
  T_48H = "t_48h",
  T_1 = "t_1",
  T_2 = "t_2",
  T_3 = "t_3",
  T_4 = "t_4",
  T_5 = "t_5",
}

export const MEDICINE_FREQUENCY_KTS = new KeyTextStrings([
  $KTS(MedicineFrequency.T_4H, "4h"),
  $KTS(MedicineFrequency.T_6H, "6h"),
  $KTS(MedicineFrequency.T_8H, "8h"),
  $KTS(MedicineFrequency.T_10H, "10h"),
  $KTS(MedicineFrequency.T_12H, "12h"),
  $KTS(MedicineFrequency.T_48H, "48h"),
  $KTS(MedicineFrequency.T_1, "Once"),
  $KTS(MedicineFrequency.T_2, "Twice"),
  $KTS(MedicineFrequency.T_3, "Thrice"),
  $KTS(MedicineFrequency.T_4, "4 Times"),
  $KTS(MedicineFrequency.T_5, "5 Times"),
]);

export enum MedicineDuration {
  DAYS_1 = "days_1",
  DAYS_2 = "days_2",
}

export const MEDICINE_DURATION_KTS = new KeyTextStrings([
  $KTS(MedicineDuration.DAYS_1, "1 Day"),
  $KTS(MedicineDuration.DAYS_2, "2 Days"),
]);

@JsonObject()
export class ConsultMedicine {

  @JsonProperty()
  readonly key: string;

  @FormGen({
    name: "Quantity",
    type: "enum",
    enumValues: MEDICINE_QUANTITY_KTS.values,
    enumVariant: "button",
  })
  @JsonProperty()
  quantity: string;

  @FormGen({
    name: "Frequency",
    type: "enum",
    enumValues: MEDICINE_FREQUENCY_KTS.values,
    enumVariant: "button",
  })
  @JsonProperty()
  frequency: string;

  @FormGen({
    name: "Duration",
    type: "enum",
    enumValues: MEDICINE_DURATION_KTS.values,
    enumVariant: "button",
  })
  @JsonProperty()
  duration: string;

  constructor(key: string) {
    this.key = key;
  }
}

@JsonObject()
export class ConsultLabTest {

  @JsonProperty()
  readonly key: string;

  @JsonProperty()
  name: string;

  constructor(key: string) {
    this.key = key;
  }
}

@JsonObject()
export class ConsultPrescriptionNote {

  @FormGen({
    name: "Prescription note",
    type: "text",
    size: "xsmall",
    description: "This is printed on the prescription.",
  })
  @JsonProperty()
  note: string;
}

@JsonObject()
export class ConsultDoctorNote {

  @FormGen({
    name: "Doctor's note",
    type: "text",
    size: "xsmall",
    description: "This is NOT printed.",
  })
  @JsonProperty()
  note: string;
}

@JsonObject()
export class Consult extends BasePracticeListItem {

  @JsonProperty()
  readonly patientId: string;

  @JsonProperty()
  readonly symptoms: ConsultSymptom[] = [];

  @JsonProperty()
  readonly findings: ConsultFinding[] = [];

  @JsonProperty()
  readonly diagnoses: ConsultDiagnosis[] = [];

  @JsonProperty()
  readonly medicines: ConsultMedicine[] = [];

  @JsonProperty()
  readonly labTests: ConsultLabTest[] = [];

  @JsonProperty()
  readonly prescriptionNote: ConsultPrescriptionNote = new ConsultPrescriptionNote();

  @JsonProperty()
  readonly doctorNote: ConsultDoctorNote = new ConsultDoctorNote();

  static createNew(patientId: string): Consult {
    return new Consult(md5_uuid(), patientId);
  }

  constructor(id: string, patientId: string) {
    super(id);
    this.patientId = patientId;
  }

  static readonly PATIENT_ID_REL_NAME = "patientIds";

  patientIdsRel(): Rel {
    return new Rel(Review.PATIENT_ID_REL_NAME, this.patientId, this.id, true, true);
  }
}

export class Consults extends BasePracticeListItemsLoader<Consult> {

  constructor(practiceId: string) {
    super(practiceId);
  }

  protected practiceDataBasePath(): string {
    return "consults";
  }

  async addListItem(item: Consult, ...rels): Promise<void> {
    throw new Error("Use addConsult instead");
  }

  async deleteListItem(item: Consult, ...rels): Promise<void> {
    throw new Error("Use deleteConsult instead");
  }

  async addConsult(consult: Consult): Promise<void> {
    await super.addListItem(consult, consult.patientIdsRel());
  }

  async deleteConsult(consult: Consult): Promise<void> {
    await super.deleteListItem(consult, consult.patientIdsRel());
  }

  protected deserializeItem(value: any): Consult {
    return JSON_OBJECT.deserializeObject(value, Consult);
  }

  protected serializeItem(item: Consult): any {
    return JSON_OBJECT.serializeObject(item);
  }

  protected sortOrder(item1: Consult, item2: Consult): number {
    return item2.created - item1.created;
  }
}

/// Contact Requests

export enum ContactRequestSource {
  MYSMILE = "mysmile",
}

@JsonObject()
export class ContactRequest extends BasePracticeListItem {

  @JsonProperty()
  readonly source: ContactRequestSource;

  @FormGen({
    name: "Name",
    type: "string",
    required: true,
  })
  @JsonProperty()
  name: string;

  @FormGen({
    name: "Email",
    type: "string",
  })
  @JsonProperty()
  email: string;

  @FormGen({
    name: "Phone",
    type: "string",
  })
  @JsonProperty()
  phone: string;

  static createNew(source: ContactRequestSource, name?: string): ContactRequest {
    const request = new ContactRequest(md5_uuid(), source);
    request.name = name;
    return request;
  }

  constructor(id: string, source: ContactRequestSource) {
    super(id);
    this.source = source;
  }
}

export class ContactRequests extends BasePracticeListItemsLoader<ContactRequest> {

  constructor(practiceId: string) {
    super(practiceId);
  }

  protected practiceDataBasePath(): string {
    return "contactRequests";
  }

  protected deserializeItem(value: any): ContactRequest {
    return JSON_OBJECT.deserializeObject(value, ContactRequest);
  }

  protected serializeItem(item: ContactRequest): any {
    return JSON_OBJECT.serializeObject(item);
  }

  protected sortOrder(item1: ContactRequest, item2: ContactRequest): number {
    return item1.name.localeCompare(item2.name);
  }
}

/// Dental Prescriptions

const DENTAL_MEDICATION_DRUG_PRESETS = new DefinedPresets(new PresetsKey(PresetListKey.DENTAL_MEDICATION_DRUGS), [
  $PRTXT0("Paracetamol"),
  $PRTXT0("Anabel"),
  $PRTXT0("Crocin"),
  $PRTXT0("Otrivin Nasal Drops"),
  $PRTXT0("Karvol Inhalations"),
  $PRTXT0("Neurobion"),
  $PRTXT0("Pyrigesic"),
  $PRTXT0("E-Mycin"),
  $PRTXT0("Spasmo-Proxyvon"),
  $PRTXT0("Proxyvon"),
  $PRTXT0("Voveran"),
  $PRTXT0("Clohex Mouthwash"),
  $PRTXT0("Orex-Lo"),
  $PRTXT0("Dologel"),
  $PRTXT0("Emoform Toothpaste"),
  $PRTXT0("Gumex"),
  $PRTXT0("Wymox"),
  $PRTXT0("Clobetesol Cream"),
  $PRTXT0("Sensodent KF Toothpaste"),
  $PRTXT0("Stolin Gum Astringent"),
  $PRTXT0("G - 32 Gum Paint"),
  $PRTXT0("Diclomol"),
  $PRTXT0("Brufen"),
  $PRTXT0("Decdan"),
  $PRTXT0("Neurobion Forte"),
  $PRTXT0("Metrogyl"),
  $PRTXT0("Ciplox"),
  $PRTXT0("Doxy-1"),
  $PRTXT0("Syr. Ibugesic"),
  $PRTXT0("Syr. Mox"),
  $PRTXT0("Mox"),
  $PRTXT0("Tab. Sumo"),
]);

PRESET_LIST_KTM.add(
  $KTM(PresetListKey.DENTAL_MEDICATION_DRUGS, {type: "defined", definedPresets: DENTAL_MEDICATION_DRUG_PRESETS}),
);

const DENTAL_MEDICATION_DURATION_PRESETS = new DefinedPresets(new PresetsKey(PresetListKey.DENTAL_MEDICATION_DURATIONS), [
  $PRTXT("1_day", "1 day"),
  $PRTXT("1_week", "1 week"),
  $PRTXT("1_month", "1 month"),
  $PRTXT("1_year", "1 year"),
]);

PRESET_LIST_KTM.add(
  $KTM(PresetListKey.DENTAL_MEDICATION_DURATIONS, {
    type: "defined",
    definedPresets: DENTAL_MEDICATION_DURATION_PRESETS
  }),
);

const DENTAL_MEDICATION_FREQUENCY_PRESETS = new DefinedPresets(new PresetsKey(PresetListKey.DENTAL_MEDICATION_FREQUENCIES), [
  $PRTXT("prn", "PRN (As needed)"),
  $PRTXT("1-0-0", "1-0-0"),
  $PRTXT("0-1-0", "0-1-0"),
  $PRTXT("0-0-1", "0-0-1"),
  $PRTXT("1-1-1", "1-1-1"),
]);

PRESET_LIST_KTM.add(
  $KTM(PresetListKey.DENTAL_MEDICATION_FREQUENCIES, {
    type: "defined",
    definedPresets: DENTAL_MEDICATION_FREQUENCY_PRESETS
  }),
);

const DENTAL_MEDICATION_MEALSPEC_PRESETS = new DefinedPresets(new PresetsKey(PresetListKey.DENTAL_MEDICATION_MEALSPECS), [
  $PRTXT("after_meal", "After meal"),
  $PRTXT("before_meal", "Before meal"),
]);

PRESET_LIST_KTM.add(
  $KTM(PresetListKey.DENTAL_MEDICATION_MEALSPECS, {
    type: "defined",
    definedPresets: DENTAL_MEDICATION_MEALSPEC_PRESETS
  }),
);

export enum DentalPrescriptionStatus {
  ACTIVE = "active",
  DISCONTINUED = "discontinued",
  INACTIVE = "inactive",
}

export const DENTAL_PRESCRIPTION_STATUS_KTS = new KeyTextStrings([
  $KTS(DentalPrescriptionStatus.ACTIVE, "Active"),
  $KTS(DentalPrescriptionStatus.DISCONTINUED, "Discontinued"),
  $KTS(DentalPrescriptionStatus.INACTIVE, "Inactive"),
]);

export const DENTAL_PRESCRIPTION_STATUS_KTM_COLORS = new KeyMetadatas<ColorValue>([
  $KTM<ColorValue>(DentalPrescriptionStatus.ACTIVE, new ColorValue(colorDarkGreen, colorPastelGreen)),
  $KTM<ColorValue>(DentalPrescriptionStatus.DISCONTINUED, new ColorValue(colorDarkPink, colorPastelRed)),
  $KTM<ColorValue>(DentalPrescriptionStatus.INACTIVE, new ColorValue(white, mediumGray)),
]);

@JsonObject()
export class DentalPrescriptionFile {

  @JsonProperty()
  readonly url: string;

  static createNew(url: string): DentalPrescriptionFile {
    return new DentalPrescriptionFile(url);
  }

  constructor(url: string) {
    this.url = url;
  }
}

@JsonObject()
export class DentalPrescriptionMedication {

  @JsonProperty()
  readonly id: string;

  drugText: string;

  @JsonProperty()
  @FormGen({
    name: "Drug",
    type: "enum",
    required: true,
    _asyncInit: async (metadata: FormGenMetadata) => {
      metadata.enumValues = (await DentwyzePresetListsCache.getInstance().lists.getOrLoadList(PresetListKey.DENTAL_MEDICATION_DRUGS)).values;
    },
  })
  drugId: string;

  frequencyText: string;

  @JsonProperty()
  @FormGen({
    name: "Frequency",
    type: "enum",
    required: true,
    _asyncInit: async (metadata: FormGenMetadata) => {
      metadata.enumValues = (await DentwyzePresetListsCache.getInstance().lists.getOrLoadList(PresetListKey.DENTAL_MEDICATION_FREQUENCIES)).values;
    },
  })
  frequencyId: string;

  durationText: string;

  @JsonProperty()
  @FormGen({
    name: "Duration",
    type: "enum",
    required: true,
    _asyncInit: async (metadata: FormGenMetadata) => {
      metadata.enumValues = (await DentwyzePresetListsCache.getInstance().lists.getOrLoadList(PresetListKey.DENTAL_MEDICATION_DURATIONS)).values;
    },
  })
  durationId: string;

  mealSpecText: string;

  @JsonProperty()
  @FormGen({
    name: "Meal Spec",
    type: "enum",
    required: true,
    _asyncInit: async (metadata: FormGenMetadata) => {
      metadata.enumValues = (await DentwyzePresetListsCache.getInstance().lists.getOrLoadList(PresetListKey.DENTAL_MEDICATION_MEALSPECS)).values;
    },
  })
  mealSpecId: string;

  static createNew(drugId: string): DentalPrescriptionMedication {
    const medication = new DentalPrescriptionMedication(md5_uuid());
    medication.drugId = drugId;
    return medication;
  }

  constructor(id: string) {
    this.id = id;
  }

  async onAfterItemDeserialized(): Promise<void> {
    this.drugText = (await DentwyzePresetListsCache.getInstance().lists.getOrLoadList(PresetListKey.DENTAL_MEDICATION_DRUGS)).findText(this.drugId);
    this.frequencyText = (await DentwyzePresetListsCache.getInstance().lists.getOrLoadList(PresetListKey.DENTAL_MEDICATION_FREQUENCIES)).findText(this.frequencyId);
    this.durationText = (await DentwyzePresetListsCache.getInstance().lists.getOrLoadList(PresetListKey.DENTAL_MEDICATION_DURATIONS)).findText(this.durationId);
    this.mealSpecText = (await DentwyzePresetListsCache.getInstance().lists.getOrLoadList(PresetListKey.DENTAL_MEDICATION_MEALSPECS)).findText(this.mealSpecId);
  }
}

@JsonObject()
export class DentalPrescription extends BasePracticeRecordListItem {

  @FormGen({name: "Note", type: "text", size: "xsmall"})
  @JsonProperty()
  note: string;

  @JsonProperty()
  status: DentalPrescriptionStatus = DentalPrescriptionStatus.ACTIVE;

  @JsonProperty({dataStructure: "array", type: DentalPrescriptionMedication})
  medications: DentalPrescriptionMedication[] = [];

  @JsonProperty()
  file: DentalPrescriptionFile;

  static createNew(src: RecordSource): DentalPrescription {
    return new DentalPrescription(md5_uuid().substring(0, 8).toUpperCase(), src);
  }

  async onAfterItemDeserialized(): Promise<void> {
    await super.onAfterItemDeserialized();
    for (const medication of this.medications) {
      await medication.onAfterItemDeserialized();
    }
  }
}

export class DentalPrescriptions extends BasePracticeListItemsLoader<DentalPrescription> {

  protected practiceDataBasePath(): string {
    return "prescriptions";
  }

  protected deserializeItem(value: any): DentalPrescription {
    return JSON_OBJECT.deserializeObject(value, DentalPrescription);
  }

  protected serializeItem(item: DentalPrescription): any {
    return JSON_OBJECT.serializeObject(item);
  }

  protected sortOrder(item1: DentalPrescription, item2: DentalPrescription): number {
    return item2.created - item1.created;
  }
}

/// Dental Charts

export enum DentalChartType {
  DEFAULT = "default",
  PERIO = "perio",
  QUICK = "quick",
}

export type DentalChartTypeMetadata = {
  text: string,
  colorValue: ColorValue,
}

export const DENTAL_CHART_TYPE_KTM = new KeyMetadatas<DentalChartTypeMetadata>([
  $KTM(DentalChartType.DEFAULT, {
    text: "Dental Chart",
    colorValue: new ColorValue(colorDarkGreen, colorLightPastelGreen)
  }),
  $KTM(DentalChartType.PERIO, {text: "Perio", colorValue: new ColorValue(black, colorYellow)}),
  $KTM(DentalChartType.QUICK, {text: "Quick", colorValue: new ColorValue(colorDarkBlue, colorLightPastelBlue)}),
]);

@JsonObject()
export class DentalChart extends BasePracticeRecordListItem {

  @JsonProperty()
  readonly type: DentalChartType = DentalChartType.DEFAULT;

  @JsonProperty()
  data: any;

  static createNew(src: RecordSource, type: DentalChartType): DentalChart {
    return new DentalChart(md5_uuid().substring(0, 8).toUpperCase(), src, type);
  }

  constructor(id: string, src: RecordSource, type: DentalChartType) {
    super(id, src);
    this.type = type;
  }
}

export class DentalCharts extends BasePracticeRecordListItemsLoader<DentalChart> {

  protected practiceDataBasePath(): string {
    return "charts";
  }

  protected deserializeItem(value: any): DentalChart {
    return JSON_OBJECT.deserializeObject(value, DentalChart);
  }

  protected serializeItem(item: DentalChart): any {
    return JSON_OBJECT.serializeObject(item);
  }

  protected sortOrder(item1: DentalChart, item2: DentalChart): number {
    return item2.created - item1.created;
  }
}

/// Dental Notes

export enum DentalNoteType {
  CLINICAL = "clinical",
  ASSESSMENT = "assessment",
}

export type DentalNoteTypeMetadata = {
  text: string,
  colorValue: ColorValue,
}

export const DENTAL_NOTE_TYPE_KTM = new KeyMetadatas<DentalNoteTypeMetadata>([
  $KTM(DentalNoteType.CLINICAL, {
    text: "Clinical Note",
    colorValue: new ColorValue(colorDarkGreen, colorLightPastelGreen)
  }),
  $KTM(DentalNoteType.ASSESSMENT, {text: "Assessment", colorValue: new ColorValue(black, colorYellow)}),
]);

@JsonObject()
export class DentalNote extends BasePracticeRecordListItem {

  @JsonProperty()
  readonly type: DentalNoteType = DentalNoteType.CLINICAL;

  @JsonProperty()
  data: any;

  static createNew(src: RecordSource, type: DentalNoteType): DentalNote {
    return new DentalNote(md5_uuid().substring(0, 8).toUpperCase(), src, type);
  }

  constructor(id: string, src: RecordSource, type: DentalNoteType) {
    super(id, src);
    this.type = type;
  }
}

export class DentalNotes extends BasePracticeRecordListItemsLoader<DentalNote> {

  protected practiceDataBasePath(): string {
    return "notes";
  }

  protected deserializeItem(value: any): DentalNote {
    return JSON_OBJECT.deserializeObject(value, DentalNote);
  }

  protected serializeItem(item: DentalNote): any {
    return JSON_OBJECT.serializeObject(item);
  }

  protected sortOrder(item1: DentalNote, item2: DentalNote): number {
    return item2.created - item1.created;
  }
}

/// Dental Procedures

export enum DentalProcedureDepartment {
  GENERAL = "general",
  ENDODONTICS = "endodontics",
  PROSTHODONTICS = "prosthodontics",
  IMPLANT = "implant",
  PERIODONTICS = "periodontics",
  ORTHODONTICS = "orthodontics",
  ORAL_SURGERY = "oral_surgery",
  ORAL_RADIOLOGY = "oral_radiology",
  PEDODONTICS = "pedodontics",
}

export const DENTAL_PROCEDURE_DEPARTMENT_KTS = new KeyTextStrings([
  $KTS(DentalProcedureDepartment.GENERAL, "General Dentistry"),
  $KTS(DentalProcedureDepartment.ENDODONTICS, "Conservative Dentistry & Endodontics"),
  $KTS(DentalProcedureDepartment.PROSTHODONTICS, "Prosthodontics"),
  $KTS(DentalProcedureDepartment.IMPLANT, "Implant Dentistry"),
  $KTS(DentalProcedureDepartment.PERIODONTICS, "Periodontics"),
  $KTS(DentalProcedureDepartment.ORTHODONTICS, "Orthodontics"),
  $KTS(DentalProcedureDepartment.ORAL_SURGERY, "Oral & Maxillofacial Surgery"),
  $KTS(DentalProcedureDepartment.ORAL_RADIOLOGY, "Oral & Maxillofacial Radiology"),
  $KTS(DentalProcedureDepartment.PEDODONTICS, "Pedodontics"),
]);

export class PresetDentalProcedure extends Preset {

  constructor(readonly department: DentalProcedureDepartment, readonly text: string, readonly code: string) {
    super(code);
  }

  toString(): string {
    return this.text;
  }
}

function $PROC(department: DentalProcedureDepartment, text: string, code: string): PresetDentalProcedure {
  return new PresetDentalProcedure(department, text, code);
}

const DENTAL_PROCEDURE_PRESETS = new DefinedPresets<PresetDentalProcedure>(new PresetsKey(PresetListKey.DENTAL_PROCEDURE_TYPES), [
  $PROC(DentalProcedureDepartment.GENERAL, "General Consultation", "DW1001"),
  $PROC(DentalProcedureDepartment.GENERAL, "RVG/ IOPAR X-RAY", "DW1010"),
  $PROC(DentalProcedureDepartment.GENERAL, "Oral Prophylaxis", "DW1020"),
  $PROC(DentalProcedureDepartment.GENERAL, "Office Bleaching/ Whitening", "DW1030"),
  $PROC(DentalProcedureDepartment.GENERAL, "Home Bleaching/ Whitening", "DW1035"),

  $PROC(DentalProcedureDepartment.ENDODONTICS, "Composite Restoration - 2 Surface", "DW2001"),
  $PROC(DentalProcedureDepartment.ENDODONTICS, "Composite Restoration - All Surface", "DW2005"),
  $PROC(DentalProcedureDepartment.ENDODONTICS, "Composite Restoration - Complete", "DW2010"),
  $PROC(DentalProcedureDepartment.ENDODONTICS, "GIC Restoration", "DW2020"),
  $PROC(DentalProcedureDepartment.ENDODONTICS, "Amalgam Restoration", "DW2030"),
  $PROC(DentalProcedureDepartment.ENDODONTICS, "Temporary Restoration - ZoE/ Temp", "DW2040"),
  $PROC(DentalProcedureDepartment.ENDODONTICS, "Root Canal Treatment - Single/ Anterior Tooth", "DW2050"),
  $PROC(DentalProcedureDepartment.ENDODONTICS, "Root Canal Treatment - Premolar Tooth", "DW2055"),
  $PROC(DentalProcedureDepartment.ENDODONTICS, "Root Canal Treatment - Molar Tooth", "DW2060"),
  $PROC(DentalProcedureDepartment.ENDODONTICS, "Re Root Canal Treatment", "DW2065"),
  $PROC(DentalProcedureDepartment.ENDODONTICS, "Post and Core - Metal", "DW2070"),
  $PROC(DentalProcedureDepartment.ENDODONTICS, "Post and Core - Fibre", "DW2075"),
  $PROC(DentalProcedureDepartment.ENDODONTICS, "Apiceoctomy", "DW2080"),
  $PROC(DentalProcedureDepartment.ENDODONTICS, "Apexification", "DW2085"),
  $PROC(DentalProcedureDepartment.ENDODONTICS, "Metapex Restoration", "DW2090"),

  $PROC(DentalProcedureDepartment.PROSTHODONTICS, "Complete Denture - Single Arch", "DW3001"),
  $PROC(DentalProcedureDepartment.PROSTHODONTICS, "Complete Denture - Both Arches", "DW3010"),
  $PROC(DentalProcedureDepartment.PROSTHODONTICS, "Partial Denture Removable", "DW3015"),
  $PROC(DentalProcedureDepartment.PROSTHODONTICS, "Fixed Partial Denture", "DW3020"),
  $PROC(DentalProcedureDepartment.PROSTHODONTICS, "Immediate Denture", "DW3025"),
  $PROC(DentalProcedureDepartment.PROSTHODONTICS, "Flexible Denture", "DW3030"),
  $PROC(DentalProcedureDepartment.PROSTHODONTICS, "Crown and Bridge - 3 Units", "DW3040"),
  $PROC(DentalProcedureDepartment.PROSTHODONTICS, "Crown and Bridge - 4 Units", "DW3045"),
  $PROC(DentalProcedureDepartment.PROSTHODONTICS, "Crown and Bridge - 5 Units", "DW3050"),
  $PROC(DentalProcedureDepartment.PROSTHODONTICS, "Crown - Zirconium", "DW3060"),
  $PROC(DentalProcedureDepartment.PROSTHODONTICS, "Crown - Zirconium 10 Years", "DW3065"),
  $PROC(DentalProcedureDepartment.PROSTHODONTICS, "Crown - Zirconium 15 Years", "DW3070"),
  $PROC(DentalProcedureDepartment.PROSTHODONTICS, "Crown - PFM Anterior", "DW3080"),
  $PROC(DentalProcedureDepartment.PROSTHODONTICS, "Crown - PFM Posterior", "DW3085"),
  $PROC(DentalProcedureDepartment.PROSTHODONTICS, "Crown - Metal", "DW3090"),

  $PROC(DentalProcedureDepartment.IMPLANT, "Implant - Anterior", "DW4001"),
  $PROC(DentalProcedureDepartment.IMPLANT, "Implant - Posterior", "DW4005"),
  $PROC(DentalProcedureDepartment.IMPLANT, "Implant - All on 4", "DW4010"),
  $PROC(DentalProcedureDepartment.IMPLANT, "Implant - All on 6", "DW4015"),
  $PROC(DentalProcedureDepartment.IMPLANT, "Cover Screw", "DW4020"),

  $PROC(DentalProcedureDepartment.PERIODONTICS, "Scaling and Polishing", "DW5001"),
  $PROC(DentalProcedureDepartment.PERIODONTICS, "Sub Gingival Deep Scaling/ Root Planing", "DW5010"),
  $PROC(DentalProcedureDepartment.PERIODONTICS, "Soft Tissue Grafting - Per Quardant", "DW5020"),
  $PROC(DentalProcedureDepartment.PERIODONTICS, "Sinus Lift Surgery", "DW5030"),
  $PROC(DentalProcedureDepartment.PERIODONTICS, "Artificial Bone Graft", "DW5040"),
  $PROC(DentalProcedureDepartment.PERIODONTICS, "Gingivectomy - Laser - Per Quardant", "DW5050"),
  $PROC(DentalProcedureDepartment.PERIODONTICS, "Gingivectomy - Surgical - Per Quardant", "DW5060"),

  $PROC(DentalProcedureDepartment.ORTHODONTICS, "Clear Aligner Treatment", "DW6001"),
  $PROC(DentalProcedureDepartment.ORTHODONTICS, "Invisalign Treatment - Mild", "DW6005"),
  $PROC(DentalProcedureDepartment.ORTHODONTICS, "Invisalign Treatment - Moderate", "DW6010"),
  $PROC(DentalProcedureDepartment.ORTHODONTICS, "Invisalign Treatment - Severe", "DW6015"),
  $PROC(DentalProcedureDepartment.ORTHODONTICS, "Metal Braces - Mild", "DW6020"),
  $PROC(DentalProcedureDepartment.ORTHODONTICS, "Metal Braces - Moderate", "DW6025"),
  $PROC(DentalProcedureDepartment.ORTHODONTICS, "Metal Braces- Severe", "DW6030"),
  $PROC(DentalProcedureDepartment.ORTHODONTICS, "Ceramic Braces - Mild", "DW6040"),
  $PROC(DentalProcedureDepartment.ORTHODONTICS, "Ceramic Braces - Moderate", "DW6045"),
  $PROC(DentalProcedureDepartment.ORTHODONTICS, "Ceramic Braces - Severe", "DW6050"),
  $PROC(DentalProcedureDepartment.ORTHODONTICS, "Complicated Case", "DW6060"),
  $PROC(DentalProcedureDepartment.ORTHODONTICS, "Self Ligating Braces", "DW6070"),
  $PROC(DentalProcedureDepartment.ORTHODONTICS, "Surgical Orthodontic Case", "DW6075"),
  $PROC(DentalProcedureDepartment.ORTHODONTICS, "MyoFunctional Removable Appliance", "DW6080"),
  $PROC(DentalProcedureDepartment.ORTHODONTICS, "Fixed Functional Appliance", "DW6085"),
  $PROC(DentalProcedureDepartment.ORTHODONTICS, "Face Mask/ Chin Cup Therapy", "DW6090"),
  $PROC(DentalProcedureDepartment.ORTHODONTICS, "FIxed Retainer - Single Arch", "DW6092"),
  $PROC(DentalProcedureDepartment.ORTHODONTICS, "Fixed Retainer - Both Arches", "DW6094"),
  $PROC(DentalProcedureDepartment.ORTHODONTICS, "Removable Clear Retainer - Single Arch", "DW6096"),
  $PROC(DentalProcedureDepartment.ORTHODONTICS, "Removable Clear Retainer - Both Arches", "DW6100"),
  $PROC(DentalProcedureDepartment.ORTHODONTICS, "Removable Hawlwr's Retainer - Single Arch", "DW6105"),
  $PROC(DentalProcedureDepartment.ORTHODONTICS, "Removable Hawley's Retainer - Both Arches", "DW6110"),

  $PROC(DentalProcedureDepartment.ORAL_SURGERY, "Simple Extraction", "DW7001"),
  $PROC(DentalProcedureDepartment.ORAL_SURGERY, "Complicated Extraction", "DW7005"),
  $PROC(DentalProcedureDepartment.ORAL_SURGERY, "Surgical Extraction", "DW7010"),
  $PROC(DentalProcedureDepartment.ORAL_SURGERY, "Impaction - Canine", "DW7020"),
  $PROC(DentalProcedureDepartment.ORAL_SURGERY, "Impaction - Premolar", "DW7025"),
  $PROC(DentalProcedureDepartment.ORAL_SURGERY, "Impaction - Molar", "DW7030"),
  $PROC(DentalProcedureDepartment.ORAL_SURGERY, "Impaction - Third Molar", "DW7035"),
  $PROC(DentalProcedureDepartment.ORAL_SURGERY, "Inter Maxillary FIxation", "DW7040"),
  $PROC(DentalProcedureDepartment.ORAL_SURGERY, "Bone Plate and Screws", "DW7050"),
  $PROC(DentalProcedureDepartment.ORAL_SURGERY, "Excisional Biopsy", "DW7060"),
  $PROC(DentalProcedureDepartment.ORAL_SURGERY, "Alveolectomy", "DW7070"),
  $PROC(DentalProcedureDepartment.ORAL_SURGERY, "Alveoloplasty", "DW7075"),

  $PROC(DentalProcedureDepartment.ORAL_RADIOLOGY, "IOPAR", "DW8001"),
  $PROC(DentalProcedureDepartment.ORAL_RADIOLOGY, "CBCT - Maxilla", "DW8010"),
  $PROC(DentalProcedureDepartment.ORAL_RADIOLOGY, "CBCT - Mandible", "DW8020"),
  $PROC(DentalProcedureDepartment.ORAL_RADIOLOGY, "Lateral Cephalogram", "DW8030"),
  $PROC(DentalProcedureDepartment.ORAL_RADIOLOGY, "OPG ", "DW8040"),
  $PROC(DentalProcedureDepartment.ORAL_RADIOLOGY, "Cancer Screening", "DW8050"),

  $PROC(DentalProcedureDepartment.PEDODONTICS, "Extraction - Deciduous Anterior Tooth", "DW9001"),
  $PROC(DentalProcedureDepartment.PEDODONTICS, "Extraction - Deciduous Posterior Tooth", "DW9005"),
  $PROC(DentalProcedureDepartment.PEDODONTICS, "Extraction - Permanent Tooth", "DW9010"),
  $PROC(DentalProcedureDepartment.PEDODONTICS, "Pulpotomy - Anterior Tooth", "DW9015"),
  $PROC(DentalProcedureDepartment.PEDODONTICS, "Pulpotomy - Posterior Tooth", "DW9020"),
  $PROC(DentalProcedureDepartment.PEDODONTICS, "Pulpectomy - Anterior Tooth", "DW9025"),
  $PROC(DentalProcedureDepartment.PEDODONTICS, "Pulpectimy - Posterior Tooth", "DW9030"),
  $PROC(DentalProcedureDepartment.PEDODONTICS, "Space Maintainer", "DW9040"),
  $PROC(DentalProcedureDepartment.PEDODONTICS, "Crown - Metal - Anterior", "DW9045"),
  $PROC(DentalProcedureDepartment.PEDODONTICS, "Crown - Metal - Posterior", "DW9050"),
  $PROC(DentalProcedureDepartment.PEDODONTICS, "Crown - Zirconium - Anterior ", "DW9055"),
  $PROC(DentalProcedureDepartment.PEDODONTICS, "Crown - Zirconium - Posterior", "DW9060"),
  $PROC(DentalProcedureDepartment.PEDODONTICS, "GIC Anterior Restoration", "DW9065"),
  $PROC(DentalProcedureDepartment.PEDODONTICS, "GIC Posterior Restoration", "DW9070"),
  $PROC(DentalProcedureDepartment.PEDODONTICS, "Zoe / Calcium Hydroxide / Temp Restoration", "DW9075"),
  $PROC(DentalProcedureDepartment.PEDODONTICS, "Composite Restoration - Anterior Tooth", "DW9080"),
  $PROC(DentalProcedureDepartment.PEDODONTICS, "Composite Restoration - Posterior Tooth", "DW9085"),
  $PROC(DentalProcedureDepartment.PEDODONTICS, "MyoFunctional Treatment", "DW9090"),
  $PROC(DentalProcedureDepartment.PEDODONTICS, "Interceptive Treatment", "DW9100"),
  $PROC(DentalProcedureDepartment.PEDODONTICS, "Nitrous Oxide/ Conceous Sedation", "DW9110"),
  $PROC(DentalProcedureDepartment.PEDODONTICS, "Root Canal Treatment - Anterior Tooth", "DW9120"),
  $PROC(DentalProcedureDepartment.PEDODONTICS, "Root Canal Treatment - Posterior Tooth", "DW9125"),
]);

PRESET_LIST_KTM.add(
  $KTM(PresetListKey.DENTAL_PROCEDURE_TYPES, {type: "defined", definedPresets: DENTAL_PROCEDURE_PRESETS}),
);

@JsonObject()
export class DentalProcedure extends BasePracticeListItem {

  @FormGen({
    name: "Department",
    type: "enum",
    enumValues: DENTAL_PROCEDURE_DEPARTMENT_KTS.values,
    required: true,
    enumDefaultKeyFirst: true,
  })
  @JsonProperty()
  department: string;

  @FormGen({
    name: "Name",
    type: "string",
    required: true
  })
  @JsonProperty()
  name: string;

  @FormGen({
    name: "Code",
    type: "string",
    required: true
  })
  @JsonProperty()
  code: string;

  @FormGen({
    name: "Amount",
    type: "currency",
    required: true
  })
  @JsonProperty()
  amount: number;

  @FormGen({
    name: "Enabled",
    type: "boolean",
    required: true
  })
  @JsonProperty()
  enabled: boolean;

  static createFromPreset(preset: PresetDentalProcedure): DentalProcedure {
    const procedure = new DentalProcedure(preset.id);
    procedure.department = preset.department;
    procedure.name = preset.text;
    procedure.code = preset.code;
    return procedure;
  }

  static createNew(): DentalProcedure {
    const procedure = new DentalProcedure(md5_uuid());
    return procedure;
  }

  constructor(id: string) {
    super(id);
  }
}

export class DentalProcedures extends BasePracticeListItemsLoader<DentalProcedure> {

  constructor(practiceId: string) {
    super(practiceId);
  }

  async loadListItems(): Promise<void> {
    await super.loadListItems();
    const procedures = this.getListItems();
    const all: Promise<void>[] = [];
    for (const preset of DENTAL_PROCEDURE_PRESETS.list) {
      if (!procedures.find(proc => proc.code === preset.code)) {
        const item = DentalProcedure.createFromPreset(preset);
        procedures.push(item);
        all.push(this.addListItem(item));
      }
    }
    await Promise.all(all);
  }

  protected practiceDataBasePath(): string {
    return "presets/dentalProcedures";
  }

  protected deserializeItem(value: any): DentalProcedure {
    return JSON_OBJECT.deserializeObject(value, DentalProcedure);
  }

  protected serializeItem(item: DentalProcedure): any {
    return JSON_OBJECT.serializeObject(item);
  }

  protected sortOrder(item1: DentalProcedure, item2: DentalProcedure): number {
    return item1.name.localeCompare(item2.name);
  }
}

export const dentalProcedureAutocompleteLoader = (): Promise<KeyTextString[]> => {
  const procedures = PracticeData.provider().presets.dentalProcedures;
  return new Promise<KeyTextString[]>((resolve, reject) => {
    procedures.getOrLoadListItems()
      .then(procs => resolve(procs.filter(proc => proc.enabled).map(proc => $KTS(proc.id, `${proc.code} ${proc.name}`))))
      .catch(reason => reject(reason));
  });
}


/// Dental Tx Plans

export enum DentalTxPlanStatus {
  ACTIVE = "active",
  INACTIVE = "inactive",
}

export const DENTAL_TXPLAN_STATUS_KTS = new KeyTextStrings([
  $KTS(DentalTxPlanStatus.ACTIVE, "Active"),
  $KTS(DentalTxPlanStatus.INACTIVE, "Inactive"),
]);

export const DENTAL_TXPLAN_STATUS_KTM_COLORS = new KeyMetadatas<ColorValue>([
  $KTM<ColorValue>(DentalTxPlanStatus.ACTIVE, new ColorValue(colorDarkGreen, colorPastelGreen)),
  $KTM<ColorValue>(DentalTxPlanStatus.INACTIVE, new ColorValue(white, mediumGray)),
]);

@JsonObject()
export class DentalTxPlanFile {

  @JsonProperty()
  readonly url: string;

  static createNew(url: string): DentalTxPlanFile {
    return new DentalTxPlanFile(url);
  }

  constructor(url: string) {
    this.url = url;
  }
}

@JsonObject()
export class DentalTxPlanProcedure {

  @JsonProperty()
  readonly id: string;

  procedure: DentalProcedure;

  @FormGen({
    name: "Procedure",
    type: "autocomplete",
    required: true,
    autocompleteLoader: dentalProcedureAutocompleteLoader,
    onValueSet: (target, value, updateCallback) => {
      (async function () {
        const action = target as TransactionActionServiceDataDentalProcedure;
        const procedure = await PracticeData.provider().presets.dentalProcedures.getOrLoadItem(value);
        action.parent.transaction.description = procedure?.name;
        action.parent.amount = procedure.amount;
        await updateCallback();
      })();
    }
  })
  @JsonProperty()
  procedureId: string;

  @FormGen({
    name: "Comment",
    type: "text",
    size: "xsmall",
  })
  @JsonProperty()
  comment: string;

  static createNew(procedureId: string): DentalTxPlanProcedure {
    const procedure = new DentalTxPlanProcedure(md5_uuid());
    procedure.procedureId = procedureId;
    return procedure;
  }

  constructor(id: string) {
    this.id = id;
  }

  async onAfterItemDeserialized(): Promise<void> {
    this.procedure = await PracticeData.provider().presets.dentalProcedures.getOrLoadItem(this.procedureId);
  }
}

@JsonObject()
export class DentalTxPlan extends BasePracticeRecordListItem {

  @FormGen({name: "Note", type: "text", size: "xsmall"})
  @JsonProperty()
  note: string;

  @JsonProperty()
  status: DentalTxPlanStatus = DentalTxPlanStatus.ACTIVE;

  @JsonProperty({dataStructure: "array", type: DentalTxPlanProcedure})
  procedures: DentalTxPlanProcedure[] = [];

  @JsonProperty()
  file: DentalTxPlanFile;

  static createNew(src: RecordSource): DentalTxPlan {
    return new DentalTxPlan(md5_uuid().substring(0, 8).toUpperCase(), src);
  }

  async onAfterItemDeserialized(): Promise<void> {
    await super.onAfterItemDeserialized();
    for (const procedure of this.procedures) {
      await procedure.onAfterItemDeserialized();
    }
  }
}

export class DentalTxPlans extends BasePracticeListItemsLoader<DentalTxPlan> {

  protected practiceDataBasePath(): string {
    return "txPlans";
  }

  protected deserializeItem(value: any): DentalTxPlan {
    return JSON_OBJECT.deserializeObject(value, DentalTxPlan);
  }

  protected serializeItem(item: DentalTxPlan): any {
    return JSON_OBJECT.serializeObject(item);
  }

  protected sortOrder(item1: DentalTxPlan, item2: DentalTxPlan): number {
    return item2.created - item1.created;
  }
}

/// Transactions

export enum TransactionActionType {
  PAYMENT = "payment",
  PROCEDURE = "procedure", // same as CHARGE, deprecated.
  CHARGE = "charge",
  CHARGE_ADJUSTMENT = "charge_adjustment",
  CREDIT_ADJUSTMENT = "credit_adjustment",
}

export const TRANSACTION_ACTION_TYPES_KTS = new KeyTextStrings([
  $KTS(TransactionActionType.PAYMENT, "Payment (-)"),
  $KTS(TransactionActionType.CHARGE, "Charge (+)"),
  $KTS(TransactionActionType.CHARGE_ADJUSTMENT, "Adj (+)"),
  $KTS(TransactionActionType.CREDIT_ADJUSTMENT, "Adj (-)"),
]);

export enum TransactionMethodType {
  // PAYMENT
  PAYMENT_INSURANCE_CHECK = "pay_ins_check",
  PAYMENT_INSURANCE_ELECTRONIC = "pay_ins_electronic",
  PAYMENT_INSURANCE_CREDIT_CARD = "pay_ins_cc",
  PAYMENT_CHECK = "pay_check",
  PAYMENT_CASH = "pay_cash",
  PAYMENT_CREDIT_CARD = "pay_cc",
  PAYMENT_FINANCING = "pay_financing",
  PAYMENT_EFT = "pay_eft",

  // CHARGE_ADJUSTMENT
  CHARGE_ADJUSTMENT_GENERIC = "cha_generic",
  CHARGE_ADJUSTMENT_COLLECTED_BAD_DEBT = "cha_collected_bad_debt",
  CHARGE_ADJUSTMENT_ELECTRONIC_CHECK_CREDIT = "cha_electronic_check_credit",
  CHARGE_ADJUSTMENT_INITIAL_CHARGE_BALANCE_FORWARD = "cha_initial_balance_forward",
  CHARGE_ADJUSTMENT_INSURANCE_ADJUSTMENT = "cha_insurance_adjustment",
  CHARGE_ADJUSTMENT_INSURANCE_OVER_PAYMENT_REFUND = "cha_insurance_opr",
  CHARGE_ADJUSTMENT_NSF_BANK_FEE = "cha_nsf_bank_fee",
  CHARGE_ADJUSTMENT_NSF_CHECK = "cha_nsf_check",
  CHARGE_ADJUSTMENT_PATIENT_REFUND = "cha_patient_refund",
  CHARGE_ADJUSTMENT_SALES_TAX = "cha_sales_tax",
  CHARGE_ADJUSTMENT_TRANSFER_CHARGE_BALANCE = "cha_xfer_charge_balance",
  CHARGE_ADJUSTMENT_INSURANCE_CHARGE_OFFSET = "cha_insurance_charge_offset",
  CHARGE_ADJUSTMENT_CC_REFUND = "cha_cc_refund",
  CHARGE_ADJUSTMENT_CC_VOID = "cha_cc_void",

  // CREDIT_ADJUSTMENT
  CREDIT_ADJUSTMENT_GENERIC = "cra_generic",
  CREDIT_ADJUSTMENT_BANKRUPTCY_WRITEOFF = "cra_bankruptcy_writeoff",
  CREDIT_ADJUSTMENT_CHARITABLE_CONTRIBUTION = "cra_chartitable_contrib",
  CREDIT_ADJUSTMENT_FAMILY_FRIEND_COURTESY = "cra_ff_courtesy",
  CREDIT_ADJUSTMENT_FULL_PAYMENT_COURTESY = "cra_full_payment_courtesy",
  CREDIT_ADJUSTMENT_INITIAL_CREDIT_BALANCE_COURTESY = "cra_initial_balance_forward",
  CREDIT_ADJUSTMENT_INOFFICE_WRITEOFF = "cra_inoffice_writeoff",
  CREDIT_ADJUSTMENT_INSURANCE_ADJUSTMENT = "cra_insurance_adjustment",
  CREDIT_ADJUSTMENT_PROFESSIONAL_COURTESY = "cra_professional_courtesy",
  CREDIT_ADJUSTMENT_SENIOR_CITIZEN_COURTESY = "cra_senior_citizen_courtesy",
  CREDIT_ADJUSTMENT_STAFF_COURTESY = "cra_staff_courtesy",
  CREDIT_ADJUSTMENT_TRANSFER_CREDIT_BALANCE = "cra_xfer_credit_balance",
  CREDIT_ADJUSTMENT_WRITEOFF = "cra_writeoff",
  CREDIT_ADJUSTMENT_OFFSETTING_ADJUSTMENT = "cra_offsetting_adjustment",
  CREDIT_ADJUSTMENT_DISCOUNT = "cra_discount",
}

export class PresetTransactionMethodType extends Preset {

  readonly text: string;
  readonly actionType: TransactionActionType;

  constructor(id: string, text: string, actionType: TransactionActionType) {
    super(id);
    this.text = text;
    this.actionType = actionType;
  }

  toString(): string {
    return this.text;
  }
}

function $PTM(id, text, actionType) {
  return new PresetTransactionMethodType(id, text, actionType);
}

// See https://hsps.pro/DentrixAscend/Help/Transaction_types_list.htm
const TRANSACTION_METHOD_PRESETS = new DefinedPresets(new PresetsKey(PresetListKey.TRANSACTION_METHOD_TYPES), [
  $PTM(TransactionMethodType.PAYMENT_INSURANCE_CHECK, "Insurance - Check", TransactionActionType.PAYMENT),
  $PTM(TransactionMethodType.PAYMENT_INSURANCE_ELECTRONIC, "Insurance - Electronic", TransactionActionType.PAYMENT),
  $PTM(TransactionMethodType.PAYMENT_INSURANCE_CREDIT_CARD, "Insurance - Credit Card", TransactionActionType.PAYMENT),
  $PTM(TransactionMethodType.PAYMENT_CHECK, "Check", TransactionActionType.PAYMENT),
  $PTM(TransactionMethodType.PAYMENT_CASH, "Cash", TransactionActionType.PAYMENT),
  $PTM(TransactionMethodType.PAYMENT_CREDIT_CARD, "Credit Card", TransactionActionType.PAYMENT),
  $PTM(TransactionMethodType.PAYMENT_FINANCING, "Financing", TransactionActionType.PAYMENT),
  $PTM(TransactionMethodType.PAYMENT_EFT, "Electronic Fund Transfer", TransactionActionType.PAYMENT),

  $PTM(TransactionMethodType.CHARGE_ADJUSTMENT_GENERIC, "Charge Adjustment", TransactionActionType.CHARGE_ADJUSTMENT),
  $PTM(TransactionMethodType.CHARGE_ADJUSTMENT_COLLECTED_BAD_DEBT, "Collected Bad Debt", TransactionActionType.CHARGE_ADJUSTMENT),
  $PTM(TransactionMethodType.CHARGE_ADJUSTMENT_ELECTRONIC_CHECK_CREDIT, "Electronic Check Credit", TransactionActionType.CHARGE_ADJUSTMENT),
  $PTM(TransactionMethodType.CHARGE_ADJUSTMENT_INITIAL_CHARGE_BALANCE_FORWARD, "Initial Balance Forward", TransactionActionType.CHARGE_ADJUSTMENT),
  $PTM(TransactionMethodType.CHARGE_ADJUSTMENT_INSURANCE_ADJUSTMENT, "Insurance Adjustment", TransactionActionType.CHARGE_ADJUSTMENT),
  $PTM(TransactionMethodType.CHARGE_ADJUSTMENT_INSURANCE_OVER_PAYMENT_REFUND, "Insurance Overpayment Refund", TransactionActionType.CHARGE_ADJUSTMENT),
  $PTM(TransactionMethodType.CHARGE_ADJUSTMENT_NSF_BANK_FEE, "NSF Bank Fee", TransactionActionType.CHARGE_ADJUSTMENT),
  $PTM(TransactionMethodType.CHARGE_ADJUSTMENT_NSF_CHECK, "NSF Check", TransactionActionType.CHARGE_ADJUSTMENT),
  $PTM(TransactionMethodType.CHARGE_ADJUSTMENT_PATIENT_REFUND, "Patient Refund", TransactionActionType.CHARGE_ADJUSTMENT),
  $PTM(TransactionMethodType.CHARGE_ADJUSTMENT_SALES_TAX, "Sales Tax", TransactionActionType.CHARGE_ADJUSTMENT),
  $PTM(TransactionMethodType.CHARGE_ADJUSTMENT_TRANSFER_CHARGE_BALANCE, "Transfer Charge Balance", TransactionActionType.CHARGE_ADJUSTMENT),
  $PTM(TransactionMethodType.CHARGE_ADJUSTMENT_INSURANCE_CHARGE_OFFSET, "Insurance Charge Offset", TransactionActionType.CHARGE_ADJUSTMENT),
  $PTM(TransactionMethodType.CHARGE_ADJUSTMENT_CC_REFUND, "Credit Card Refund", TransactionActionType.CHARGE_ADJUSTMENT),
  $PTM(TransactionMethodType.CHARGE_ADJUSTMENT_CC_VOID, "Credit Card Void", TransactionActionType.CHARGE_ADJUSTMENT),

  $PTM(TransactionMethodType.CREDIT_ADJUSTMENT_GENERIC, "Credit Adjustment", TransactionActionType.CREDIT_ADJUSTMENT),
  $PTM(TransactionMethodType.CREDIT_ADJUSTMENT_BANKRUPTCY_WRITEOFF, "Bankruptcy Write-off", TransactionActionType.CREDIT_ADJUSTMENT),
  $PTM(TransactionMethodType.CREDIT_ADJUSTMENT_CHARITABLE_CONTRIBUTION, "Charitable Contribution", TransactionActionType.CREDIT_ADJUSTMENT),
  $PTM(TransactionMethodType.CREDIT_ADJUSTMENT_FAMILY_FRIEND_COURTESY, "Family/Friend Courtesy", TransactionActionType.CREDIT_ADJUSTMENT),
  $PTM(TransactionMethodType.CREDIT_ADJUSTMENT_FULL_PAYMENT_COURTESY, "Full Payment Courtesy", TransactionActionType.CREDIT_ADJUSTMENT),
  $PTM(TransactionMethodType.CREDIT_ADJUSTMENT_INITIAL_CREDIT_BALANCE_COURTESY, "Initial Balance Forward", TransactionActionType.CREDIT_ADJUSTMENT),
  $PTM(TransactionMethodType.CREDIT_ADJUSTMENT_INOFFICE_WRITEOFF, "In-office Write-off", TransactionActionType.CREDIT_ADJUSTMENT),
  $PTM(TransactionMethodType.CREDIT_ADJUSTMENT_INSURANCE_ADJUSTMENT, "Insurance Adjustment", TransactionActionType.CREDIT_ADJUSTMENT),
  $PTM(TransactionMethodType.CREDIT_ADJUSTMENT_PROFESSIONAL_COURTESY, "Professional Courtesy", TransactionActionType.CREDIT_ADJUSTMENT),
  $PTM(TransactionMethodType.CREDIT_ADJUSTMENT_SENIOR_CITIZEN_COURTESY, "Senior Citizen Courtesy", TransactionActionType.CREDIT_ADJUSTMENT),
  $PTM(TransactionMethodType.CREDIT_ADJUSTMENT_STAFF_COURTESY, "Staff Courtesy", TransactionActionType.CREDIT_ADJUSTMENT),
  $PTM(TransactionMethodType.CREDIT_ADJUSTMENT_TRANSFER_CREDIT_BALANCE, "Transfer Credit Balance", TransactionActionType.CREDIT_ADJUSTMENT),
  $PTM(TransactionMethodType.CREDIT_ADJUSTMENT_WRITEOFF, "Write-off", TransactionActionType.CREDIT_ADJUSTMENT),
  $PTM(TransactionMethodType.CREDIT_ADJUSTMENT_OFFSETTING_ADJUSTMENT, "Offsetting Adjustment", TransactionActionType.CREDIT_ADJUSTMENT),
  $PTM(TransactionMethodType.CREDIT_ADJUSTMENT_DISCOUNT, "Discount", TransactionActionType.CREDIT_ADJUSTMENT),
]);

PRESET_LIST_KTM.add(
  $KTM(PresetListKey.TRANSACTION_METHOD_TYPES, {type: "defined", definedPresets: TRANSACTION_METHOD_PRESETS})
);

type TransactionInterface = {
  description: string;
}

type TransactionActionInterface = {
  transaction: TransactionInterface,
  amount: number,
}

export enum TransactionActionServiceType {
  DENTAL_PROCEDURE = "dental_proc",
  CONSULTATION = "consultation",
  LAB_ORDER = "lab_order",
}

@JsonObject()
export abstract class TransactionActionServiceData {

  parent: TransactionActionInterface;
}

// @JsonObject()
export abstract class BaseTransactionAction implements TransactionActionInterface {

  @JsonProperty()
  readonly serviceType: TransactionActionServiceType;

  @FormGen({name: "Amount", type: "currency", noneditable: true, required: true})
  @JsonProperty()
  amount: number;

  @FormGen({
    name: "",
    type: "object",
  })
  @JsonProperty()
  private _data: TransactionActionServiceData;

  constructor(readonly sign: 1 | -1, readonly type: TransactionActionType, readonly transaction: TransactionInterface, serviceType: TransactionActionServiceType) {
    this.serviceType = serviceType;
    if (serviceType) { // serviceType may be undefined during deserialization, in which case _data will be set later.
      this._data = BaseTransactionAction.createData(type, serviceType);
      this._data.parent = this;
    }
  }

  static readonly sFactoriesMap = new Map<TransactionActionType, TransactionActionServiceDataFactory<any>[]>();

  static registerFactory(types: TransactionActionType[], factory: TransactionActionServiceDataFactory<any>): void {
    for (const type of types) {
      let factories: TransactionActionServiceDataFactory<any>[] = this.sFactoriesMap.get(type);
      if (!factories) {
        factories = [];
        this.sFactoriesMap.set(type, factories);
      }
      factories.push(factory);
    }
  }

  protected static createData(type: TransactionActionType, serviceType: TransactionActionServiceType): TransactionActionServiceData {
    return BaseTransactionAction.sFactoriesMap.get(type)?.find(factory => factory.type === serviceType)?.create();
  }

  protected readDataFrom(json: any): TransactionActionServiceData {
    return BaseTransactionAction.sFactoriesMap.get(this.type)?.find(factory => factory.type === this.serviceType)?.read(json);
  }

  onAfterItemDeserialized(): void {
    this._data = this.readDataFrom(this._data);
    if (this._data) {
      this._data.parent = this;
    }
  }

  readData<T>(): T {
    return this._data as T;
  }

  getSignedAmount(): number {
    return this.sign * Math.abs(this.amount || 0);
  }

  getSignedAmountText(): string {
    return CurrencyText(this.getSignedAmount());
  }
}

@JsonObject()
export class TransactionActionPayment extends BaseTransactionAction {

  @FormGen({
    name: "Method",
    type: "enum",
    required: true,
    noneditable: true,
    _asyncInit: async (metadata: FormGenMetadata) => {
      metadata.enumValues = (await DentwyzePresetListsCache.getInstance().lists.getOrLoadList(PresetListKey.TRANSACTION_METHOD_TYPES))
        .values.filter(val => val.key.startsWith("pay_"));
    },
    enumDefaultKey: TransactionMethodType.PAYMENT_CASH
  })
  @JsonProperty()
  method: string = TransactionMethodType.PAYMENT_CASH;

  constructor(transaction: TransactionInterface, serviceType: TransactionActionServiceType) {
    super(-1, TransactionActionType.PAYMENT, transaction, serviceType);
  }
}

export type TransactionActionServiceTypeMetadata = {
  text: string,
  color: ColorValue,
}

export const TRANSACTION_ACTION_SERVICE_TYPE_KTM = new KeyMetadatas<TransactionActionServiceTypeMetadata>([
  $KTM(
    TransactionActionServiceType.DENTAL_PROCEDURE,
    {text: "Dental Procedure", color: new ColorValue("black", colorPastelBlue)}),
  $KTM(
    TransactionActionServiceType.CONSULTATION,
    {text: "Consultation", color: new ColorValue("black", colorPastelBlue)}),
  $KTM(
    TransactionActionServiceType.LAB_ORDER,
    {text: "Lab Order", color: new ColorValue("black", colorPastelBlue)}),
]);

@JsonObject()
export class TransactionActionServiceDataDentalProcedure extends TransactionActionServiceData {

  @FormGen({
    name: "Procedure",
    type: "autocomplete",
    required: true,
    autocompleteLoader: dentalProcedureAutocompleteLoader,
    onValueSet: (target, value, updateCallback) => {
      (async function () {
        const action = target as TransactionActionServiceDataDentalProcedure;
        const procedure = await PracticeData.provider().presets.dentalProcedures.getOrLoadItem(value);
        action.parent.transaction.description = procedure?.name;
        action.parent.amount = procedure.amount;
        await updateCallback();
      })();
    }
  })
  @JsonProperty()
  procedureId: string;
}

@JsonObject()
export class TransactionActionServiceDataConsultation extends TransactionActionServiceData {
}

@JsonObject()
export class TransactionActionServiceDataLabOrder extends TransactionActionServiceData {

  @FormGen({
    name: "Lab Order Code",
    type: "string",
    required: true,
  })
  @JsonProperty()
  labOrderId: string;
}

export abstract class TransactionActionServiceDataFactory<T extends TransactionActionServiceData> {

  constructor(readonly type: TransactionActionServiceType) {
  }

  abstract create(): T;

  abstract read(json: any): T;
}

@JsonObject()
export class TransactionActionCharge extends BaseTransactionAction {

  constructor(transaction: TransactionInterface, serviceType: TransactionActionServiceType) {
    super(1, TransactionActionType.CHARGE, transaction, serviceType);
  }
}

@JsonObject()
export class TransactionActionChargeAdjustment extends BaseTransactionAction {

  @FormGen({
    name: "Type",
    type: "enum",
    required: true,
    noneditable: true,
    _asyncInit: async (metadata: FormGenMetadata) => {
      metadata.enumValues = (await DentwyzePresetListsCache.getInstance().lists.getOrLoadList(PresetListKey.TRANSACTION_METHOD_TYPES))
        .values.filter(val => val.key.startsWith("cha_"));
    },
    enumDefaultKey: TransactionMethodType.CHARGE_ADJUSTMENT_GENERIC
  })
  @JsonProperty()
  method: string = TransactionMethodType.CHARGE_ADJUSTMENT_GENERIC;

  constructor(transaction: TransactionInterface, serviceType: TransactionActionServiceType) {
    super(1, TransactionActionType.CHARGE_ADJUSTMENT, transaction, serviceType);
  }
}

@JsonObject()
export class TransactionActionCreditAdjustment extends BaseTransactionAction {

  @FormGen({
    name: "Type",
    type: "enum",
    required: true,
    noneditable: true,
    _asyncInit: async (metadata: FormGenMetadata) => {
      metadata.enumValues = (await DentwyzePresetListsCache.getInstance().lists.getOrLoadList(PresetListKey.TRANSACTION_METHOD_TYPES))
        .values.filter(val => val.key.startsWith("cra_"));
    },
    enumDefaultKey: TransactionMethodType.CREDIT_ADJUSTMENT_GENERIC
  })
  @JsonProperty()
  method: string = TransactionMethodType.CREDIT_ADJUSTMENT_GENERIC;

  constructor(transaction: TransactionInterface, serviceType: TransactionActionServiceType) {
    super(-1, TransactionActionType.CREDIT_ADJUSTMENT, transaction, serviceType);
  }
}

@JsonObject()
export class Transaction extends BasePracticeListItem implements TransactionInterface {

  patient: PatientProfile;

  @FormGen({
    name: "Patient",
    type: "autocomplete",
    noneditable: true,
    required: true,
    autocompleteCreateAction: patientProfileAutocompleteCreateAction,
    autocompleteLoader: async function (value, inputText) {
      return patientProfileAutocompleteLoaderCreator(ProfileKeyType.ID)();
    },
  })
  @JsonProperty()
  patientId: string;

  provider: ProviderProfile;

  @FormGen({
    name: "Provider",
    type: "autocomplete",
    noneditable: true,
    autocompleteLoader: async function (value, inputText) {
      return providerProfileAutocompleteLoaderCreator(ProfileKeyType.ID)();
    },
  })
  @JsonProperty()
  providerId: string;

  @FormGen({name: "Transaction Date", type: "datenum", required: true})
  @JsonProperty()
  transactionAt: number = Date.now();

  @FormGen({name: "Description", type: "text", size: "xsmall", required: true})
  @JsonProperty()
  description: string;

  @JsonProperty()
  actionType: TransactionActionType;

  @JsonProperty()
  actionPayment: TransactionActionPayment;

  // deprecated. Use actionCharge instead.
  @JsonProperty()
  actionProcedure: TransactionActionCharge;

  @JsonProperty()
  actionCharge: TransactionActionCharge;

  @JsonProperty()
  actionChargeAdjustment: TransactionActionChargeAdjustment;

  @JsonProperty()
  actionCreditAdjustment: TransactionActionCreditAdjustment;

  @FormGen({name: "", type: "object"})
    // Not serialized.
  action: BaseTransactionAction;

  static createNew(patientId: string, actionType: TransactionActionType, serviceType: TransactionActionServiceType): Transaction {
    const transaction = new Transaction(md5_uuid(), patientId);
    transaction.actionType = actionType;
    switch (actionType) {
      case TransactionActionType.PAYMENT:
        transaction.action = new TransactionActionPayment(transaction, serviceType);
        break;
      case TransactionActionType.PROCEDURE:
      case TransactionActionType.CHARGE:
        transaction.action = new TransactionActionCharge(transaction, serviceType);
        break;
      case TransactionActionType.CHARGE_ADJUSTMENT:
        transaction.action = new TransactionActionChargeAdjustment(transaction, serviceType);
        break;
      case TransactionActionType.CREDIT_ADJUSTMENT:
        transaction.action = new TransactionActionCreditAdjustment(transaction, serviceType);
        break;
    }
    return transaction;
  }

  static createNewPayment(patientId: string, serviceType: TransactionActionServiceType): Transaction {
    const transaction = new Transaction(md5_uuid(), patientId);
    transaction.actionType = TransactionActionType.PAYMENT;
    transaction.action = new TransactionActionPayment(transaction, serviceType);
    return transaction;
  }

  static createNewCharge(patientId: string, serviceType: TransactionActionServiceType): Transaction {
    const transaction = new Transaction(md5_uuid(), patientId);
    transaction.actionType = TransactionActionType.CHARGE;
    transaction.action = new TransactionActionCharge(transaction, serviceType);
    return transaction;
  }

  static createNewChargeAdjustment(patientId: string, serviceType: TransactionActionServiceType): Transaction {
    const transaction = new Transaction(md5_uuid(), patientId);
    transaction.actionType = TransactionActionType.CHARGE_ADJUSTMENT;
    transaction.action = new TransactionActionChargeAdjustment(transaction, serviceType);
    return transaction;
  }

  static createNewCreditAdjustment(patientId: string, serviceType: TransactionActionServiceType): Transaction {
    const transaction = new Transaction(md5_uuid(), patientId);
    transaction.actionType = TransactionActionType.CREDIT_ADJUSTMENT;
    transaction.action = new TransactionActionCreditAdjustment(transaction, serviceType);
    return transaction;
  }

  constructor(id: string, patientId: string) {
    super(id);
    this.patientId = patientId;
  }

  getSignedAmount(): number {
    return this.action.getSignedAmount();
  }

  getSignedAmountText(): string {
    return (this.action as BaseTransactionAction).getSignedAmountText();
  }

  static readonly LAB_ORDER_ID_REL_NAME = "labOrderIds";

  labOrderIdRel(): Rel {
    const labOrderId = this.action.readData<TransactionActionServiceDataLabOrder>()?.labOrderId;
    if (!labOrderId) {
      return null;
    }
    return new Rel(Transaction.LAB_ORDER_ID_REL_NAME, labOrderId, this.id, true, true);
  }
}

export class Transactions extends BasePracticeListItemsLoader<Transaction> {

  async loadTransactionsByLabOrderId(labOrderId: string): Promise<Transaction[]> {
    const transactions: Transaction[] = [];
    const val = await this.getProvisioningContext().dbRef_getVal(this.listPath() + Rel.oneToOnePathFor(Transaction.LAB_ORDER_ID_REL_NAME, labOrderId));
    if (val) {
      for (const key of Object.keys(val)) {
        transactions.push(await this.loadListItem(key));
      }
    }
    return transactions;
  }

  protected practiceDataBasePath(): string {
    return "transactions";
  }

  protected deserializeItem(value: any): Transaction {
    return JSON_OBJECT.deserializeObject(value, Transaction);
  }

  protected serializeItem(item: Transaction): any {
    return JSON_OBJECT.serializeObject(item);
  }

  protected sortOrder(item1: Transaction, item2: Transaction): number {
    return item2.transactionAt - item1.transactionAt;
  }

  async addListItem(item: Transaction, ...rels): Promise<void> {
    if (item.action.serviceType === TransactionActionServiceType.LAB_ORDER) {
      rels = [...(rels || []), item.labOrderIdRel()].filter(Boolean);
    }
    return super.addListItem(item, ...rels);
  }

  protected async onBeforeItemSerialized(item: Transaction): Promise<Transaction> {
    await super.onBeforeItemSerialized(item);
    switch (item.actionType) {
      default:
      case TransactionActionType.PAYMENT:
        item.actionPayment = item.action as TransactionActionPayment;
        break;
      case TransactionActionType.PROCEDURE:
      case TransactionActionType.CHARGE:
        item.actionCharge = item.action as TransactionActionCharge;
        break;
      case TransactionActionType.CHARGE_ADJUSTMENT:
        item.actionChargeAdjustment = item.action as TransactionActionChargeAdjustment;
        break;
      case TransactionActionType.CREDIT_ADJUSTMENT:
        item.actionCreditAdjustment = item.action as TransactionActionCreditAdjustment;
        break;
    }
    return item;
  }

  protected async onAfterItemDeserialized(item: Transaction): Promise<Transaction> {
    await item.onAfterItemDeserialized();
    item.patient = await PracticeData.shared().patientProfiles.getOrLoadItem(item.patientId);
    if (item.providerId) {
      item.provider = await PracticeData.shared().providerProfiles.getOrLoadItem(item.providerId);
    }
    switch (item.actionType) {
      default:
      case TransactionActionType.PAYMENT:
        item.action = item.actionPayment;
        break;
      case TransactionActionType.PROCEDURE:
        item.actionProcedure.onAfterItemDeserialized();
        item.action = item.actionProcedure;
        break;
      case TransactionActionType.CHARGE:
        item.actionCharge.onAfterItemDeserialized();
        item.action = item.actionCharge;
        break;
      case TransactionActionType.CHARGE_ADJUSTMENT:
        item.action = item.actionChargeAdjustment;
        break;
      case TransactionActionType.CREDIT_ADJUSTMENT:
        item.action = item.actionCreditAdjustment;
        break;
    }
    return item;
  }
}

export enum StatementDataDateRange {
  TODAY = "today",
  SINCE_LAST_ZERO_BALANCE = "sinceLastZeroBalance",
}

const STATEMENT_DATA_DATE_RANGE_KTS = new KeyTextStrings([
  $KTS(StatementDataDateRange.TODAY, "Today"),
  $KTS(StatementDataDateRange.SINCE_LAST_ZERO_BALANCE, "Since Last Zero Balance"),
]);

@JsonObject()
export class StatementData {

  @JsonProperty()
  readonly name: string;

  @FormGen({
    name: "Patient",
    type: "autocomplete",
    noneditable: true,
    required: true,
    autocompleteCreateAction: patientProfileAutocompleteCreateAction,
    autocompleteLoader: async function (value, inputText) {
      return patientProfileAutocompleteLoaderCreator(ProfileKeyType.ID)();
    },
  })
  @JsonProperty()
  readonly patientId: string;

  @FormGen({
    name: "Date Range",
    type: "enum",
    enumVariant: "radio",
    enumValues: STATEMENT_DATA_DATE_RANGE_KTS.values,
    enumDefaultKey: StatementDataDateRange.SINCE_LAST_ZERO_BALANCE
  })
  @JsonProperty()
  dateRange: string = StatementDataDateRange.SINCE_LAST_ZERO_BALANCE;

  @FormGen({name: "Message", type: "text", size: "xsmall"})
  @JsonProperty()
  message: string;

  @FormGen({name: "Due date", type: "datenum"})
  @JsonProperty()
  dueDate: number;

  @JsonProperty()
  transactionIds: string[];

  @JsonProperty()
  prevBalance: number;

  @JsonProperty()
  credits: number;

  @JsonProperty()
  newCharges: number;

  @JsonProperty()
  totalBalance: number;

  transactions: Transaction[];

  constructor(patientId: string, transactions: Transaction[]) {
    this.name = `Statement-${DateUtil.toDatestamp(new Date())}`;
    this.patientId = patientId;
    this.dueDate = Date.now() + 31 * DAY_IN_MILLIS;
    this.transactions = transactions;
  }

  calculate(): void {
    let prevBalance = 0, credits = 0, newCharges = 0, totalBalance = 0;
    const transactionIds: string[] = [];
    if (this.transactions?.length > 0) {
      // Sort oldest to newest.
      const transactions = [...this.transactions].filter(t => t.patientId === this.patientId);
      transactions.sort((a, b) => a.transactionAt - b.transactionAt);
      const runningBalance: number[] = [];
      let prevBalance: number = 0;
      for (const transaction of transactions) {
        const amount = transaction.getSignedAmount();
        const balance = prevBalance + amount;
        runningBalance.push(balance);
        prevBalance = balance;
      }
      // Reverse
      transactions.reverse();
      runningBalance.reverse();
      const now = dayjs();
      let index = 0;
      for (const transaction of transactions) {
        const date = dayjs(transaction.transactionAt);
        if (
          (this.dateRange === StatementDataDateRange.TODAY && now.diff(date, 'day') > 1)
          || (this.dateRange === StatementDataDateRange.SINCE_LAST_ZERO_BALANCE && transactionIds.length > 0 && runningBalance[index] === 0)) {
          prevBalance = runningBalance[index];
          break;
        }
        const signedAmount = transaction.getSignedAmount();
        if (signedAmount > 0) {
          newCharges += signedAmount;
        } else {
          credits += -signedAmount;
        }
        totalBalance += signedAmount;
        transactionIds.push(transaction.id);
        index++;
      }
    }
    transactionIds.reverse();
    this.prevBalance = prevBalance;
    this.credits = credits;
    this.newCharges = newCharges;
    this.totalBalance = totalBalance;
    this.transactionIds = transactionIds;
  }
}

@JsonObject()
export class Statement extends BasePracticeListItem {

  patient: PatientProfile;

  @JsonProperty()
  readonly data: StatementData;

  @JsonProperty()
  readonly fileUrl: string;

  static createNew(data: StatementData, fileUrl: string): Statement {
    return new Statement(md5_uuid(), data, fileUrl);
  }

  constructor(id: string, data: StatementData, fileUrl: string) {
    super(id);
    this.data = data;
    this.fileUrl = fileUrl;
  }

  static readonly PATIENT_ID_REL_NAME = "patientIds";

  patientIdRel(): Rel {
    return new Rel(Statement.PATIENT_ID_REL_NAME, this.data.patientId, this.id, true, true);
  }
}

export class Statements extends BasePracticeListItemsLoader<Statement> {

  constructor(practiceId: string) {
    super(practiceId);
  }

  async loadStatementsByPatientProfile(patientProfile: PatientProfile): Promise<Statement[]> {
    const statements: Statement[] = [];
    const val = await this.getProvisioningContext().dbRef_getVal(this.listPath() + Rel.oneToOnePathFor(Statement.PATIENT_ID_REL_NAME, patientProfile.id));
    if (val) {
      for (const key of Object.keys(val)) {
        statements.push(await this.loadListItem(key));
      }
    }
    return statements;
  }

  protected practiceDataBasePath(): string {
    return "statements";
  }

  async addStatement(statement: Statement): Promise<void> {
    return super.addListItem(statement, statement.patientIdRel());
  }

  async deleteStatement(statement: Statement): Promise<void> {
    return super.deleteListItem(statement, statement.patientIdRel());
  }

  protected deserializeItem(value: any): Statement {
    return JSON_OBJECT.deserializeObject(value, Statement);
  }

  protected serializeItem(item: Statement): any {
    return JSON_OBJECT.serializeObject(item);
  }

  protected async onAfterItemDeserialized(item: Statement): Promise<Statement> {
    await super.onAfterItemDeserialized(item);
    item.patient = await PracticeData.shared().patientProfiles.getOrLoadItem(item.data.patientId);
    return item;
  }

  protected sortOrder(item1: Statement, item2: Statement): number {
    return item2.created - item1.created;
  }
}

/// Payment Modes
export enum PaymentType {
  CASH = "cash",
  CHECK = "check",
  CARD = "card",
  ONLINE = "online",
  OTHER = "other",
}

export const PAYMENT_TYPE_KTS = new KeyTextStrings([
  $KTS(PaymentType.CASH, "Cash"),
  $KTS(PaymentType.CHECK, "Check"),
  $KTS(PaymentType.CARD, "Card"),
  $KTS(PaymentType.ONLINE, "Online"),
  $KTS(PaymentType.OTHER, "Other"),
]);

export class PresetPaymentMode extends Preset {

  constructor(readonly name: string, readonly paymentType: PaymentType, readonly vendorFee: string) {
    super(paymentType);
  }

  toString(): string {
    return this.name;
  }
}

const PAYMENT_MODE_PRESETS = new DefinedPresets<PresetPaymentMode>(new PresetsKey(PresetListKey.PAYMENT_MODES), [
  new PresetPaymentMode("Cash", PaymentType.CASH, "0.0"),
  new PresetPaymentMode("Check", PaymentType.CHECK, "0.0"),
  new PresetPaymentMode("Card", PaymentType.CARD, "0.0"),
  new PresetPaymentMode("Online", PaymentType.ONLINE, "0.0"),
  new PresetPaymentMode("Other", PaymentType.OTHER, "0.0"),
]);

PRESET_LIST_KTM.add(
  $KTM(PresetListKey.PAYMENT_MODES, {type: "defined", definedPresets: PAYMENT_MODE_PRESETS}),
);

@JsonObject()
export class PaymentMode extends BasePracticeListItem {

  @FormGen({name: "Name", type: "string", required: true})
  @JsonProperty()
  name: string;

  @FormGen({
    name: "Payment Type",
    type: "enum",
    enumValues: PAYMENT_TYPE_KTS.values,
    enumDefaultKey: PaymentType.CASH,
    required: true,
  })
  @JsonProperty()
  paymentType: string = PaymentType.CASH;

  @FormGen({name: "Vendor Fee", type: "string", required: true})
  @JsonProperty()
  vendorFee: string;

  @FormGen({
    name: "Enabled",
    type: "boolean",
    required: true
  })
  @JsonProperty()
  enabled: boolean = true;

  static createFromPreset(preset: PresetPaymentMode): PaymentMode {
    const mode = new PaymentMode(preset.id);
    mode.name = preset.name;
    mode.paymentType = preset.paymentType;
    mode.vendorFee = preset.vendorFee;
    return mode;
  }

  static createNew(): PaymentMode {
    const mode = new PaymentMode(md5_uuid());
    return mode;
  }

  constructor(id: string) {
    super(id);
  }
}

export class PaymentModes extends BasePracticeListItemsLoader<PaymentMode> {

  constructor(practiceId: string) {
    super(practiceId);
  }

  async loadListItems(): Promise<void> {
    await super.loadListItems();
    const paymentModes = this.getListItems();
    const all: Promise<void>[] = [];
    for (const preset of PAYMENT_MODE_PRESETS.list) {
      if (!paymentModes.find(mode => mode.id === preset.id)) {
        const item = PaymentMode.createFromPreset(preset);
        paymentModes.push(item);
        all.push(this.addListItem(item));
      }
    }
    await Promise.all(all);
  }

  protected practiceDataBasePath(): string {
    return "presets/paymentModes";
  }

  protected deserializeItem(value: any): PaymentMode {
    return JSON_OBJECT.deserializeObject(value, PaymentMode);
  }

  protected serializeItem(item: PaymentMode): any {
    return JSON_OBJECT.serializeObject(item);
  }

  protected sortOrder(item1: PaymentMode, item2: PaymentMode): number {
    return item1.name.localeCompare(item2.name);
  }
}

export class PresetTax extends Preset {

  constructor(readonly name: string, readonly value: string) {
    super(StringUtil.toSlug(name));
  }

  toString(): string {
    return this.name;
  }
}

const TAX_PRESETS = new DefinedPresets<PresetTax>(new PresetsKey(PresetListKey.TAXES), [
  new PresetTax("Service Tax", "0.0"),
]);

PRESET_LIST_KTM.add(
  $KTM(PresetListKey.TAXES, {type: "defined", definedPresets: TAX_PRESETS}),
);

@JsonObject()
export class Tax extends BasePracticeListItem {

  @FormGen({name: "Name", type: "string"})
  @JsonProperty()
  name: string;

  @FormGen({name: "Value (%)", type: "string", inputMode: "numeric", required: true})
  @JsonProperty()
  value: string = "0.0";

  @FormGen({
    name: "Enabled",
    type: "boolean",
    required: true
  })
  @JsonProperty()
  enabled: boolean = true;

  static createFromPreset(preset: PresetTax): Tax {
    const tax = new Tax(preset.id);
    tax.name = preset.name;
    tax.value = preset.value;
    return tax;
  }

  static createNew(): Tax {
    const tax = new Tax(md5_uuid());
    return tax;
  }

  constructor(id: string) {
    super(id);
  }
}

export class Taxes extends BasePracticeListItemsLoader<Tax> {

  constructor(practiceId: string) {
    super(practiceId);
  }

  async loadListItems(): Promise<void> {
    await super.loadListItems();
    const taxes = this.getListItems();
    const all: Promise<void>[] = [];
    for (const preset of TAX_PRESETS.list) {
      if (!taxes.find(tax => tax.id === preset.id)) {
        const item = Tax.createFromPreset(preset);
        taxes.push(item);
        all.push(this.addListItem(item));
      }
    }
    await Promise.all(all);
  }

  protected practiceDataBasePath(): string {
    return "presets/taxes";
  }

  protected deserializeItem(value: any): Tax {
    return JSON_OBJECT.deserializeObject(value, Tax);
  }

  protected serializeItem(item: Tax): any {
    return JSON_OBJECT.serializeObject(item);
  }

  protected sortOrder(item1: Tax, item2: Tax): number {
    return item1.name.localeCompare(item2.name);
  }
}

export class PresetExpenseType extends Preset {

  constructor(readonly name: string) {
    super(StringUtil.toSlug(name));
  }

  toString(): string {
    return this.name;
  }
}

const EXPENSE_TYPE_PRESETS = new DefinedPresets<PresetExpenseType>(new PresetsKey(PresetListKey.EXPENSE_TYPES), [
  new PresetExpenseType("Electricity Bill"),
  new PresetExpenseType("Lab Charges"),
  new PresetExpenseType("Materials"),
  new PresetExpenseType("Membership Expense"),
  new PresetExpenseType("Others"),
  new PresetExpenseType("Personal"),
  new PresetExpenseType("Salary"),
  new PresetExpenseType("Telephone Bill"),
  new PresetExpenseType("Water Bill"),
]);

PRESET_LIST_KTM.add(
  $KTM(PresetListKey.EXPENSE_TYPES, {type: "defined", definedPresets: EXPENSE_TYPE_PRESETS}),
);

@JsonObject()
export class ExpenseType extends BasePracticeListItem {

  @FormGen({name: "Name", type: "string", required: true})
  @JsonProperty()
  name: string;

  @FormGen({
    name: "Enabled",
    type: "boolean",
    required: true
  })
  @JsonProperty()
  enabled: boolean = true;

  static createFromPreset(preset: PresetExpenseType): ExpenseType {
    const mode = new ExpenseType(preset.id);
    mode.name = preset.name;
    return mode;
  }

  static createNew(): ExpenseType {
    const mode = new ExpenseType(md5_uuid());
    return mode;
  }

  constructor(id: string) {
    super(id);
  }
}

export class ExpenseTypes extends BasePracticeListItemsLoader<ExpenseType> {

  constructor(practiceId: string) {
    super(practiceId);
  }

  async loadListItems(): Promise<void> {
    await super.loadListItems();
    const expenseTypes = this.getListItems();
    const all: Promise<void>[] = [];
    for (const preset of EXPENSE_TYPE_PRESETS.list) {
      if (!expenseTypes.find(mode => mode.id === preset.id)) {
        const item = ExpenseType.createFromPreset(preset);
        expenseTypes.push(item);
        all.push(this.addListItem(item));
      }
    }
    await Promise.all(all);
  }

  protected practiceDataBasePath(): string {
    return "presets/expenseTypes";
  }

  protected deserializeItem(value: any): ExpenseType {
    return JSON_OBJECT.deserializeObject(value, ExpenseType);
  }

  protected serializeItem(item: ExpenseType): any {
    return JSON_OBJECT.serializeObject(item);
  }

  protected sortOrder(item1: ExpenseType, item2: ExpenseType): number {
    return item1.name.localeCompare(item2.name);
  }
}

export const expenseTypeAutocompleteCreateAction = new Action("Add Expense Type", (event, input: string) => {
  EditExpenseTypeHelper.addExpenseType();
}, AddOutlined);

export const expenseTypeAutocompleteLoader = (): Promise<KeyTextString[]> => {
  const expenseTypes = PracticeData.provider().presets.expenseTypes;
  return new Promise<KeyTextString[]>((resolve, reject) => {
    expenseTypes.getOrLoadListItems()
      .then(types => resolve(types.filter(type => type.enabled).map(proc => $KTS(proc.id, proc.name))))
      .catch(reason => reject(reason));
  });
}


/// Expenses

@JsonObject()
export class Expense extends BasePracticeListItem {

  @FormGen({
    name: "Expense Type",
    type: "autocomplete",
    required: true,
    autocompleteCreateAction: expenseTypeAutocompleteCreateAction,
    autocompleteLoader: expenseTypeAutocompleteLoader,
  })
  @JsonProperty()
  expenseType: string;

  @FormGen({name: "Date", type: "datenum", required: true})
  @JsonProperty()
  date: number;

  @FormGen({name: "Amount", type: "currency", required: true})
  @JsonProperty()
  amount: number;

  @FormGen({name: "Attachments", type: "array_file", description: "Attach related files, e.g. receipt, invoice etc."})
  @JsonProperty()
  attachments: string;

  static createNew(): Expense {
    return new Expense(md5_uuid());
  }

  constructor(id: string) {
    super(id);
  }

  getAmountText(): string {
    return CurrencyText(this.amount);
  }
}

export class Expenses extends BasePracticeListItemsLoader<Expense> {

  constructor(practiceId: string) {
    super(practiceId);
  }

  protected practiceDataBasePath(): string {
    return "expenses";
  }

  protected deserializeItem(value: any): Expense {
    return JSON_OBJECT.deserializeObject(value, Expense);
  }

  protected serializeItem(item: Expense): any {
    return JSON_OBJECT.serializeObject(item);
  }

  protected sortOrder(item1: Expense, item2: Expense): number {
    return item2.created - item1.created;
  }
}

/// Vendors

@JsonObject()
export class Vendor extends BasePracticeListItem {

  @FormGen({name: "Name", type: "string", required: true})
  @JsonProperty()
  name: string;

  @FormGen({
    name: "Expense Type",
    type: "autocomplete",
    required: false,
    autocompleteCreateAction: expenseTypeAutocompleteCreateAction,
    autocompleteLoader: expenseTypeAutocompleteLoader,
  })
  @JsonProperty()
  expenseType: string;

  @FormGen({
    name: "Contacts",
    type: "array_string",
  })
  @JsonProperty()
  contacts: string[];

  @FormGen({
    name: "Note",
    type: "text",
    size: "xsmall",
  })
  @JsonProperty()
  note: string;

  static createNew(): Vendor {
    return new Vendor(md5_uuid());
  }

  constructor(id: string) {
    super(id);
  }
}

export class Vendors extends BasePracticeListItemsLoader<Vendor> {

  constructor(practiceId: string) {
    super(practiceId);
  }

  protected practiceDataBasePath(): string {
    return "vendors";
  }

  protected deserializeItem(value: any): Vendor {
    return JSON_OBJECT.deserializeObject(value, Vendor);
  }

  protected serializeItem(item: Vendor): any {
    return JSON_OBJECT.serializeObject(item);
  }

  protected sortOrder(item1: Vendor, item2: Vendor): number {
    return item2.created - item1.created;
  }
}

/// Suppliers

export enum SupplierType {
  DENTAL_LAB = "dental_lab",
  PATHOLOGY_LAB = "pathology_lab",
  DENTAL_SUPPLIER = "dental_supplier",
  OTHER = "other",
}

export const SUPPLIER_TYPE_KTS = new KeyTextStrings([
  $KTS(SupplierType.DENTAL_LAB, "Dental Lab"),
  $KTS(SupplierType.PATHOLOGY_LAB, "Pathology Lab"),
  $KTS(SupplierType.DENTAL_SUPPLIER, "Dental Supplier"),
  $KTS(SupplierType.OTHER, "Other"),
]);

@JsonObject()
export class Supplier extends BasePracticeListItem {

  @FormGen({name: "Name", type: "string", required: true})
  @JsonProperty()
  name: string;

  @FormGen({
    name: "Supplier Type",
    type: "enum",
    enumValues: SUPPLIER_TYPE_KTS.values,
    enumDefaultKey: SupplierType.OTHER,
    required: true,
  })
  @JsonProperty()
  supplierType: string = SupplierType.OTHER;

  @FormGen({
    name: "Contacts",
    type: "array_string",
  })
  @JsonProperty()
  contacts: string[];

  @FormGen({
    name: "Note",
    type: "text",
    size: "xsmall",
  })
  @JsonProperty()
  note: string;

  static createNew(): Supplier {
    return new Supplier(md5_uuid());
  }

  constructor(id: string) {
    super(id);
  }
}

export class Suppliers extends BasePracticeListItemsLoader<Supplier> {

  constructor(practiceId: string) {
    super(practiceId);
  }

  protected practiceDataBasePath(): string {
    return "suppliers";
  }

  protected deserializeItem(value: any): Supplier {
    return JSON_OBJECT.deserializeObject(value, Supplier);
  }

  protected serializeItem(item: Supplier): any {
    return JSON_OBJECT.serializeObject(item);
  }

  protected sortOrder(item1: Supplier, item2: Supplier): number {
    return (item1.name || "").localeCompare(item2.name || "");
  }
}

/// Inventory SKUs

export class PresetInventorySku extends Preset {
  constructor(readonly name: string) {
    super(StringUtil.toSlug(name));
  }

  toString(): string {
    return this.name;
  }
}

const INVENTORY_SKU_PRESETS = new DefinedPresets<PresetInventorySku>(
  new PresetsKey(PresetListKey.INVENTORY_SKUS),
  [
    new PresetInventorySku("Composite Resin"),
    new PresetInventorySku("Dental Cement"),
    new PresetInventorySku("Dental Burs"),
    new PresetInventorySku("Dental Gloves"),
    new PresetInventorySku("Dental Impression Material"),
    new PresetInventorySku("Dental Needles"),
    new PresetInventorySku("Dental Wax"),
    new PresetInventorySku("Face Masks"),
    new PresetInventorySku("Gutta Percha"),
    new PresetInventorySku("Local Anaesthetic"),
    new PresetInventorySku("Orthodontic Brackets"),
    new PresetInventorySku("Suture Material"),
    new PresetInventorySku("X-Ray Film"),
  ]
);

@JsonObject()
export class InventorySku extends BasePracticeListItem {

  @FormGen({name: "Name", type: "string", required: true})
  @JsonProperty()
  name: string;

  @FormGen({name: "Enabled", type: "boolean", required: true})
  @JsonProperty()
  enabled: boolean = true;

  static createFromPreset(preset: PresetInventorySku): InventorySku {
    const sku = new InventorySku(preset.id);
    sku.name = preset.name;
    return sku;
  }

  static createNew(): InventorySku {
    return new InventorySku(md5_uuid());
  }

  constructor(id: string) {
    super(id);
  }
}

export class InventorySkus extends BasePracticeListItemsLoader<InventorySku> {

  constructor(practiceId: string) {
    super(practiceId);
  }

  async loadListItems(): Promise<void> {
    await super.loadListItems();
    const skus = this.getListItems();
    const all: Promise<void>[] = [];
    for (const preset of INVENTORY_SKU_PRESETS.list) {
      if (!skus.find(sku => sku.id === preset.id)) {
        const item = InventorySku.createFromPreset(preset);
        skus.push(item);
        all.push(this.addListItem(item));
      }
    }
    await Promise.all(all);
  }

  protected practiceDataBasePath(): string {
    return "presets/inventorySkus";
  }

  protected deserializeItem(value: any): InventorySku {
    return JSON_OBJECT.deserializeObject(value, InventorySku);
  }

  protected serializeItem(item: InventorySku): any {
    return JSON_OBJECT.serializeObject(item);
  }

  protected sortOrder(item1: InventorySku, item2: InventorySku): number {
    return item1.name.localeCompare(item2.name);
  }
}

export const inventorySkuAutocompleteCreateAction = new Action("Add SKU", (event, input: string) => {
  EditInventorySkuHelper.addInventorySku();
}, AddOutlined);

export const inventorySkuAutocompleteLoader = (): Promise<KeyTextString[]> => {
  const skus = PracticeData.provider().presets.inventorySkus;
  return new Promise<KeyTextString[]>((resolve, reject) => {
    skus.getOrLoadListItems()
      .then(items => resolve(items.filter(sku => sku.enabled).map(sku => $KTS(sku.id, sku.name))))
      .catch(reason => reject(reason));
  });
}


/// InventoryItems

@JsonObject()
export class InventoryItem extends BasePracticeListItem {

  sku: InventorySku;

  @FormGen({
    name: "SKU",
    type: "autocomplete",
    required: true,
    autocompleteCreateAction: inventorySkuAutocompleteCreateAction,
    autocompleteLoader: inventorySkuAutocompleteLoader,
  })
  @JsonProperty()
  skuId: string;

  @FormGen({name: "Price", type: "currency", required: true})
  @JsonProperty()
  price: number;

  @FormGen({name: "Quantity", type: "number", required: true})
  @JsonProperty()
  quantity: number;

  async onAfterItemDeserialized(): Promise<void> {
    this.sku = await PracticeData.provider().presets.inventorySkus.getOrLoadItem(this.skuId);
  }

  static createNew(): InventoryItem {
    return new InventoryItem(md5_uuid());
  }

  constructor(id: string) {
    super(id);
  }
}

export class InventoryItems extends BasePracticeListItemsLoader<InventoryItem> {

  constructor(practiceId: string) {
    super(practiceId);
  }

  protected practiceDataBasePath(): string {
    return "inventoryItems";
  }

  protected deserializeItem(value: any): InventoryItem {
    return JSON_OBJECT.deserializeObject(value, InventoryItem);
  }

  protected serializeItem(item: InventoryItem): any {
    return JSON_OBJECT.serializeObject(item);
  }

  protected sortOrder(item1: InventoryItem, item2: InventoryItem): number {
    return item2.created - item1.created;
  }
}

/// Medicines

export type MedicineContainer =
  "Ampoule"
  | "Bottle"
  | "Box"
  | "Canister"
  | "Cartridge"
  | "Combo Pack"
  | "Disposable Pen"
  | "Flexpen"
  | "Jar"
  | "Kit"
  | "Packet"
  | "Penfill"
  | "Pouch"
  | "Pre-filled Pen"
  | "Prefilled Syringe"
  | "Pump Bottle"
  | "Roll-on Bottle"
  | "Sachet"
  | "Strip"
  | "Tube"
  | "Vial";

export type MedicineUnit = 'ml' | 'gm' | 'mg' | 'ltr';

export type MedicineModifier =
  "CR"
  | "DR"
  | "DT"
  | "EC"
  | "ER"
  | "FC"
  | "IR"
  | "MD"
  | "MR"
  | "OPS"
  | "PR"
  | "SR"
  | "TR"
  | "VT"
  | "XL"
  | "XR";

export type MedicineFormulation =
  "Alicaps"
  | "Ampules"
  | "Bar"
  | "Bio-Adhesive"
  | "Body Wash"
  | "Bottle"
  | "Bottles"
  | "Boxes"
  | "Caplets"
  | "Capsule"
  | "Capsule Combipack"
  | "Capsules"
  | "Captabs"
  | "Cartridge"
  | "Cartriges"
  | "Chewable Tablet"
  | "Chewable Tablets"
  | "Chewing Gums"
  | "Combo Pack"
  | "Condition"
  | "Cream"
  | "Creams"
  | "Dental Gel"
  | "Device"
  | "Disintegrating Strip"
  | "Disintegrating Strips"
  | "Drop"
  | "Dry Syrup"
  | "Dusting Powd"
  | "Ear Drop"
  | "Effervescent Tablet"
  | "Elix"
  | "Emulsion"
  | "Enema"
  | "Expectorant"
  | "Eye Capsule"
  | "Eye Capsules"
  | "Eye Dr"
  | "Eye Drop"
  | "Eye Gel"
  | "Eye Ointment"
  | "Eye/Ear Dr"
  | "Eye/ear Dr"
  | "Face Wash"
  | "Foam"
  | "Gargle"
  | "Gel"
  | "Gelatin Coated Tablets"
  | "Gels"
  | "Grains"
  | "Granules"
  | "Gum"
  | "Gum Paint"
  | "Husk"
  | "Infusion"
  | "Infusions"
  | "Inhal"
  | "Injection"
  | "Injections"
  | "Instacaps"
  | "Intrauterine System"
  | "Jelly"
  | "Kit"
  | "Linctus"
  | "Liniment"
  | "Liquid"
  | "Lotion"
  | "Lozenges"
  | "MDI Autohal"
  | "MDI Inhal"
  | "MDI Multihal"
  | "MDI Nasal Solution"
  | "MDI Nasal Spray"
  | "MDI Respicap"
  | "MDI Sublingual Spray"
  | "MDI Transhal"
  | "Moisturis"
  | "Mouth Paint"
  | "Mouth Spray"
  | "Mouth Wash"
  | "Nail Lacqu"
  | "Nasal Dr"
  | "Nasal Solution"
  | "Nasal Spray"
  | "Nebulisers"
  | "Oil"
  | "Ointment"
  | "Ophthalmic Emulsion"
  | "Ophthalmic Emulsions"
  | "Ophthalmic Gel"
  | "Ophthalmic Solution"
  | "Ophthalmic Suspension"
  | "Oral Dr"
  | "Oral Emulsion"
  | "Oral Gel"
  | "Oral Jelly"
  | "Oral Liquid"
  | "Oral Paste"
  | "Oral Solution"
  | "Oral Solutions"
  | "Oral Suspension"
  | "Oral Suspensions"
  | "Paint"
  | "Particles"
  | "Paste"
  | "Patch"
  | "Patches"
  | "Pellet"
  | "Pellets"
  | "Pen"
  | "Pessaries"
  | "Pouch"
  | "Powd"
  | "Powder for Eye Drop"
  | "Powder for Infusion"
  | "Powder for Inhalation"
  | "Powder for Injection"
  | "Powder for Oral Solution"
  | "Powder for Oral Suspension"
  | "Powders"
  | "Pre-filled Pen"
  | "Prefilled Syringe"
  | "Rectal Cream"
  | "Rectal Gel"
  | "Rectal Ointment"
  | "Redicaps"
  | "Redimix Suspension"
  | "Rediuse Suspension"
  | "Respicaps"
  | "Respules"
  | "Rheocap"
  | "Rotacaps"
  | "Sachet"
  | "Sachets"
  | "Scrub"
  | "Serum"
  | "Shampoo"
  | "Shampoos"
  | "Soap"
  | "Soflets"
  | "Soft Gelatin Capsule"
  | "Soft Gelatin Capsules"
  | "Solution"
  | "Solution for Infusion"
  | "Solution for Inhalation"
  | "Solution for Injection"
  | "Solutions"
  | "Spray"
  | "Strips"
  | "Sublingual Spray"
  | "Sublingual Tablet"
  | "Suppositories"
  | "Suppository"
  | "Suspension"
  | "Suspension for Injection"
  | "Syrup"
  | "Tabcaps"
  | "Tablet"
  | "Tablet Combikit"
  | "Tablet Combipack"
  | "Tablets"
  | "Tonic"
  | "Toothpaste"
  | "Transcaps"
  | "Transdermal Patch"
  | "Transdermal Patches"
  | "Transpules"
  | "Tubes"
  | "Unit"
  | "Vaccine"
  | "Vaginal Capsule"
  | "Vaginal Capsules"
  | "Vaginal Cream"
  | "Vaginal Gel"
  | "Vaginal Ointment"
  | "Vaginal Spray"
  | "Vaginal Suppository";

export type MedicineLabel = {
  c: MedicineContainer,
  n: number,
  u?: MedicineUnit,
  f: MedicineFormulation,
  m?: MedicineModifier,
}

export type MedicineTypeMini = {
  id: string;
  slug: string;
  name: string;
}

// These are types and not classes as we don't store them in the db yet (instead they're read from a json file)
export type MedicineType = MedicineTypeMini & {
  tId: number,
  tSlug: string,
  manufacturedBy: string,
  marketedBy: string,
  discontinued?: boolean,
  available?: boolean,
  tags?: string[],
  label: MedicineLabel,
  composition: string,
  imageUrl?: string,
  quantity?: number,
}

/// SMS Templates

export class PresetSmsTemplate extends Preset {

  constructor(readonly name: string, readonly value: string) {
    super(StringUtil.toSlug(name));
  }

  toString(): string {
    return this.name;
  }
}

const SMS_TEMPLATE_PRESETS = new DefinedPresets<PresetSmsTemplate>(new PresetsKey(PresetListKey.SMS_TEMPLATES), []);

PRESET_LIST_KTM.add(
  $KTM(PresetListKey.SMS_TEMPLATES, {type: "defined", definedPresets: SMS_TEMPLATE_PRESETS}),
);

@JsonObject()
export class SmsTemplate extends BasePracticeListItem {

  @FormGen({name: "Name", type: "string"})
  @JsonProperty()
  name: string;

  @FormGen({name: "Content", type: "text"})
  @JsonProperty()
  content: string;

  static createFromPreset(preset: PresetSmsTemplate): SmsTemplate {
    const smsTemplate = new SmsTemplate(preset.id);
    smsTemplate.name = preset.name;
    smsTemplate.content = preset.value;
    return smsTemplate;
  }

  static createNew(): SmsTemplate {
    const smsTemplate = new SmsTemplate(md5_uuid());
    return smsTemplate;
  }

  constructor(id: string) {
    super(id);
  }
}

export class SmsTemplates extends BasePracticeListItemsLoader<SmsTemplate> {

  constructor(practiceId: string) {
    super(practiceId);
  }

  async loadListItems(): Promise<void> {
    await super.loadListItems();
    const smsTemplates = this.getListItems();
    const all: Promise<void>[] = [];
    for (const preset of SMS_TEMPLATE_PRESETS.list) {
      if (!smsTemplates.find(smsTemplate => smsTemplate.id === preset.id)) {
        const item = SmsTemplate.createFromPreset(preset);
        smsTemplates.push(item);
        all.push(this.addListItem(item));
      }
    }
    await Promise.all(all);
  }

  protected practiceDataBasePath(): string {
    return "templates/sms";
  }

  protected deserializeItem(value: any): SmsTemplate {
    return JSON_OBJECT.deserializeObject(value, SmsTemplate);
  }

  protected serializeItem(item: SmsTemplate): any {
    return JSON_OBJECT.serializeObject(item);
  }

  protected sortOrder(item1: SmsTemplate, item2: SmsTemplate): number {
    return item1.name.localeCompare(item2.name);
  }
}

/// Email Templates

export class PresetEmailTemplate extends Preset {

  constructor(readonly name: string, readonly value: string) {
    super(StringUtil.toSlug(name));
  }

  toString(): string {
    return this.name;
  }
}

const EMAIL_TEMPLATE_PRESETS = new DefinedPresets<PresetEmailTemplate>(new PresetsKey(PresetListKey.EMAIL_TEMPLATES), []);

PRESET_LIST_KTM.add(
  $KTM(PresetListKey.EMAIL_TEMPLATES, {type: "defined", definedPresets: EMAIL_TEMPLATE_PRESETS}),
);

@JsonObject()
export class EmailTemplate extends BasePracticeListItem {

  @FormGen({name: "Name", type: "string"})
  @JsonProperty()
  name: string;

  @FormGen({name: "Subject", type: "string"})
  @JsonProperty()
  subject: string;

  @FormGen({name: "Content", type: "text", size: "small"})
  @JsonProperty()
  content: string;

  @FormGen({name: "Attachments", type: "array_file"})
  @JsonProperty()
  attachments: string[];

  static createFromPreset(preset: PresetEmailTemplate): EmailTemplate {
    const emailTemplate = new EmailTemplate(preset.id);
    emailTemplate.name = preset.name;
    emailTemplate.content = preset.value;
    return emailTemplate;
  }

  static createNew(): EmailTemplate {
    const emailTemplate = new EmailTemplate(md5_uuid());
    return emailTemplate;
  }

  constructor(id: string) {
    super(id);
  }
}

export class EmailTemplates extends BasePracticeListItemsLoader<EmailTemplate> {

  constructor(practiceId: string) {
    super(practiceId);
  }

  async loadListItems(): Promise<void> {
    await super.loadListItems();
    const emailTemplates = this.getListItems();
    const all: Promise<void>[] = [];
    for (const preset of EMAIL_TEMPLATE_PRESETS.list) {
      if (!emailTemplates.find(emailTemplate => emailTemplate.id === preset.id)) {
        const item = EmailTemplate.createFromPreset(preset);
        emailTemplates.push(item);
        all.push(this.addListItem(item));
      }
    }
    await Promise.all(all);
  }

  protected practiceDataBasePath(): string {
    return "templates/email";
  }

  protected deserializeItem(value: any): EmailTemplate {
    return JSON_OBJECT.deserializeObject(value, EmailTemplate);
  }

  protected serializeItem(item: EmailTemplate): any {
    return JSON_OBJECT.serializeObject(item);
  }

  protected sortOrder(item1: EmailTemplate, item2: EmailTemplate): number {
    return item1.name.localeCompare(item2.name);
  }
}

/// Import / Export

export enum ExportRequestStatus {
  NONE = "none",
  QUEUED = "queued",
  SUCCESS = "success",
  ERROR = "error",
}

export const EXPORT_REQUEST_STATUS_KTS = new KeyTextStrings([
  $KTS(ExportRequestStatus.NONE, "None"),
  $KTS(ExportRequestStatus.QUEUED, "Queued"),
  $KTS(ExportRequestStatus.SUCCESS, "Success"),
  $KTS(ExportRequestStatus.ERROR, "Error"),
]);

export enum ExportRequestField {
  PATIENTS = "patients",
  CALENDAR = "calendar",
  TREATMENTS = "treatments",
  PRESCRIPTIONS = "prescriptions",
  BILLING = "billing",
}

export const EXPORT_REQUEST_FIELD_KTS = new KeyTextStrings([
  $KTS(ExportRequestField.PATIENTS, "Patients"),
  $KTS(ExportRequestField.CALENDAR, "Calendar"),
  $KTS(ExportRequestField.TREATMENTS, "Treatments"),
  $KTS(ExportRequestField.PRESCRIPTIONS, "Prescriptions"),
  $KTS(ExportRequestField.BILLING, "Billing"),
]);

@JsonObject()
export class ExportRequest extends BasePracticeTypedObject {

  static async load(): Promise<ExportRequest> {
    const request = new ExportRequest(PracticeData.provider().practiceId);
    await request.load();
    return request;
  }

  readonly id: string = "exportRequest";

  @JsonProperty()
  fields: string[] = [
    ExportRequestField.PATIENTS,
    ExportRequestField.CALENDAR,
    ExportRequestField.TREATMENTS,
    ExportRequestField.PRESCRIPTIONS,
    ExportRequestField.BILLING,
  ];

  @JsonProperty()
  createdAt: number;

  @JsonProperty()
  status: ExportRequestStatus = ExportRequestStatus.NONE;

  constructor(practiceId: string) {
    super(practiceId);
  }

  protected getType(): Type<any> {
    return ExportRequest;
  }

  async queue(): Promise<void> {
    if (this.status !== ExportRequestStatus.NONE) {
      throw new Error("Invalid request status: " + this.status);
    }
    this.createdAt = Date.now();
    this.status = ExportRequestStatus.QUEUED;
    await this.save();
  }
}

/// Logs

enum LogType {
  ACTIVITY = "activity",
  SMS = "sms",
  EMAIL = "email",
  WHATSAPP = "whatsapp",
  VOICE = "voice",
}

@JsonObject()
abstract class BaseActivityLog extends BasePracticeListItem {

  @JsonProperty()
  readonly json: any = {};

  constructor(id: string, json: any) {
    super(id);
    this.json = json;
  }
}

abstract class BaseActivityLogs<T extends BaseActivityLog> extends BasePracticeListItemsLoader<T> {

  constructor(practiceId: string, readonly logType: LogType) {
    super(practiceId);
  }

  protected practiceDataBasePath(): string {
    return `logs/${this.logType}`;
  }
}

export enum ActivityLogCategory {
  PATIENTS = "patients",
}

export const ACTIVITY_LOG_CATEGORY_KTS = new KeyTextStrings([
  $KTS(ActivityLogCategory.PATIENTS, "Patients"),
]);

@JsonObject()
export class ActivityLog extends BaseActivityLog {

  @JsonProperty()
  readonly category: string = "";

  static createNew(json: any, category: ActivityLogCategory): ActivityLog {
    const activityLog = new ActivityLog(md5_uuid(), json, category);
    return activityLog;
  }

  constructor(id: string, json: any, category: ActivityLogCategory) {
    super(id, json);
    this.category = category;
  }
}

export class ActivityLogs extends BaseActivityLogs<ActivityLog> {

  constructor(practiceId: string) {
    super(practiceId, LogType.ACTIVITY);
  }

  protected deserializeItem(value: any): ActivityLog {
    return JSON_OBJECT.deserializeObject(value, ActivityLog);
  }

  protected serializeItem(item: ActivityLog): any {
    return JSON_OBJECT.serializeObject(item);
  }

  protected sortOrder(item1: ActivityLog, item2: ActivityLog): number {
    return item2.createdAt - item1.createdAt;
  }
}

@JsonObject()
export class SmsLog extends BaseActivityLog {

  @JsonProperty()
  readonly json: any = {};

  static createNew(json: any): SmsLog {
    const smsLog = new SmsLog(md5_uuid(), json);
    return smsLog;
  }
}

export class SmsLogs extends BaseActivityLogs<SmsLog> {

  constructor(practiceId: string) {
    super(practiceId, LogType.SMS);
  }

  protected deserializeItem(value: any): SmsLog {
    return JSON_OBJECT.deserializeObject(value, SmsLog);
  }

  protected serializeItem(item: SmsLog): any {
    return JSON_OBJECT.serializeObject(item);
  }

  protected sortOrder(item1: SmsLog, item2: SmsLog): number {
    return item2.createdAt - item1.createdAt;
  }
}

@JsonObject()
export class EmailLog extends BaseActivityLog {

  @JsonProperty()
  readonly json: any = {};

  static createNew(json: any): EmailLog {
    const emailLog = new EmailLog(md5_uuid(), json);
    return emailLog;
  }
}

export class EmailLogs extends BaseActivityLogs<EmailLog> {

  constructor(practiceId: string) {
    super(practiceId, LogType.EMAIL);
  }

  protected deserializeItem(value: any): EmailLog {
    return JSON_OBJECT.deserializeObject(value, EmailLog);
  }

  protected serializeItem(item: EmailLog): any {
    return JSON_OBJECT.serializeObject(item);
  }

  protected sortOrder(item1: EmailLog, item2: EmailLog): number {
    return item2.createdAt - item1.createdAt;
  }
}

@JsonObject()
export class WhatsAppLog extends BaseActivityLog {

  @JsonProperty()
  readonly json: any = {};

  static createNew(json: any): WhatsAppLog {
    const whatsAppLog = new WhatsAppLog(md5_uuid(), json);
    return whatsAppLog;
  }
}

export class WhatsAppLogs extends BaseActivityLogs<WhatsAppLog> {

  constructor(practiceId: string) {
    super(practiceId, LogType.WHATSAPP);
  }

  protected deserializeItem(value: any): WhatsAppLog {
    return JSON_OBJECT.deserializeObject(value, WhatsAppLog);
  }

  protected serializeItem(item: WhatsAppLog): any {
    return JSON_OBJECT.serializeObject(item);
  }

  protected sortOrder(item1: WhatsAppLog, item2: WhatsAppLog): number {
    return item2.createdAt - item1.createdAt;
  }
}

@JsonObject()
export class VoiceLog extends BaseActivityLog {

  @JsonProperty()
  readonly json: any = {};

  static createNew(json: any): VoiceLog {
    const voiceLog = new VoiceLog(md5_uuid(), json);
    return voiceLog;
  }
}

export class VoiceLogs extends BaseActivityLogs<VoiceLog> {

  constructor(practiceId: string) {
    super(practiceId, LogType.VOICE);
  }

  protected deserializeItem(value: any): VoiceLog {
    return JSON_OBJECT.deserializeObject(value, VoiceLog);
  }

  protected serializeItem(item: VoiceLog): any {
    return JSON_OBJECT.serializeObject(item);
  }

  protected sortOrder(item1: VoiceLog, item2: VoiceLog): number {
    return item2.createdAt - item1.createdAt;
  }
}

/// Practice + Profile scoped objects

@JsonObject()
export class ScheduledTask extends BasePracticeProfileListItem {

  static createNew(): ScheduledTask {
    return new ScheduledTask(md5_uuid());
  }

  @FormGen({
    name: "Title",
    type: "text",
    size: "xsmall",
    required: true,
  })
  @JsonProperty()
  title: string;

  @FormGen({
    name: "Task Due",
    type: "datetimenum",
  })
  @JsonProperty()
  dueAt: number;

  @JsonProperty()
  completed: boolean;

  constructor(id: string) {
    super(id);
  }
}

export class ScheduledTasks extends BasePracticeProfileListItemsLoader<ScheduledTask> {

  protected practiceProfileDataBasePath(): string {
    return "scheduledTasks";
  }

  protected serializeItem(item: ScheduledTask): any {
    return JSON_OBJECT.serializeObject(item);
  }

  protected deserializeItem(value: any): ScheduledTask {
    return JSON_OBJECT.deserializeObject(value, ScheduledTask);
  }

  protected sortOrder(item1: ScheduledTask, item2: ScheduledTask): number {
    return item1.created - item2.created;
  }
}